import { useTranslation } from 'react-i18next';
import { useBIMStore } from '../state/useBIMStore';
import { useAppStore } from '../state/store';
import { CSGHelper } from '../core/CSGHelper';
import { BIMElement, WallElement, Level, Grid, View, BIMMaterial, AppearanceAsset } from '../core/types';
import { PlanViewMaterial } from '../rendering/Materials';
import { layerMaterialColor, resolveMaterial, resolveAppearance, hatchParams, appearanceTexture } from '../rendering/MaterialResolver';
import { useImageLibraryStore } from '../state/useImageLibraryStore';
import { useManageSettingsStore } from '../state/useManageSettingsStore';
import { useThree, ThreeEvent } from '@react-three/fiber';
import { memo, useEffect, useMemo, useState, useRef } from 'react';
import * as THREE from 'three';
import { Text, Line } from '@react-three/drei';
import { useShallow } from 'zustand/react/shallow';
import { StairSolver } from '../core/StairSolver';
import { SlabGeometrySolver } from '../core/SlabGeometrySolver';
import { RebarSolver } from '../core/RebarSolver';
import { CurtainWallSolver } from '../core/CurtainWallSolver';
import { PREVIEW_COLORS } from '../themes/colors';
import { computeMemberSegments, buildSteelHoleCutters } from '../utils/steelCutGeometry';
import { buildOpeningCutter } from '../utils/structuralOpeningGeometry';
import { useWallEditPreviewStore } from '../state/wallEditPreviewStore';
import { generatePatHatchSegments, type PatHatchPoint } from '../utils/hatchPat';
import { resolveHatchPattern } from '../state/useHatchPatternStore';
import { clipPolygonMaxV, polygonBounds2D, shapeFromPolygon2D, wallBasisMatrix, wallLocalToWorld } from '../utils/wallProfileGeometry';
import { sanitizeWallProfileQuad } from '../utils/WallGeometryUtils';
import { StructuralLoadRenderer, SpaceRenderer, ZoneRenderer, SpaceSeparatorRenderer } from '../rendering/AnalyzeElementRenderers';
import { AnalyticalModelOverlay, ResultsColorFillOverlay } from '../rendering/AnalyzeOverlays';
import { mergeGeometries } from '../rendering/instancing/mergeGeometries';
import { ensureGeometryBVH } from '../rendering/instancing/instancingUtils';
import { StaticMeshFamilyRenderer, isStaticMeshGeometryData } from '../rendering/StaticMeshFamilyRenderer';
import { useAnnotationScaleFactor, useActiveViewScale } from '../hooks/useViewScale';
import { useAnnotationInk } from '../hooks/useThemeInk';
import { InkEdges } from '../rendering/InkEdges';
import { buildReinforcementMesh, buildMountingPartMesh } from '../core/PrecastSolver';
import ViewMarkerRenderer from '../rendering/ViewMarkerRenderer';
import {
    BIM_INSTANCING_VIEW_MODES,
    BIM_INSTANCE_THRESHOLD,
    BIMInstanceGroup,
    BIMInstanceGroupCache,
    BIMInstancedCategory,
    buildBIMInstanceGroups,
} from '../rendering/instancing/BIMInstancedCategory';
import {
    buildWallLayersUncached,
    getOpeningIndex,
    getWallLayerMeshes,
    releaseWallGeometry,
    useWallCacheWarmup,
} from '../rendering/BIMWallGeometryCache';
import { dispatchEntityClick, useEntityRegistryRef } from '../rendering/toolEventBus';

interface BIMSceneContentProps {
    viewType: '3d' | 'plan';
    viewMode: 'wireframe' | 'shaded' | 'rendered' | 'xray' | 'ghosted' | 'hiddenLine' | 'consistentColors' | 'realistic';
}

/**
 * Resolves the view-scope visibility of a BIM element from the view control
 * bar state (Temporary Hide/Isolate + Reveal Hidden Elements).
 * - 'isolate': only the selection (or isolated category) stays visible
 * - 'hide': elements hidden via "Hide in View" disappear
 * - revealHidden: hidden elements render instead of disappearing (caller colors them)
 */
function useViewScopeVisibility(elementId: string, category: string): { hidden: boolean; revealed: boolean } {
    const hideIsolateMode = useAppStore((s) => s.view.hideIsolateMode);
    const isolateCategoryName = useAppStore((s) => s.view.isolateCategoryName);
    const hiddenElementIds = useAppStore((s) => s.view.hiddenElementIds);
    const revealHidden = useAppStore((s) => s.view.revealHidden);
    const selectedIds = useAppStore((s) => s.selectedIds);
    const bimSelection = useBIMStore((s) => s.selection);

    const isHidden = hiddenElementIds.includes(elementId);

    if (hideIsolateMode === 'isolate') {
        if (isolateCategoryName) {
            return { hidden: category !== isolateCategoryName, revealed: false };
        }
        const inScope = selectedIds.includes(elementId) || bimSelection.includes(elementId);
        return { hidden: !inScope, revealed: false };
    }

    if (isHidden) {
        return { hidden: !revealHidden, revealed: revealHidden };
    }

    return { hidden: false, revealed: false };
}

export function BIMSceneContent({ viewType, viewMode }: BIMSceneContentProps) {
    const { t } = useTranslation();
    const project = useBIMStore(state => state.project);
    const activeView = project.views.get(project.activeViewId);
    const cropView = useAppStore((s) => s.view.cropView);
    const { gl } = useThree();

    // Handle View Clipping
    useEffect(() => {
        if (activeView && activeView.clippingPlanes) {
            const planes = activeView.clippingPlanes.map(cp =>
                new THREE.Plane(new THREE.Vector3(...cp.normal), cp.constant)
            );
            gl.clippingPlanes = planes;
            gl.localClippingEnabled = true; // Or false if we want global? Global is easier for sections.
        } else {
            gl.clippingPlanes = [];
        }
    }, [activeView, gl]);

    // Plan view range clipping (BIM-style): geometry above the view range
    // Top and below the View Depth is clipped out of the render. The Cut Plane
    // itself does NOT clip projection — elements below it stay visible until
    // the depth limit — but wall bodies spanning it are truncated there (see
    // WallRenderer) so the plan reads as a true cut through the model.
    const planClipPlanes = useMemo(() => {
        if (viewType !== 'plan' || !activeView || !activeView.viewRange) return [];
        const level = activeView.associatedLevelId
            ? project.levels.get(activeView.associatedLevelId)
            : undefined;
        if (!level) return [];

        const levelZ = level.elevation / 1000; // mm -> m
        const { top, depth } = activeView.viewRange;

        // Keep everything below Top: normal (0,0,-1), constant = topZ.
        // Keep everything above Depth: normal (0,0,1), constant = -depthZ,
        // where depthZ = levelZ - depth (View Depth extends BELOW the level —
        // kernel comment: "show elements 1 m below the level"). Using
        // levelZ + here would clamp the window to [depth..top] and clip out
        // everything at the level itself, including tool previews.
        return [
            new THREE.Plane(new THREE.Vector3(0, 0, -1), levelZ + top),
            new THREE.Plane(new THREE.Vector3(0, 0, 1), -(levelZ - depth)),
        ];
    }, [viewType, activeView, project.levels]);

    // Crop View (BIM-style): clip the model to the active view's crop
    // region rectangle (X/Y bounds). Enabled from the view control bar.
    const cropClipPlanes = useMemo(() => {
        if (!cropView || !activeView?.cropRegion) return [];
        const { x, y, width, height } = activeView.cropRegion;
        return [
            new THREE.Plane(new THREE.Vector3(-1, 0, 0), -x),
            new THREE.Plane(new THREE.Vector3(1, 0, 0), x + width),
            new THREE.Plane(new THREE.Vector3(0, -1, 0), -y),
            new THREE.Plane(new THREE.Vector3(0, 1, 0), y + height),
        ];
    }, [cropView, activeView?.cropRegion]);

    const activeClipPlanes = useMemo(
        () => (cropClipPlanes.length > 0 ? [...planClipPlanes, ...cropClipPlanes] : planClipPlanes),
        [planClipPlanes, cropClipPlanes]
    );

    // Selection Box (contextual View panel): the active view's sectionBox
    // clips the model to the AABB captured from the selection. Inward normals
    // keep the inside of the box (same convention as ViewProcessor).
    const sectionBoxPlanes = useMemo(() => {
        if (!activeView?.sectionBox) return [];
        const min = new THREE.Vector3(...activeView.sectionBox.min);
        const max = new THREE.Vector3(...activeView.sectionBox.max);
        return [
            new THREE.Plane(new THREE.Vector3(-1, 0, 0), max.x),
            new THREE.Plane(new THREE.Vector3(1, 0, 0), -min.x),
            new THREE.Plane(new THREE.Vector3(0, 0, -1), max.z),
            new THREE.Plane(new THREE.Vector3(0, 0, 1), -min.z),
            new THREE.Plane(new THREE.Vector3(0, -1, 0), max.y),
            new THREE.Plane(new THREE.Vector3(0, 1, 0), -min.y),
        ];
    }, [activeView?.sectionBox]);

    const finalClipPlanes = useMemo(
        () => (sectionBoxPlanes.length > 0 ? [...activeClipPlanes, ...sectionBoxPlanes] : activeClipPlanes),
        [activeClipPlanes, sectionBoxPlanes]
    );

    useEffect(() => {
        gl.clippingPlanes = finalClipPlanes;
        gl.localClippingEnabled = true;
        return () => { gl.clippingPlanes = []; };
    }, [gl, finalClipPlanes]);

    return (
        <group>
            {/* Levels Visualization */}
            {Array.from(project.levels.values()).map(level => (
                <LevelRenderer key={level.id} level={level} viewType={viewType} />
            ))}

            {/* Grid Lines */}
            {Array.from(project.grids.values()).map(grid => (
                <GridRenderer key={grid.id} grid={grid} />
            ))}

            {/* BIM Elements: per-element memoized renderers + instanced bulk */}
            <BIMElementLayer viewType={viewType} viewMode={viewMode} activeView={activeView} />

            {/* Analyze tab overlays: analytical model display + result diagrams */}
            <AnalyticalModelOverlay />
            <ResultsColorFillOverlay />
        </group>
    );
}

function LevelRenderer({ level, viewType }: { level: Level, viewType: string }) {
    const selection = useBIMStore(state => state.selection);
    const setSelection = useBIMStore(state => state.setSelection);
    const updateLevel = useBIMStore(state => state.updateLevel);
    const appSelection = useAppStore(state => state.selectedIds);
    const select = useAppStore(state => state.select);
    const addToAppSelection = useAppStore(state =>state.addToSelection); const [isHovered, setIsHovered] = useState(false); const [isDragging, setIsDragging] = useState(false); const [dragStartZ, setDragStartZ] = useState(0); const [dragStartElevation, setDragStartElevation] = useState(0); const groupRef = useRef<THREE.Group>(null);
    // Level name label is an annotation: constant paper size, world size
    // follows the active view scale (BIM rule).
    const annotationScale = useAnnotationScaleFactor();

    const isSelected = selection.includes(level.id) || appSelection.includes(level.id);

    if (viewType === 'plan') return null; // Don't show level planes in plan view for now

    // Level boundary extent: the explicit 2D extents written by the context
    // tab's Maximize / Propagate tools, or the automatic ±50 m default.
    const ext = level.extents2D;
    const minX = ext?.min[0] ?? -50;
    const maxX = ext?.max[0] ?? 50;
    const minY = ext?.min[1] ?? -50;
    const maxY = ext?.max[1] ?? 50;
    const extentSize = Math.max(Math.abs(minX), Math.abs(maxX), Math.abs(minY), Math.abs(maxY));

    // Dashed boundary rectangle corners
    const corners = [
        new THREE.Vector3(minX, minY, 0),
        new THREE.Vector3(maxX, minY, 0),
        new THREE.Vector3(maxX, maxY, 0),
        new THREE.Vector3(minX, maxY, 0),
        new THREE.Vector3(minX, minY, 0), // Close the loop
    ];

    // Border-only pick strips (BIM datum behavior): the level must never
    // swallow clicks aimed at elements inside its extent, so the hit surface
    // hugs the dashed boundary instead of covering the whole level plane.
    // Depth scales with the extent (0.5 m floor) so the strips stay hittable
    // when zoomed out.
    const borderHitDepth = Math.max(0.5, extentSize * 0.02);
    const borderHits: { key: string; position: [number, number, number]; size: [number, number] }[] = [
        { key: 'bottom', position: [(minX + maxX) / 2, minY, 0], size: [maxX - minX, borderHitDepth] },
        { key: 'top', position: [(minX + maxX) / 2, maxY, 0], size: [maxX - minX, borderHitDepth] },
        { key: 'left', position: [minX, (minY + maxY) / 2, 0], size: [borderHitDepth, maxY - minY] },
        { key: 'right', position: [maxX, (minY + maxY) / 2, 0], size: [borderHitDepth, maxY - minY] },
    ];

    // Determine color based on state
    const getColor = () => {
        if (isSelected) return '#0078d4'; // Blue when selected
        if (isHovered) return '#00aaff'; // Light blue on hover
        return '#666666'; // Default gray
    };

    const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        if (e.button === 0) {
            // Select this level
            if (e.ctrlKey || e.shiftKey) {
                // Add to selection
                addToAppSelection([level.id]);
                if (!selection.includes(level.id)) {
                    setSelection([...selection, level.id]);
                }
            } else {
                select([level.id]);
                setSelection([level.id]);
            }

            // Start drag
            setIsDragging(true);
            setDragStartZ(e.point.z);
            setDragStartElevation(level.elevation);
            (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
        }
    };

    const handlePointerMove = (e: ThreeEvent<PointerEvent>) => {
        if (isDragging) {
            e.stopPropagation();
            const deltaZ = e.point.z - dragStartZ;
            // Update elevation in meters (multiply by 1000 if elevation is in mm)
            const newElevation = dragStartElevation + deltaZ * 1000; // Assuming elevation is in mm

            // Update the level position visually during drag
            if (groupRef.current) {
                groupRef.current.position.z = newElevation / 1000;
            }
        }
    };

    const handlePointerUp = (e: ThreeEvent<PointerEvent>) => {
        if (isDragging) {
            e.stopPropagation();
            const deltaZ = e.point.z - dragStartZ;
            const newElevation = dragStartElevation + deltaZ * 1000;

            // Commit the elevation change
            if (Math.abs(deltaZ) > 0.01) { // Only update if moved significantly
                updateLevel(level.id, { elevation: newElevation });
            }

            setIsDragging(false);
            (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
        }
    };

    return (
        <group
            ref={groupRef}
            position={[0, 0, level.elevation / 1000]}
            userData={{ id: level.id, levelId: level.id }}
        >
            {/* Border hit strips: level is selectable (and draggable) only
                from its boundary, leaving the interior click-transparent */}
            {borderHits.map(strip => (
                <mesh
                    key={strip.key}
                    position={strip.position}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerOver={() => setIsHovered(true)}
                    onPointerOut={() => { setIsHovered(false); if (!isDragging) setIsDragging(false); }}
                    visible={false}
                >
                    <planeGeometry args={strip.size} />
                    <meshBasicMaterial transparent opacity={0} />
                </mesh>
            ))}

            <Text
                position={[extentSize + 1, extentSize + 1, 0]}
                fontSize={0.5 * annotationScale}
                color={isSelected ? '#0078d4' : '#0066ff'}
                anchorX="left"
                anchorY="bottom"
            >
                {level.name} ({(level.elevation / 1000).toFixed(2)}m)
            </Text>

            {/* Level Boundary - Dashed Rectangle (BIM-style) */}
            <Line
                points={corners}
                color={getColor()}
                lineWidth={isSelected ? 2 : 1}
                dashed
                dashSize={1}
                gapSize={0.5}
            />

            {/* Selection indicator */}
            {isSelected && (
                <mesh position={[0, 0, 0]} raycast={() => null}>
                    <planeGeometry args={[extentSize * 2, extentSize * 2]} />
                    <meshBasicMaterial color="#0078d4" transparent opacity={0.05} />
                </mesh>
            )}
        </group>
    );
}



function GridRenderer({ grid }: { grid: Grid }) {
    const selection = useBIMStore(state => state.selection);
    const setSelection = useBIMStore(state => state.setSelection);
    const appSelection = useAppStore(state => state.selectedIds);
    const select = useAppStore(state => state.select);
    const addToAppSelection = useAppStore(state =>state.addToSelection); const isSelected = selection.includes(grid.id) || appSelection.includes(grid.id); 
// Elevate grid lines slightly above z=0 to prevent z-fighting with the background grid.
        const gridZ = 0.01; const start = new THREE.Vector3(grid.start[0], grid.start[1], gridZ); const end = new THREE.Vector3(grid.end[0], grid.end[1], gridZ); const color = isSelected ? '#0078d4' : '#444444'; // BIM datums are selectable: an invisible capsule along the line gives
            // the dashed line a comfortable click target.
            const dir = end.clone().sub(start); const lineLen = dir.length(); const center = start.clone().add(end).multiplyScalar(0.5); const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize()); const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        if (e.button !== 0) return;
        if (e.ctrlKey || e.shiftKey) {
            addToAppSelection([grid.id]);
            if (!selection.includes(grid.id)) setSelection([...selection, grid.id]);
        } else {
            select([grid.id]);
            setSelection([grid.id]);
        }
    };

    return (
        <group userData={{ id: grid.id }}>
            {/* Grid Line - Using Line from drei for proper dashed line rendering */}
            <Line
                points={[start, end]}
                color={color}
                lineWidth={isSelected ? 2 : 1}
                dashed
                dashSize={1}
                gapSize={0.5}
            />

            {/* Invisible pick capsule (click to select the grid datum) */}
            <mesh position={center} quaternion={quat} onPointerDown={handlePointerDown} visible={false}>
                <cylinderGeometry args={[0.25, 0.25, lineLen, 6]} />
                <meshBasicMaterial transparent opacity={0} />
            </mesh>

            {/* Bubble at Start */}
            {(grid.bubbleEnd === 'Start' || grid.bubbleEnd === 'Both') && (
                <GridBubble position={start} label={grid.name} />
            )}

            {/* Bubble at End */}
            {(grid.bubbleEnd === 'End' || grid.bubbleEnd === 'Both') && (
                <GridBubble position={end} label={grid.name} />
            )}
        </group>
    );
}

function GridBubble({ position, label }: { position: THREE.Vector3, label: string }) {
    // Grid bubbles are annotations: fixed paper size that grows/shrinks with
    // the view scale. The group is positioned at the model point and only the
    // bubble graphics are scaled, so the anchor never moves.
    const s = useAnnotationScaleFactor();
    return (
        <group position={position}>
            <group scale={[s, s, 1]}>
                <mesh>
                    <circleGeometry args={[0.3, 32]} />
                    <meshBasicMaterial color="white" />
                </mesh>
            <line>
                <bufferGeometry>
                    <float32BufferAttribute
                        attach="attributes-position"
                        count={33} // 32 segments + 1 to close
                        array={new Float32Array(
                            Array.from({ length: 33 }).flatMap((_, i) => {
                                const angle = (i / 32) * Math.PI * 2;
                                return [Math.cos(angle) * 0.3, Math.sin(angle) * 0.3, 0];
                            })
                        )}
                        itemSize={3}
                    />
                </bufferGeometry>
                <lineBasicMaterial color="#444444" />
            </line>
            <Text
                position={[0, 0, 0.01]}
                fontSize={0.3}
                color="black"
                anchorX="center"
                anchorY="middle"
            >
                {label}
            </Text>
            </group>
        </group>
    );
}

function BIMElementRenderer({ element, isSelected, isRevealed, viewType, viewMode, activeView, hostWall, hostedElements, structuralOpenings }: { element: BIMElement, isSelected: boolean, isRevealed?: boolean, viewType: string, viewMode: string, activeView?: View, hostWall?: WallElement, hostedElements?: BIMElement[], structuralOpenings?: BIMElement[] }) {
    const props = { element, isSelected, isRevealed, viewType, viewMode, activeView, hostWall, hostedElements, structuralOpenings };
    // Baked-asset elements (IFC static families) draw their IndexedDB mesh
    // regardless of category, before the per-category dispatch below.
    if (isStaticMeshGeometryData((element as any).geometryData)) {
        return <StaticMeshFamilyRenderer element={element as any} isSelected={isSelected} viewType={viewType} viewMode={viewMode} />;
    }
    switch (element.category) {
        case 'Walls':
            return <WallRenderer {...props} element={element as WallElement} />; case 'Doors': return<DoorRenderer {...props} />; case 'Windows': return<WindowRenderer {...props} />; case 'Floors': return<FloorRenderer {...props} />; case 'Roofs': return<RoofRenderer {...props} />; case 'Ceilings': return<CeilingRenderer {...props} />; case 'Rooms': return<RoomRenderer {...props} />; case 'Areas': return<AreaRenderer {...props} />; case 'Railings': return<RailingRenderer {...props} />; case 'Ramps': return<RampRenderer {...props} />; case 'Generic Models': return<ComponentRenderer {...props} />; case 'Mass': return<MassRenderer {...props} />; case 'Structural Columns': return<ColumnRenderer {...props} />; case 'Structural Foundations': return<FoundationRenderer {...props} />; case 'Structural Trusses': return<TrussRenderer {...props} />; case 'Structural Rebar': return<RebarRenderer {...props} />; case 'Structural Connections': return<ConnectionRenderer {...props} />; case 'Steel Plates': return<SteelPlateRenderer {...props} />; case 'Steel Bolts': return<SteelBoltRenderer {...props} />; case 'Steel Welds': return<SteelWeldRenderer {...props} />; case 'Structural Area Reinforcement': return<AreaReinforcementRenderer {...props} />; case 'Structural Path Reinforcement': return<PathReinforcementRenderer {...props} />; case 'Air Terminals': return<AirTerminalRenderer {...props} />; case 'Duct Placeholders': return<DuctPlaceholderRenderer {...props} />; case 'Pipe Placeholders': return<PipePlaceholderRenderer {...props} />; case 'Flex Ducts': return<FlexDuctRenderer {...props} />; case 'Flex Pipes': return<FlexPipeRenderer {...props} />; case 'Duct Accessories': return<DuctAccessoryRenderer {...props} />; case 'Pipe Accessories': return<PipeAccessoryRenderer {...props} />; case 'Cable Tray Fittings': return<TrayFittingRenderer {...props} />; case 'Sprinklers': return<SprinklerRenderer {...props} />; case 'Plumbing Fixtures': return<PlumbingFixtureRenderer {...props} />; case 'Lighting Fixtures': return<LightingFixtureRenderer {...props} />; case 'Electrical Fixtures': case 'Electrical Devices': case 'Lighting Devices': return<ElectricalDeviceRenderer {...props} />; case 'Wires': return<WireRenderer {...props} />; case 'Fabrication Parts': return<FabricationPartRenderer {...props} />; case 'Revision Clouds': return<RevisionCloudRenderer {...props} />; case 'Generic Annotations': return<SymbolAnnotationRenderer {...props} />; case 'Stairs': return<StairRenderer {...props} />; case 'Curtain Walls': return<CurtainWallRenderer {...props} />; 
// MEP
        case 'Ducts': return<DuctRenderer {...props} />; case 'Pipes': return<PipeRenderer {...props} />; case 'Duct Fittings': return<DuctFittingRenderer {...props} />; case 'Pipe Fittings': return<PipeFittingRenderer {...props} />; 
// View markers (Elevation / Section / Camera / Callout): 2D plan symbols
        case 'Views': return<ViewMarkerRenderer element={element} isSelected={isSelected} inPlanView={viewType === 'plan'} />; 
// Links & Imports
        case 'BIM Links': case 'CAD Links': case 'IFC Links': case 'Point Cloud': case 'Other Links': return<LinkRenderer {...props} />; case 'Raster Images': case 'PDF Imports': 
// Treat PDF as Image
        return<ImageRenderer {...props} />; case 'Decals': return<DecalRenderer {...props} />; 
// Annotations
        case 'Dimensions': return<DimensionRenderer {...props} />; case 'Tags': return<TagRenderer {...props} />; case 'Text Notes': case 'Model Text': case 'ModelText': return<TextRenderer {...props} />; case 'Model Lines': case 'ModelLines': case 'Lines': return<ModelLineRenderer {...props} />; case 'Detail Items': return<DetailComponentRenderer {...props} />; case 'Filled Regions': return<FilledRegionRenderer {...props} />; case 'Cable Trays': return<CableTrayRenderer {...props} />; case 'Conduits': return<ConduitRenderer {...props} />; case 'Mechanical Equipment': case 'Electrical Equipment': return<EquipmentRenderer {...props} />; case 'Structural Framing': 
// Beams and Braces // Check type or assume beam/brace are same renderer logic (Framing)
        if ((element as any).type.includes('Brace')) return<BraceRenderer {...props} />; return <BeamRenderer {...props} />; case 'Beam Systems': return<BeamSystemRenderer {...props} />; case 'Structural Openings': 
// Parametric void: renders as a subtle dashed wireframe so the // opening stays selectable; the actual material removal happens // in the host renderers via CSG cutters.
        return<StructuralOpeningWireframe element={element} isSelected={isSelected} />; case 'Rebar Couplers': return<RebarCouplerRenderer {...props} />; case 'Reference Planes': return<ReferencePlaneRenderer {...props} />; case 'Wall Sweeps': case 'Wall Reveals': return<WallBandRenderer {...props} />; case 'Soffits': case 'Fascias': case 'Gutters': case 'Slab Edges': return<EdgeBandRenderer {...props} />; case 'Room Separators': return<RoomSeparatorRenderer {...props} />; case 'Area Boundaries': return<AreaBoundaryLineRenderer {...props} />; case 'Model Groups': case 'ModelGroups': return<ModelGroupMarkerRenderer {...props} />; case 'Structural In-Place': return<MassRenderer {...props} element={element} isSelected={isSelected} viewType={viewType} viewMode={viewMode} />; case 'Toposolid': return<ToposolidRenderer {...props} />; 
// Massing & Site — Site Element panel artifacts
        case 'Site': return<SiteComponentRenderer element={element} isSelected={isSelected} viewType={viewType} viewMode={viewMode} />; case 'Parking Areas': return<ParkingAreaRenderer element={element} isSelected={isSelected} />; case 'Property Lines': return<PropertyLineRenderer element={element} isSelected={isSelected} />; case 'Building Pads': return<BuildingPadOutlineRenderer element={element} isSelected={isSelected} />; case 'Graded Regions': return<GradedRegionRenderer element={element} isSelected={isSelected} />; case 'Site Subregions': return<SubregionRenderer element={element} isSelected={isSelected} />; 
// Analyze tab artifacts (loads, thermal spaces, zones, separators)
        case 'Structural Loads': return<StructuralLoadRenderer element={element as any} isSelected={isSelected} />; case 'Spaces': return<SpaceRenderer element={element as any} isSelected={isSelected} />; case 'Zones': return<ZoneRenderer element={element as any} isSelected={isSelected} />; case 'Space Separators': return<SpaceSeparatorRenderer element={element as any} isSelected={isSelected} />;
        // Precast tab categories (Precast >Split / Reinforcement / Connections)
        case 'Precast Walls': case 'Precast Columns': case 'Precast Beams': return<PrecastLinearPanelRenderer {...props} />; case 'Precast Floors': return<PrecastFloorPanelRenderer {...props} />; case 'Precast Mounting Parts': return<PrecastMountingPartRenderer {...props} />; case 'Precast Reinforcement': return<PrecastReinforcementRenderer {...props} />; default: return<GenericElementRenderer {...props} />;
    }
}

/**
 * Memoized per-element renderer (plan tasks 2.1/2.2, BIM side).
 *
 * Subscribes individually to its element slice, selection membership, host
 * wall (doors/windows) and hosted openings (walls), so a single-element edit
 * or selection change re-renders only the affected renderer(s). Works because
 * the kernel now guarantees replace-on-write element identity (structural
 * sharing semantics).
 */
/**
 * Single element view-scope gate: category visibility map + view filters.
 * An element passes when its category is not explicitly hidden in the view
 * and every filter that targets its category finds at least one rule match.
 */
function passesViewScope(element: BIMElement, activeView?: View): boolean {
    if (!activeView) return true;
    if (activeView.visibility && activeView.visibility[element.category] === false) return false;
    if (activeView.filters && activeView.filters.length > 0) {
        for (const filter of activeView.filters) {
            const categories = (filter as { categories?: string[] }).categories;
            const rules = filter.rules;
            if (!categories?.includes(element.category) || !rules?.length) continue;
            const results = rules.map((rule) => {
                const v = (element.parameters as Record<string, unknown> | undefined)?.[rule.parameter];
                if (v === undefined) return false;
                switch (rule.evaluator) {
                    case 'equals': return String(v) === String(rule.value);
                    case 'greater': return Number(v) >Number(rule.value); case 'less': return Number(v)< Number(rule.value);
                    case 'contains': return String(v).toLowerCase().includes(String(rule.value).toLowerCase());
                    case 'beginsWith': return String(v).toLowerCase().startsWith(String(rule.value).toLowerCase());
                    default: return false;
                }
            });
            const andMode = (filter as { and?: boolean }).and ?? true;
            const matched = andMode ? results.every(Boolean) : results.some(Boolean);
            if (!matched) return false;
        }
    }
    return true;
}

const EMPTY_HOSTED_ELEMENTS: BIMElement[] = [];

/**
 * Triangle floor for computing a BVH on per-element CSG geometry. Below this,
 * building a bounds tree costs more (traversal setup) than the classic
 * triangle loop it replaces; a typical single wall without openings stays on
 * the cheap path, a wall with several door/window voids goes through the BVH.
 */
const BVH_MIN_TRIANGLES = 64;

/** Categories that accept parametric Structural Opening voids. */
const OPENING_HOST_CATEGORIES = [
    'Walls', 'Floors', 'Roofs', 'Ceilings', 'Structural Framing',
    'Structural Columns', 'Structural Foundations', 'Structural In-Place',
];

// Host→opening indexes live in BIMWallGeometryCache: shared between the
// per-element selectors here and the idle wall-cache warm-up.

const MemoizedBIMElement = memo(function MemoizedBIMElement({ id, viewType, viewMode, activeView }: { id: string, viewType: string, viewMode: string, activeView?: View }) {
    const element = useBIMStore((state) => state.project.elements.get(id));
    const isSelectedBIM = useBIMStore((state) => state.selection.includes(id));
    const isSelectedApp = useAppStore((state) => state.selectedIds.includes(id));
    const isSelected = isSelectedBIM || isSelectedApp;

    // Unified cross-workbench picking: outside the BIM workbench (which owns
    // its own InteractionHandler raycasting) the element root joins the shared
    // entity registry + entity-click router, so sketch/3D/2D selection tools,
    // box-select and the click router treat a BIM element exactly like a CAD
    // entity. Hooks stay unconditional; only the DOM props are gated.
    const interactive = useAppStore((state) => state.mode) !== 'bim';
    const registryRef = useEntityRegistryRef(id, element?.category);

    // Hosted opening lookup for the parent wall (doors/windows refresh when
    // their host wall is edited thanks to replace-on-write identities).
    const hostWallId = (element as any)?.geometryData?.hostWallId as string | undefined;
    const hostWall = useBIMStore(useShallow((state) => (
        hostWallId ? (state.project.elements.get(hostWallId) as WallElement | undefined) : undefined
    )));

    // Openings hosted by this wall: O(1) lookup into the cached per-Map
    // index (identity-stable via useShallow so the CSG memo only refreshes
    // when an opening is added/removed/edited).
    const hostedElements = useBIMStore(useShallow((state) => {
        if (element?.category !== 'Walls') return EMPTY_HOSTED_ELEMENTS;
        return getOpeningIndex(state.project.elements).hostedByWall.get(id) ?? EMPTY_HOSTED_ELEMENTS;
    }));

    // Parametric Structural Opening voids cutting this host. Host-assigned
    // openings match by hostId; shafts additionally pierce every horizontal
    // comp (floors/roofs/ceilings) they cross — matching BIM behavior.
    // Both lists come from the cached index: no per-element full-map scan.
    const structuralOpenings = useBIMStore(useShallow((state) => {
        const el = state.project.elements.get(id);
        if (!el || !OPENING_HOST_CATEGORIES.includes(el.category)) return EMPTY_HOSTED_ELEMENTS;
        const index = getOpeningIndex(state.project.elements);
        const hostAssigned = index.byStructuralHost.get(id) ?? EMPTY_HOSTED_ELEMENTS;
        const isHorizontalComp = el.category === 'Floors' || el.category === 'Roofs' || el.category === 'Ceilings';
        if (!isHorizontalComp || index.shafts.length === 0) return hostAssigned;
        // Shafts pierce every horizontal comp they cross, but a shaft already
        // assigned to THIS host is in hostAssigned — never add it twice.
        const crossing = index.shafts.filter(s => (s.geometryData as any)?.hostId !== id);
        return crossing.length === 0 ? hostAssigned
            : hostAssigned === EMPTY_HOSTED_ELEMENTS ? crossing
            : [...hostAssigned, ...crossing];
    }));

    if (!element) return null;

    // View control bar scope: Temporary Hide/Isolate + Reveal Hidden Elements
    const { hidden, revealed } = useViewScopeVisibility(element.id, element.category);
    if (hidden) return null;

    // View-scope gate: Visibility/Graphics category map + per-view filters
    if (!passesViewScope(element, activeView)) return null;

    // Click dispatch gate: only the select tool picks elements; the drafting
    // tools keep the pointer (the 2D/3D router enforces the same contract).
    const handleElementClick = (e: ThreeEvent<MouseEvent>) => {
        e.stopPropagation();
        if (useAppStore.getState().activeTool !== 'select') return;
        dispatchEntityClick(element.id, e);
    };

    return (
        <group
            ref={interactive ? registryRef : undefined}
            onClick={interactive ? handleElementClick : undefined}
        >
            <BIMElementRenderer
                element={element}
                isSelected={isSelected || revealed}
                isRevealed={revealed}
                viewType={viewType}
                viewMode={viewMode}
                activeView={activeView}
                hostWall={hostWall}
                hostedElements={hostedElements}
                structuralOpenings={structuralOpenings}
            />
        </group>
    );
});

/**
 * Element layer (plan task 2.1, BIM side).
 *
 * Partitions elements between InstancedMesh batches (repetitive walls, doors,
 * windows in 3D shaded-family views) and memoized individual renderers
 * (everything else, selected instances, plan symbols, edge-only modes).
 */
/**
 * Element layer (plan task 2.1, BIM side).
 *
 * Partitions elements between InstancedMesh batches (repetitive walls, doors,
 * windows in 3D shaded-family views) and memoized individual renderers
 * (everything else, selected instances, plan symbols, edge-only modes).
 *
 * Exported: the cross-workbench BIMShadedLayer mounts this SAME layer in the
 * sketch/3D/quad viewports, so every workbench renders kernel elements with
 * one identical shaded pipeline instead of wireframe cross-references.
 */
export function BIMElementLayer({ viewType, viewMode, activeView }: { viewType: string, viewMode: string, activeView?: View }) {
    const elementsMap = useBIMStore((state) =>state.project.elements); const [groupCache, setGroupCache] = useState<BIMInstanceGroupCache>(() => new Map());

    const is3D = viewType === '3d';
    const instancingEnabled = is3D && BIM_INSTANCING_VIEW_MODES.has(viewMode);
    // Unified picking gate (see MemoizedBIMElement): the BIM workbench keeps
    // its own InteractionHandler; the other workbenches pick through the
    // shared entity-click router, including the instanced bulk.
    const interactive = useAppStore((state) => state.mode) !== 'bim';

    // Selection/hover ids excluded from instancing so they keep their full
    // per-element highlight visuals (hybrid strategy from the plan).
    const bimSelection = useBIMStore((state) => state.selection);
    const appSelection = useAppStore((state) => state.selectedIds);
    // Hosts carrying parametric Structural Opening voids need CSG cuts, so
    // they must leave the instancing batch and render individually.
    const openingHostIds = useMemo(() => {
        const ids = new Set<string>();
        elementsMap.forEach((el) => {
            if (el.category === 'Structural Openings') {
                const hostId = (el.geometryData as any)?.hostId as string | undefined;
                if (hostId) ids.add(hostId);
            }
        });
        return ids;
    }, [elementsMap]);
    const instancingExclusions = useMemo(
        () => new Set([...bimSelection, ...appSelection, ...openingHostIds]),
        [bimSelection, appSelection, openingHostIds]
    );

    // Partition into instance groups (cached for identity stability). Skipped
    // entirely when instancing is inactive (plan views / edge-only modes).
    const { groups, byElementId } = useMemo(
        () => instancingEnabled
            ? buildBIMInstanceGroups(elementsMap, BIM_INSTANCE_THRESHOLD, instancingExclusions, groupCache)
            : { groups: [] as BIMInstanceGroup[], byElementId: new Set<string>(), cache: groupCache },
        [elementsMap, instancingExclusions, groupCache, instancingEnabled]
    );

    // Drop stale cache entries to keep the registry bounded (plan registry sketch).
    useEffect(() => {
        const liveKeys = new Set(groups.map((group) => group.key));
        setGroupCache((prev: BIMInstanceGroupCache) => {
            let stale = false;
            prev.forEach((group: BIMInstanceGroup, key: string) => {
                if (!liveKeys.has(key)) stale = true;
            });
            if (!stale) return prev;
            const next: BIMInstanceGroupCache = new Map();
            prev.forEach((group: BIMInstanceGroup, key: string) => {
                if (liveKeys.has(key)) next.set(key, group);
            });
            return next;
        });
    }, [groups]);

    const individualIds = useMemo(() => {
        const ids: string[] = [];
        elementsMap.forEach((el, id) => {
            // Datum elements render through LevelRenderer/GridRenderer from
            // project.levels/project.grids; the generic box fallback would
            // draw a phantom 1m cube at the origin for them.
            if (el.category === 'Levels' || el.category === 'Grids') return;
            if (!byElementId.has(id)) ids.push(id);
        });
        return ids;
    }, [elementsMap, byElementId]);

    // Idle warm-up: while the instanced bulk covers the walls (shaded-family
    // 3D modes), pre-build their body geometries in budgeted idle ticks so
    // the FIRST switch to an edge-family mode finds every geometry ready and
    // paints instantly instead of running all wall CSGs in one pass.
    useWallCacheWarmup(instancingEnabled, groups, () => elementsMap);

    return (
        <group>
            {/* Instanced bulk: one InstancedMesh per logical part per group */}
            {instancingEnabled && groups.map((group: BIMInstanceGroup) => (
                <BIMInstancedCategory key={group.key} group={group} interactive={interactive} />
            ))}

            {/* Individual memoized renderers for the remaining elements */}
            {individualIds.map((id) => (
                <MemoizedBIMElement
                    key={id}
                    id={id}
                    viewType={viewType}
                    viewMode={viewMode}
                    activeView={activeView}
                />
            ))}
        </group>
    );
}

// Stub Renderers for missing components
// Stub renderers for unimplemented categories

/**
 * Foundation renderer: draws the three structural foundation flavours written
 * by the foundation tools — isolated footings (pad / pile cap / stepped /
 * pedestal), continuous wall footings, and mat/foundation slabs. The tools
 * store dimensions in millimetres while the scene is in meters.
 */
function FoundationRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial(PREVIEW_COLORS.foundation, isSelected, viewMode, viewType);

    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';
    const showEdges = isPlan || isHiddenLine || isWireframe || isConsistentColors || viewMode === 'shaded';

    if (!geom) return null;

    // --- Wall footing: prismatic bar along the start->end path ---
    if (geom.start && geom.end) {
        const widthM = (geom.width ?? 600) / 1000;
        const thicknessM = (geom.thickness ?? 300) / 1000;
        const start = new THREE.Vector3(...geom.start);
        const end = new THREE.Vector3(...geom.end);
        const length = start.distanceTo(end);
        if (length < 0.001) return null;

        const mid = start.clone().add(end).multiplyScalar(0.5);
        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        // Footing top sits at the reference line (wall base); body extends down.
        const centerZ = mid.z + (geom.heightOffset ?? 0) / 1000 - thicknessM / 2;

        return (
            <group position={[mid.x, mid.y, centerZ]} rotation={[0, 0, angle]} userData={{ id: element.id }}>
                <mesh>
                    <boxGeometry args={[length, widthM, thicknessM]} />
                    {material}
                    {showEdges && <InkEdges isSelected={isSelected} />}
                </mesh>
            </group>
        );
    }

    // --- Foundation slab (mat): extruded boundary polygon ---
    if (Array.isArray(geom.boundary) && geom.boundary.length >= 3) {
        const thicknessM = (geom.totalThickness ?? 300) / 1000;
        const shape = new THREE.Shape();
        shape.moveTo(geom.boundary[0][0], geom.boundary[0][1]);
        for (let i = 1; i < geom.boundary.length; i++) {
            shape.lineTo(geom.boundary[i][0], geom.boundary[i][1]);
        }
        shape.closePath();

        const geometry = new THREE.ExtrudeGeometry(shape, { depth: thicknessM, bevelEnabled: false });
        // Top face at the sketch plane; body extends downward into the soil.
        const topZ = geom.boundary[0][2] + (geom.heightOffset ?? 0) / 1000;

        return (
            <group position={[0, 0, topZ - thicknessM]} userData={{ id: element.id }}>
                <mesh geometry={geometry}>
                    {material}
                    {showEdges && <InkEdges isSelected={isSelected} />}
                </mesh>
            </group>
        );
    }

    // --- Isolated footing: base pad + stepped tiers + pedestal ---
    if (geom.position && geom.width) {
        const widthM = geom.width / 1000;
        const lengthM = (geom.length ?? geom.width) / 1000;
        const thicknessM = (geom.thickness ?? 450) / 1000;
        const pos = new THREE.Vector3(...geom.position);
        const topZ = geom.topElevation ?? pos.z;
        const bottomZ = geom.bottomElevation ?? (topZ - thicknessM);
        const rotationRad = THREE.MathUtils.degToRad(geom.rotation || 0);
        const isCircular = geom.shape === 'Circular';

        const parts: React.ReactNode[] = [];

        parts.push(
            <mesh key="base" position={[0, 0, (topZ + bottomZ) / 2 - pos.z]}>
                {isCircular
                    ? <cylinderGeometry args={[widthM / 2, widthM / 2, thicknessM, 32]} />
                    : <boxGeometry args={[widthM, lengthM, thicknessM]} />}
                {material}
                {showEdges && <InkEdges isSelected={isSelected} />}
            </mesh>
        );

        // Stepped tiers (offsets measured from the footing base)
        const steps = geom.steppedData?.steps ?? [];
        steps.forEach((step: any, i: number) => {
            const stepH = (step.thickness ?? 0) / 1000;
            if (stepH <= 0) return;
            const stepBottom = bottomZ + (step.offset ?? 0) / 1000;
            parts.push(
                <mesh key={`step-${i}`} position={[0, 0, stepBottom + stepH / 2 - pos.z]}>
                    <boxGeometry args={[step.width / 1000, step.length / 1000, stepH]} />
                    {material}
                    {showEdges && <InkEdges isSelected={isSelected} />}
                </mesh>
            );
        });

        // Pedestal stub sitting on the footing top
        const pedestal = geom.pedestalData;
        if (pedestal?.enabled && (pedestal.height ?? 0) > 0) {
            const pedH = pedestal.height / 1000;
            const pedW = (pedestal.width ?? 0) / 1000;
            const pedL = (pedestal.length ?? pedestal.width ?? 0) / 1000;
            parts.push(
                <mesh key="pedestal" position={[0, 0, topZ + pedH / 2 - pos.z]}>
                    {pedestal.shape === 'Circular'
                        ? <cylinderGeometry args={[pedW / 2, pedW / 2, pedH, 32]} />
                        : <boxGeometry args={[pedW, pedL, pedH]} />}
                    {material}
                    {showEdges && <InkEdges isSelected={isSelected} />}
                </mesh>
            );
        }

        return (
            <group position={[pos.x, pos.y, pos.z]} rotation={[0, 0, rotationRad]} userData={{ id: element.id }}>
                {parts}
            </group>
        );
    }

    return null;
}

/**
 * Stair renderer backed by StairSolver: merges run/landing geometries into a
 * single mesh (riser/tread math handled by the solver).
 */
function StairRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const material = useViewMaterial('#d8d8d8', isSelected, viewMode, viewType);
    const mesh = useMemo(() => {
        try {
            return StairSolver.createStairMesh(element as any);
        } catch {
            return null;
        }
    }, [element]);
    // Empty geometry (no position attribute) must not reach <Edges>: 
// EdgesGeometry dereferences position.count and would throw.
        if ( !mesh || !mesh.geometry || !mesh.geometry.attributes.position || mesh.geometry.attributes.position.count === 0 ) return null; const isPlan = viewType === 'plan'; const isHiddenLine = viewMode === 'hiddenLine'; const isWireframe = viewMode === 'wireframe'; return (
<group userData={{ id: element.id }}>
            <mesh geometry={mesh.geometry}>
                {material}
                {(isPlan || isHiddenLine || isWireframe) && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
        </group>
    );
}

/**
 * Rebar renderer backed by RebarSolver: resolves the shape code, distributes
 * bars along the layout rule, and merges them into one tube mesh.
 */
function RebarRenderer({ element, isSelected, viewType, viewMode, hostWall }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string, hostWall?: BIMElement }) {
    const material = useViewMaterial('#cd6946', isSelected, viewMode, viewType);
    const mesh = useMemo(() => {
        try {
            const host = hostWall || { id: '', category: 'Structural Columns' };
            return RebarSolver.solve(element as any, host as any);
        } catch {
            return null;
        }
    }, [element, hostWall]);
    // Empty geometry (no position attribute) must not reach <Edges>: 
// EdgesGeometry dereferences position.count and would throw.
        if ( !mesh || !mesh.geometry || !mesh.geometry.attributes.position || mesh.geometry.attributes.position.count === 0 ) return null; const isPlan = viewType === 'plan'; const isHiddenLine = viewMode === 'hiddenLine'; const isWireframe = viewMode === 'wireframe'; return (
<group userData={{ id: element.id }}>
            <mesh geometry={mesh.geometry}>
                {material}
                {(isPlan || isHiddenLine || isWireframe) && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
        </group>
    );
}

/**
 * Curtain wall renderer backed by CurtainWallSolver: builds mullions/transoms
 * and glass panels from the curtain wall grid definition.
 */
function CurtainWallRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const group = useMemo(() => {
        try {
            return CurtainWallSolver.generateCurtainWall(element as any);
        } catch {
            return null;
        }
    }, [element]);
    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    if (!group || group.children.length === 0) return null;
    return (
        <group userData={{ id: element.id }}>
            {group.children.map((child, i) => {
                const meshChild = child as THREE.Mesh;
                if (!meshChild.geometry) return null;
                return (
                    <mesh key={i} geometry={meshChild.geometry} material={meshChild.material}>
                        {(isPlan || isHiddenLine || isWireframe) && (
                            <InkEdges isSelected={isSelected} />
                        )}
                    </mesh>
                );
            })}
        </group>
    );
}

/**
 * Structural connection marker (BIM: solid connection arrow/marker between
 * joined framing members). Draws a small double-triangle marker at the joint.
 */
function ConnectionRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    if (!geom?.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const color = isSelected ? '#00aaff' : '#ff8800';
    const s = 0.15; // marker size (m)

    const arrowUp = [
        new THREE.Vector3(-s, -s / 2, 0),
        new THREE.Vector3(0, s / 2, 0),
        new THREE.Vector3(s, -s / 2, 0),
    ];
    const arrowDown = arrowUp.map(p =>p.clone().multiplyScalar(-1)); return (<group position={pos} userData={{ id: element.id }}>
            <Line points={arrowUp} color={color} lineWidth={2} />
            <Line points={arrowDown} color={color} lineWidth={2} />
        </group>
    );
}

/**
 * Steel Plate renderer (Steel > Fabricate Elements > Plate). Extrudes the
 * sketch boundary polygon along the plate normal by its thickness.
 */
function SteelPlateRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#9aa7b2', isSelected, viewMode, viewType);

    const thickness = geom?.thickness ?? 0.01;
    const origin = new THREE.Vector3(...(geom?.origin ?? [0, 0, 0]));
    const normal = new THREE.Vector3(...(geom?.normal ?? [0, 0, 1])).normalize();
    const xDir = new THREE.Vector3(...(geom?.xDirection ?? [1, 0, 0]));
    xDir.addScaledVector(normal, -xDir.dot(normal));
    if (xDir.lengthSq() < 1e-8) xDir.set(1, 0, 0);
    xDir.normalize();
    const yDir = new THREE.Vector3().crossVectors(normal, xDir).normalize();

    const geometry = useMemo(() => {
        const boundary = geom?.boundary as [number, number][] | undefined;
        if (!boundary || boundary.length < 3) return null;
        const shape = new THREE.Shape();
        boundary.forEach(([x, y], i) => {
            if (i === 0) shape.moveTo(x, y);
            else shape.lineTo(x, y);
        });
        shape.closePath();
        return new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false });
    }, [geom, thickness]);

    // Local +Z of the extrude maps onto the plate normal.
    const quaternion = useMemo(
        () =>new THREE.Quaternion().setFromRotationMatrix( new THREE.Matrix4().makeBasis(xDir, yDir, normal) ), [xDir.x, xDir.y, xDir.z, yDir.x, yDir.y, yDir.z, normal.x, normal.y, normal.z] ); if (!geometry) return null; return (<group position={origin} quaternion={quaternion} userData={{ id: element.id }}>
            <mesh geometry={geometry}>
                {material}
                <InkEdges isSelected={isSelected} />
            </mesh>
        </group>
    );
}

/**
 * Steel Bolt group renderer (Steel > Fabricate Elements > Bolts). Draws the
 * bolt grid; hex heads only when the view detail level is Fine (BIM
 * behavior: bolt geometry hides at Coarse/Medium).
 */
function SteelBoltRenderer({ element, isSelected, viewType, viewMode, activeView }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string, activeView?: View }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#3d4650', isSelected, viewMode, viewType);
    if (!geom?.position) return null;

    const detailFine = !activeView || activeView.detailLevel === 'Fine';
    const axis = new THREE.Vector3(...(geom.axis ?? [0, 0, 1])).normalize();
    const rowDir = new THREE.Vector3(...(geom.rowDirection ?? [1, 0, 0]));
    const colDir = new THREE.Vector3(...(geom.columnDirection ?? [0, 1, 0]));
    const quaternion = new THREE.Quaternion().setFromRotationMatrix(
        new THREE.Matrix4().makeBasis(rowDir, colDir, axis)
    );

    const rows: number = geom.rowCount ?? 1;
    const cols: number = geom.columnCount ?? 1;
    const rowSpacing: number = geom.rowSpacing ?? 0.08;
    const colSpacing: number = geom.columnSpacing ?? 0.08;
    const diameter: number = (element.parameters?.['Bolt Diameter'] ?? 20) / 1000;
    const grip = 0.06;

    const offsets: Array<[number, number]>= []; for (let r = 0; r< rows; r++) {
        for (let c = 0; c < cols; c++) {
            offsets.push([(r - (rows - 1) / 2) * rowSpacing, (c - (cols - 1) / 2) * colSpacing]);
        }
    }

    return (
        <group position={new THREE.Vector3(...geom.position)} quaternion={quaternion} userData={{ id: element.id }}>
            {offsets.map(([rx, cy], i) => (
                <group key={i} position={[rx, cy, 0]}>
                    {/* Shank */}
                    <mesh position={[0, 0, -grip / 2]}>
                        <cylinderGeometry args={[diameter / 2, diameter / 2, grip, 10]} />
                        {material}
                    </mesh>
                    {/* Hex head + nut, Fine detail only */}
                    {detailFine && (
                        <>
                            <mesh position={[0, 0, 0.008]}>
                                <cylinderGeometry args={[diameter * 0.85, diameter * 0.85, 0.016, 6]} />
                                {material}
                            </mesh>
                            <mesh position={[0, 0, -grip - 0.008]}>
                                <cylinderGeometry args={[diameter * 0.8, diameter * 0.8, 0.016, 6]} />
                                {material}
                            </mesh>
                        </>
                    )}
                </group>
            ))}
        </group>
    );
}

/**
 * Steel Weld renderer (Steel > Fabricate Elements > Welds). Draws the weld
 * run as a thick seam line; shop welds in BIM-like blue, site welds red.
 */
function SteelWeldRenderer({ element, isSelected, viewType, activeView }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string, activeView?: View }) {
    const geom = (element as any).geometryData;
    if (!geom?.path || geom.path.length < 2) return null;

    // Weld annotations only appear at Fine/Medium detail, matching BIM's
    // hidden-at-Coarse behavior.
    if (activeView?.detailLevel === 'Coarse') return null;

    const points = geom.path.map((p: number[]) =>new THREE.Vector3(...p)); const isSite = geom.location === 'Site'; const color = isSelected ? '#00aaff' : (isSite ? '#cc3333' : '#3366cc'); return (<group userData={{ id: element.id }}>
            <Line points={points} color={color} lineWidth={3} />
            {viewType === 'plan' && (
                <Line points={points} color={color} lineWidth={1} dashed dashScale={8} />
            )}
        </group>
    );
}

/** Duct fitting (elbow): a rectangular block at the placed point. */
function DuctFittingRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial(PREVIEW_COLORS.mep, isSelected, viewMode, viewType);
    if (!geom?.position) return null;

    // Size parameter "300x300 mm" ->0.3 m body
        const size = 0.3; const pos = new THREE.Vector3(...geom.position); const rotation = THREE.MathUtils.degToRad(geom.rotation ?? 0); return (<group position={pos} rotation={[0, 0, rotation]} userData={{ id: element.id }}>
            <mesh>
                <boxGeometry args={[size, size, size]} />
                {material}
                {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
        </group>
    );
}

/** Pipe fitting (elbow): a small cylinder joint at the placed point. */
function PipeFittingRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#444444', isSelected, viewMode, viewType);
    if (!geom?.position) return null;

    // Size parameter "100 mm" ->0.1 m diameter
        const d = 0.1; const pos = new THREE.Vector3(...geom.position); const rotation = THREE.MathUtils.degToRad(geom.rotation ?? 0); return (<group position={pos} rotation={[0, 0, rotation]} userData={{ id: element.id }}>
            <mesh>
                <cylinderGeometry args={[d / 2, d / 2, d * 1.5, 16]} />
                {material}
                {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
        </group>
    );
}

/**
 * Link / import wrapper. When the link tool has delivered parsed CAD line
 * work (geometryData.cad present) we draw the real drawing; otherwise — or
 * while a parse is still running — we draw the BIM-style placeholder box
 * plus the file-name label.
 */
function LinkRenderer({ element, isSelected, viewMode }: { element: BIMElement, isSelected: boolean, viewMode: string }) {
    const geom = (element as any).geometryData;
    // File-name label is an annotation: constant paper size at any scale.
    const s = useAnnotationScaleFactor();
    const ink = useAnnotationInk();

    // Parsed point-cloud payload: a vertex buffer with per-vertex colors.
    // POINTCLOUDCROP (params) filters to an optional world-space XY window.
    const pointsGeometry = useMemo(() => {
        const pts = geom?.points as number[] | Float32Array | undefined;
        if (!pts || pts.length < 3) return null;
        const cropMin = element.parameters?.['Crop Min'] as [number, number] | undefined;
        const cropMax = element.parameters?.['Crop Max'] as [number, number] | undefined;
        const origin = (geom?.origin as number[]) ?? [0, 0, 0];
        const g = new THREE.BufferGeometry();
        const cols = geom?.colors as number[] | Float32Array | undefined;
        const flat = Float32Array.from(pts);
        if (cropMin && cropMax && cropMin.length >= 2 && cropMax.length >= 2) {
            // Keep points inside the crop box (scan-local + origin -> world).
        const keptPos: number[] = []; const keptCol: number[] = [];
        for (let i = 0; i + 2 < flat.length; i += 3) {
                const wx = flat[i] + origin[0], wy = flat[i + 1] + origin[1];
                if (wx >= cropMin[0] && wx<= cropMax[0] && wy >= cropMin[1] && wy<= cropMax[1]) {
                    keptPos.push(flat[i], flat[i + 1], flat[i + 2]);
                    if (cols && cols.length === pts.length) keptCol.push(cols[i], cols[i + 1], cols[i + 2]);
                }
            }
            g.setAttribute('position', new THREE.BufferAttribute(Float32Array.from(keptPos), 3));
            if (keptCol.length && cols) g.setAttribute('color', new THREE.BufferAttribute(Float32Array.from(keptCol), 3));
        } else {
            g.setAttribute('position', new THREE.BufferAttribute(flat, 3));
            if (cols && cols.length === pts.length) {
                g.setAttribute('color', new THREE.BufferAttribute(Float32Array.from(cols), 3));
            }
        }
        return g;
    }, [geom?.points, geom?.colors, element.parameters?.['Crop Min'], element.parameters?.['Crop Max']]);
    useEffect(() => () => { pointsGeometry?.dispose(); }, [pointsGeometry]);

    // Linked PDF/Image underlay: blob URL written by Link PDF / Image.
    const underlayUrl = element.parameters?.['Underlay URL'] as string | undefined;
    const [underlayTexture, setUnderlayTexture] = useState<THREE.Texture | null>(null);
    useEffect(() => {
        if (!underlayUrl) { setUnderlayTexture(null); return; }
        let disposed = false;
        new THREE.TextureLoader().load(underlayUrl, (tex) => {
            if (disposed) { tex.dispose(); return; }
            tex.colorSpace = THREE.SRGBColorSpace;
            setUnderlayTexture(tex);
        });
        return () => { disposed = true; };
    }, [underlayUrl]);

    if (!geom?.origin) return null;

    const origin = new THREE.Vector3(...geom.origin);
    const boxSize = geom.boxSize as [number, number, number] | undefined;
    const isPlan = viewMode === 'wireframe';
    const color = isSelected ? '#00aaff' : '#777777';

    // Parsed CAD payload: draw the actual line work instead of a box.
    const hasCad = !!geom.cad && Array.isArray(geom.lines) && geom.lines.length > 0;
    if (hasCad) {
        const cadSize = boxSize ?? [10, 10, 1];
        return (
            <group position={origin} rotation={geom.rotation as [number, number, number] ?? [0, 0, 0]} userData={{ id: element.id }}>
                {/* Invisible thin footprint so the line work stays clickable for
                    selection + the Reload context menu (links select by their
                    extents region, like BIM). A flat plane at z=0 never
                    shadows 3D elements above or below the drawing. */}
                <mesh position={[0, 0, -0.05]}>
                    <planeGeometry args={[Math.max(cadSize[0], 1) * 1.02, Math.max(cadSize[1], 1) * 1.02]} />
                    <meshBasicMaterial transparent opacity={0} depthWrite={false} side={THREE.DoubleSide} />
                </mesh>
                <CadGeometryRenderer geom={geom} isSelected={isSelected} />
                {isSelected && (
                    <Line
                        points={[
                            [-Math.max(cadSize[0], 1) * 0.51, -Math.max(cadSize[1], 1) * 0.51, 0.01],
                            [Math.max(cadSize[0], 1) * 0.51, -Math.max(cadSize[1], 1) * 0.51, 0.01],
                            [Math.max(cadSize[0], 1) * 0.51, Math.max(cadSize[1], 1) * 0.51, 0.01],
                            [-Math.max(cadSize[0], 1) * 0.51, Math.max(cadSize[1], 1) * 0.51, 0.01],
                            [-Math.max(cadSize[0], 1) * 0.51, -Math.max(cadSize[1], 1) * 0.51, 0.01],
                        ]}
                        color={color}
                        lineWidth={1}
                    />
                )}
                <Text
                    position={[0, 0, (boxSize?.[2] ?? 1) / 2 + 0.2]}
                    fontSize={0.4 * s}
                    color={color}
                    anchorX="center"
                    anchorY="bottom"
                    outlineWidth={0.02}
                    outlineColor="#ffffff"
                >
                    {element.type}
                </Text>
            </group>
        );
    }

    // Parsed point-cloud payload: render the actual scan vertices.
    if (pointsGeometry) {
        const cloudSize = boxSize ?? [10, 10, 5];
        return (
            <group position={origin} rotation={geom.rotation as [number, number, number] ?? [0, 0, 0]} userData={{ id: element.id }}>
                <points geometry={pointsGeometry}>
                    <pointsMaterial size={0.03} vertexColors={!!pointsGeometry.getAttribute('color')} color={isSelected ? '#00aaff' : '#c8d6e5'} sizeAttenuation transparent opacity={0.85} />
                </points>
                {isSelected && (
                    <Line
                        points={[
                            [-cloudSize[0] / 2, -cloudSize[1] / 2, 0],
                            [cloudSize[0] / 2, -cloudSize[1] / 2, 0],
                            [cloudSize[0] / 2, cloudSize[1] / 2, 0],
                            [-cloudSize[0] / 2, cloudSize[1] / 2, 0],
                            [-cloudSize[0] / 2, -cloudSize[1] / 2, 0],
                        ]}
                        color="#00aaff"
                        lineWidth={1}
                    />
                )}
                <Text
                    position={[0, 0, cloudSize[2] / 2 + 0.2]}
                    fontSize={0.4 * s}
                    color={isSelected ? '#00aaff' : '#777777'}
                    anchorX="center"
                    anchorY="bottom"
                    outlineWidth={0.02}
                    outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}
                >
                    {element.type}
                </Text>
            </group>
        );
    }

    // Linked PDF/Image underlay: the blob URL, texture state and loader are
    // declared above (single copy); render the textured page here.
    if (underlayUrl) {
        const uSize = boxSize ?? [0.216, 0.279, 0.001];
        return (
            <group position={origin} rotation={geom.rotation as [number, number, number] ?? [0, 0, 0]} userData={{ id: element.id }}>
                <mesh>
                    <planeGeometry args={[Math.max(uSize[0], 0.01), Math.max(uSize[1], 0.01)]} />
                    {underlayTexture ? (
                        <meshBasicMaterial map={underlayTexture} transparent side={THREE.DoubleSide} />
                    ) : (
                        <meshBasicMaterial color={isSelected ? '#00aaff' : '#dddddd'} transparent opacity={0.5} side={THREE.DoubleSide} />
                    )}
                </mesh>
                {isSelected && (
                    <Line
                        points={[
                            [-uSize[0] / 2, -uSize[1] / 2, 0.001],
                            [uSize[0] / 2, -uSize[1] / 2, 0.001],
                            [uSize[0] / 2, uSize[1] / 2, 0.001],
                            [-uSize[0] / 2, uSize[1] / 2, 0.001],
                            [-uSize[0] / 2, -uSize[1] / 2, 0.001],
                        ]}
                        color="#00aaff"
                        lineWidth={2}
                    />
                )}
                <Text
                    position={[0, Math.max(uSize[1], 0.01) / 2 + 0.2, 0]}
                    fontSize={0.4 * s}
                    color={isSelected ? '#00aaff' : '#777777'}
                    anchorX="center"
                    anchorY="bottom"
                    outlineWidth={0.02}
                    outlineColor="#ffffff"
                >
                    {element.type}
                </Text>
            </group>
        );
    }

    // Fallback footprint when the link tool didn't write a boxSize
    const size: [number, number, number] = boxSize ?? [10, 10, 5];

    return (
        <group position={origin} rotation={geom.rotation as [number, number, number] ?? [0, 0, 0]} userData={{ id: element.id }}>
            <mesh>
                <boxGeometry args={[size[0], size[1], size[2]]} />
                <meshStandardMaterial color={color} transparent opacity={0.25} depthWrite={false} />
                <Edges color={color} threshold={15} />
            </mesh>
            <Text
                position={[0, 0, size[2] / 2 + 0.2]}
                fontSize={0.4 * s}
                color={color}
                anchorX="center"
                anchorY="bottom"
                outlineWidth={0.02}
                outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}
            >
                {element.type}
            </Text>
            {isPlan && null}
        </group>
    );
}

/**
 * Merged CAD line-work renderer: one THREE.LineSegments with baked per-vertex
 * colors (so layer/entity colors display exactly like 2D) plus drei Text
 * labels for TEXT/MTEXT entities. Geometry is memoized on the element object
 * identity; selection recolors cheaply through material.color.
 */
function CadGeometryRenderer({ geom, isSelected, depthTest = true }: { geom: any; isSelected: boolean; depthTest?: boolean }) {
    const lines = geom.lines as { points: [[number, number, number], [number, number, number]]; color: string; layer: string; dash?: number[]; width?: number }[];
    const texts = (geom.texts ?? []) as { x: number; y: number; z: number; text: string; height: number; rotation: number; color: string }[];
    const layerVisible = new Map<string, boolean>((geom.layers ?? []).map((l: any) => [l.name, l.visible !== false]));

    // Solid/dashed split + width offsets (DXF linetype + polyline width):
    // dashed segments go to a second LineDashed batch; wide segments also
    // get two parallel offset edges so a thick polyline reads as a band.
    const { geometry, dashedGeometry, dashedScale } = useMemo(() => {
        const visible = lines.filter(ln => layerVisible.get(ln.layer) !== false);
        const solidBuf: { pts: number[]; cols: number[] } = { pts: [], cols: [] };
        const dashBuf: { pts: number[]; cols: number[] } = { pts: [], cols: [] };
        const c = new THREE.Color();
        let dashScale = 1;
        visible.forEach((ln) => {
            const buf = ln.dash && ln.dash.length >= 2 ? dashBuf : solidBuf;
            if (ln.dash && ln.dash.length >= 2) {
                dashScale = Math.max(dashScale, Math.abs(ln.dash[0]) + Math.abs(ln.dash[1]));
            }
            const add = (ax: number, ay: number, az: number, bx: number, by: number, bz: number) => {
                buf.pts.push(ax, ay, az, bx, by, bz);
                c.set(ln.color || '#c8c8c8');
                buf.cols.push(c.r, c.g, c.b, c.r, c.g, c.b);
            };
            add(ln.points[0][0], ln.points[0][1], ln.points[0][2], ln.points[1][0], ln.points[1][1], ln.points[1][2]);
            if (ln.width && ln.width > 0.01) {
                const dx = ln.points[1][0] - ln.points[0][0];
                const dy = ln.points[1][1] - ln.points[0][1];
                const l = Math.hypot(dx, dy);
                if (l > 1e-6) {
                    const ox = -dy / l * ln.width / 2, oy = dx / l * ln.width / 2;
                    add(ln.points[0][0] + ox, ln.points[0][1] + oy, ln.points[0][2], ln.points[1][0] + ox, ln.points[1][1] + oy, ln.points[1][2]);
                    add(ln.points[0][0] - ox, ln.points[0][1] - oy, ln.points[0][2], ln.points[1][0] - ox, ln.points[1][1] - oy, ln.points[1][2]);
                }
            }
        });
        const mkGeom = (buf: { pts: number[]; cols: number[] }) => {
            const g = new THREE.BufferGeometry();
            g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(buf.pts), 3));
            g.setAttribute('color', new THREE.BufferAttribute(new Float32Array(buf.cols), 3));
            g.computeBoundingSphere();
            return g;
        };
        const solid = mkGeom(solidBuf);
        const dashed = mkGeom(dashBuf);
        // LineDashedMaterial needs the `lineDistance` attribute: per pair of
        // endpoints, distances start at 0 and advance by the segment length.
        {
            const posAttr = dashed.getAttribute('position') as THREE.BufferAttribute;
            const n = posAttr.count;
            const dist = new Float32Array(n);
            for (let i = 0; i + 1 < n; i += 2) {
                const a = new THREE.Vector3().fromBufferAttribute(posAttr, i);
                const b = new THREE.Vector3().fromBufferAttribute(posAttr, i + 1);
                const len = a.distanceTo(b);
                dist[i] = 0; dist[i + 1] = len;
            }
            dashed.setAttribute('lineDistance', new THREE.BufferAttribute(dist, 1));
        }
        return { geometry: solid, dashedGeometry: dashed, dashedScale: dashScale };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lines, geom.layers]);

    // Dispose the previous buffer when it is replaced or the element unmounts.
    useEffect(() => () => { geometry.dispose(); dashedGeometry.dispose(); }, [geometry, dashedGeometry]);

    return (
        <group>
            <lineSegments geometry={geometry}>
                <lineBasicMaterial vertexColors color={isSelected ? '#00aaff' : '#ffffff'} depthTest={depthTest} />
            </lineSegments>
            {dashedGeometry.getAttribute('position').count > 0 && (
                <lineSegments geometry={dashedGeometry}>
                    <lineDashedMaterial vertexColors color={isSelected ? '#00aaff' : '#ffffff'}
                        dashSize={dashedScale * 0.6} gapSize={dashedScale * 0.4} depthTest={depthTest} />
                </lineSegments>
            )}
            {texts.slice(0, 2000).map((tx, i) => (
                <Text
                    key={i}
                    position={[tx.x, tx.y, tx.z + 0.005]}
                    rotation={[0, 0, THREE.MathUtils.degToRad(tx.rotation ?? 0)]}
                    fontSize={Math.max(tx.height, 0.02)}
                    color={isSelected ? '#00aaff' : tx.color || '#c8c8c8'}
                    anchorX="left"
                    anchorY="bottom"
                >
                    {tx.text}
                </Text>
            ))}
        </group>
    );
}

/** Raster image plane: textured quad placed on the view plane. */
function ImageRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    if (!geom?.position || !(geom.width > 0) || !(geom.height >0)) return null; const pos = new THREE.Vector3(...geom.position); const rotation = THREE.MathUtils.degToRad(geom.rotation ?? 0); const opacity = typeof geom.opacity === 'number' ? geom.opacity : 1; 
// Load the raster (blob/data URL written by the Image tool) asynchronously
        const [texture, setTexture] = useState
<THREE.Texture | null>(null);
    const sourceUrl = geom.sourceUrl as string | undefined;
    useEffect(() => {
        if (!sourceUrl) { setTexture(null); return; }
        let disposed = false;
        new THREE.TextureLoader().load(sourceUrl, (tex) => {
            if (disposed) { tex.dispose(); return; }
            tex.colorSpace = THREE.SRGBColorSpace;
            setTexture(tex);
        });
        return () => { disposed = true; };
    }, [sourceUrl]);

    // Crop Image (contextual): local-space crop rect (origin at image center).
    // A cropped plane gets a dedicated geometry whose UVs remap onto the crop
    // window, so the raster shows only the crop region.
    const crop = geom.crop as { minX: number; maxX: number; minY: number; maxY: number } | undefined;
    const drawOrder = element.parameters?.['Draw Order'];
    const depthTest = drawOrder !== 'Foreground';
    const croppedGeometry = useMemo(() => {
        if (!crop) return null;
        const halfW = geom.width / 2;
        const halfH = geom.height / 2;
        const x0 = Math.max(crop.minX, -halfW);
        const x1 = Math.min(crop.maxX, halfW);
        const y0 = Math.max(crop.minY, -halfH);
        const y1 = Math.min(crop.maxY, halfH);
        const geo = new THREE.PlaneGeometry(Math.max(0.01, x1 - x0), Math.max(0.01, y1 - y0));
        const uvAttr = geo.getAttribute('uv') as THREE.BufferAttribute;
        const u0 = (x0 + halfW) / geom.width;
        const u1 = (x1 + halfW) / geom.width;
        const v0 = (y0 + halfH) / geom.height;
        const v1 = (y1 + halfH) / geom.height;
        // PlaneGeometry UV order: (0,1) top-left, (1,1) top-right, (0,0) bottom-left, (1,0) bottom-right
        uvAttr.setXY(0, u0, v1);
        uvAttr.setXY(1, u1, v1);
        uvAttr.setXY(2, u0, v0);
        uvAttr.setXY(3, u1, v0);
        uvAttr.needsUpdate = true;
        return geo;
    }, [geom.width, geom.height, crop]);
    useEffect(() => () => { croppedGeometry?.dispose(); }, [croppedGeometry]);
    const cropCenter: [number, number, number] = crop
        ? [(crop.minX + crop.maxX) / 2, (crop.minY + crop.maxY) / 2, 0]
        : [0, 0, 0];

    return (
        <group position={pos} rotation={[0, 0, rotation]} userData={{ id: element.id }}>
            <mesh geometry={croppedGeometry ?? undefined} position={croppedGeometry ? cropCenter : [0, 0, 0]}>
                {!croppedGeometry && <planeGeometry args={[geom.width, geom.height]} />}
                {texture ? (
                    <meshBasicMaterial map={texture} transparent opacity={opacity} side={THREE.DoubleSide} depthTest={depthTest} />) : geom.sourceUrl ? (<meshBasicMaterial transparent opacity={opacity} side={THREE.DoubleSide} depthTest={depthTest} />
                ) : (
                    <meshBasicMaterial color={isSelected ? '#00aaff' : '#dddddd'} transparent opacity={opacity * 0.6} side={THREE.DoubleSide} depthTest={depthTest} />
                )}
            </mesh>
            {isSelected && (
                <Line
                    points={[
                        [-geom.width / 2, -geom.height / 2, 0.001],
                        [geom.width / 2, -geom.height / 2, 0.001],
                        [geom.width / 2, geom.height / 2, 0.001],
                        [-geom.width / 2, geom.height / 2, 0.001],
                        [-geom.width / 2, -geom.height / 2, 0.001],
                    ]}
                    color="#00aaff"
                    lineWidth={2}
                />
            )}
        </group>
    );
}

/** Decal: textured overlay quad on a host face. */
function DecalRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    if (!geom?.position || !(geom.width > 0) || !(geom.height >0)) return null; const pos = new THREE.Vector3(...geom.position); const normal = new THREE.Vector3(...(geom.normal ?? [0, 0, 1])); const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal.normalize()); return (<group position={pos} quaternion={quaternion} userData={{ id: element.id }}>
            <mesh>
                <planeGeometry args={[geom.width, geom.height]} />
                <meshBasicMaterial color={isSelected ? '#00aaff' : '#c080ff'} transparent opacity={0.75} side={THREE.DoubleSide} />
            </mesh>
            <Edges color={isSelected ? '#00aaff' : '#8844cc'} threshold={15} />
        </group>
    );
}

/** Detail component (2D detail item): small footprint box like a component. */
function DetailComponentRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#888888', isSelected, viewMode, viewType);
    const ink = useAnnotationInk();
    const s = useAnnotationScaleFactor();
    const color = isSelected ? '#00aaff' : ink;

    // Region sketches (Filled/Masking Region) live in Detail Items too.
    if (Array.isArray(geom?.boundary) && geom.boundary.length >= 3) {
        return <RegionShapeRenderer element={element} isSelected={isSelected} />;
    }

    // Insulation batting: zigzag ribbon between start/end.
    if (element.type.startsWith('Insulation') && geom?.start && geom?.end) {
        return <InsulationRenderer element={element} isSelected={isSelected} />;
    }

    // Keynote tags: circled number + leader dot.
    if (element.type.includes('Keynote') && geom?.position) {
        return <KeynoteRenderer element={element} isSelected={isSelected} />;
    }

    // Color Fill Legend: chip grid placed in the view.
    if (element.type.includes('Color Fill Legend') && geom?.entries) {
        return <ColorFillLegendRenderer element={element} isSelected={isSelected} />;
    }

    // Detail line (DetailLineTool): plain 2D stroke between start/end points.
    // Theme-aware ink: black on the light viewport, white on the dark one.
    if (element.type.startsWith('Detail Line') && geom?.start && geom?.end) {
        const styleWeight = /Heavy/i.test(geom.style ?? element.type) ? 3
            : /Wide/i.test(geom.style ?? element.type) ? 2.4
                : /Medium/i.test(geom.style ?? element.type) ? 1.9
                    : 1.3;
        return (
            <group userData={{ id: element.id }}>
                <Line
                    points={[geom.start, geom.end]}
                    color={isSelected ? '#00aaff' : ink}
                    lineWidth={isSelected ? 3 : styleWeight}
                />
            </group>
        );
    }

    // Repeating detail: brick-course rectangles along the drawn stroke
    if (element.type === 'Repeating Detail' && geom?.start && geom?.end) {
        const start = new THREE.Vector3(...geom.start);
        const end = new THREE.Vector3(...geom.end);
        const length = start.distanceTo(end);
        const spacing = Math.max(geom.spacing ?? 0.25, 0.05);
        const count = Math.max(Math.floor(length / spacing), 1);
        const dir = end.clone().sub(start).normalize();
        const perp = new THREE.Vector3(-dir.y, dir.x, 0);

        return (
            <group userData={{ id: element.id }}>
                {/* Baseline course lines */}
                <Line points={[start.clone().add(perp.clone().multiplyScalar(spacing * 0.35)), end.clone().add(perp.clone().multiplyScalar(spacing * 0.35))]} color={color} lineWidth={1} />
                <Line points={[start.clone().sub(perp.clone().multiplyScalar(spacing * 0.35)), end.clone().sub(perp.clone().multiplyScalar(spacing * 0.35))]} color={color} lineWidth={1} />
                {Array.from({ length: count }, (_, i) => {
                    const p = start.clone().add(dir.clone().multiplyScalar(spacing * (i + 0.5)));
                    const tick = perp.clone().multiplyScalar(spacing * 0.35);
                    // Alternating face-shell offset like a real brick bond
                    const shift = i % 2 === 0 ? dir.clone().multiplyScalar(spacing * 0.5) : new THREE.Vector3();
                    return (
                        <Line
                            key={i}
                            points={[p.clone().sub(tick).sub(shift), p.clone().add(tick).sub(shift)]}
                            color={color}
                            lineWidth={1}
                        />
                    );
                })}
            </group>
        );
    }

    if (!geom?.position) return null;

    // Detail Component 2D symbols (Annotate > Detail > Component): paper-sized
    // glyphs placed in the view; rotate with the stored rotation.
    if (element.type.startsWith('Detail Component') && geom?.symbol) {
        const dpos = new THREE.Vector3(...geom.position);
        const rot = THREE.MathUtils.degToRad(geom.rotation ?? 0);
        const sz = 0.25 * s;
        const sym: string = geom.symbol;
        return (
            <group position={[dpos.x, dpos.y, dpos.z + 0.01]} rotation={[0, 0, rot]} userData={{ id: element.id }}>
                {sym === 'Brick' && (
                    <>
                        <Line points={[[-sz, -sz * 0.5, 0], [sz, -sz * 0.5, 0], [sz, sz * 0.5, 0], [-sz, sz * 0.5, 0], [-sz, -sz * 0.5, 0]]} color={color} lineWidth={1.3} />
                        <Line points={[[0, sz * 0.5, 0], [0, -sz * 0.5, 0]]} color={color} lineWidth={0.8} />
                    </>
                )}
                {sym === 'Block' && (
                    <>
                        <Line points={[[-sz, -sz * 0.7, 0], [sz, -sz * 0.7, 0], [sz, sz * 0.7, 0], [-sz, sz * 0.7, 0], [-sz, -sz * 0.7, 0]]} color={color} lineWidth={1.3} />
                        <Line points={[[-sz, 0, 0], [sz, 0, 0]]} color={color} lineWidth={0.8} />
                    </>
                )}
                {sym === 'Bolt' && (
                    <>
                        <Line points={circlePoints(0, 0, sz * 0.8, 0)} color={color} lineWidth={1.3} />
                        <Line points={circlePoints(0, 0, sz * 0.35, 0)} color={color} lineWidth={1} />
                    </>
                )}
                {sym === 'Dot' && (
                    <mesh>
                        <circleGeometry args={[sz * 0.18, 12]} />
                        <meshBasicMaterial color={color} />
                    </mesh>
                )}
            </group>
        );
    }

    const pos = new THREE.Vector3(...geom.position);
    const rotation = THREE.MathUtils.degToRad(geom.rotation ?? 0);
    const width = geom.width ?? 0.5;
    const depth = geom.depth ?? 0.5;
    const height = geom.height ?? 0.5;

    return (
        <group position={pos} rotation={[0, 0, rotation]} userData={{ id: element.id }}>
            <mesh position={[0, 0, height / 2]}>
                <boxGeometry args={[width, depth, height]} />
                {material}
                {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
        </group>
    );
}

/**
 * Region renderer (Filled / Masking Region). Filled regions get real chord-
 * clipped hatch lines per the pattern chosen in the options bar; masking
 * regions paint an opaque cover that hides what is behind them (drawn as a
 * dashed outline + solid white plate). Boundary polygons stored in Detail
 * Items by RegionTool and legacy 'Filled Regions' elements both flow here.
 */
function RegionShapeRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    const ink = useAnnotationInk();
    const isMasking = element.type.startsWith('Masking')
        || geom?.variant === 'masking'
        || (element as any).category === 'Masking Regions';
    if (!geom?.boundary || geom.boundary.length < 3) return null;

    const boundary = geom.boundary as [number, number][];
    const elevation = typeof geom.elevation === 'number' ? geom.elevation : 0;

    const shape = new THREE.Shape();
    shape.moveTo(boundary[0][0], boundary[0][1]);
    for (let i = 1; i < boundary.length; i++) {
        shape.lineTo(boundary[i][0], boundary[i][1]);
    }
    shape.closePath();

    const shapeGeo = new THREE.ShapeGeometry(shape);
    const outline = [...boundary.map(p => new THREE.Vector3(p[0], p[1], 0)), new THREE.Vector3(boundary[0][0], boundary[0][1], 0)];

    const pattern = String(geom.pattern ?? 'Diagonal Crosshatch');
    let hatches: [THREE.Vector3, THREE.Vector3][] = [];
    if (!isMasking) {
        const patDef = resolveHatchPattern(pattern);
        if (patDef) {
            // Imported/built-in .pat definition: exact line-family clipping
            // with dash support, at the region's native pattern scale.
            hatches = generatePatHatchSegments(boundary as PatHatchPoint[], patDef).map((s) => [
                new THREE.Vector3(s[0], s[1], s[2]),
                new THREE.Vector3(s[3], s[4], s[5]),
            ]);
        } else if (pattern === 'ANSI31' || pattern === 'Diagonal Crosshatch') {
            hatches = hatchSegments(boundary, 45, 0.15);
            if (pattern === 'Diagonal Crosshatch') {
                hatches = hatches.concat(hatchSegments(boundary, -45, 0.15));
            }
        } else if (pattern === 'ANSI37') {
            hatches = hatchSegments(boundary, 45, 0.1).concat(hatchSegments(boundary, -45, 0.3));
        } else if (pattern === 'Concrete') {
            hatches = hatchSegments(boundary, 30, 0.2)
                .concat(hatchSegments(boundary, 120, 0.45))
                .concat(hatchSegments(boundary, -35, 0.6));
        } else if (pattern === 'Insulation') {
            hatches = hatchSegments(boundary, 45, 0.08);
        }
    }

    return (
        <group userData={{ id: element.id }} position={[0, 0, elevation + (isMasking ? 0.03 : 0.02)]}>
            {isMasking ? (
                // Opaque cover plate: hides model geometry behind the region.
                <mesh geometry={shapeGeo} renderOrder={5}>
                    <meshBasicMaterial color={ink === '#ffffff' ? '#1a1a1a' : '#ffffff'} side={THREE.DoubleSide} />
                </mesh>
            ) : (
                <mesh geometry={shapeGeo}>
                    <meshBasicMaterial color={isSelected ? '#00aaff' : '#999999'} transparent opacity={pattern === 'Solid Fill' ? 0.6 : 0.18} side={THREE.DoubleSide} />
                </mesh>
            )}
            {!isMasking && hatches.map((seg, i) => (
                <Line key={i} points={[seg[0], seg[1]]} color={isSelected ? '#00aaff' : ink} lineWidth={0.7} />
            ))}
            <Line
                points={outline}
                color={isSelected ? '#00aaff' : ink}
                lineWidth={1.5}
                dashed={isMasking}
                dashSize={0.15}
                gapSize={0.1}
            />
        </group>
    );
}

/** Filled region legacy entry point ('Filled Regions' category). */
function FilledRegionRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    return <RegionShapeRenderer element={element} isSelected={isSelected} />;
}

/** Insulation batting: BIM's wavy double-line ribbon between two points. */
function InsulationRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    const ink = useAnnotationInk();
    if (!geom?.start || !geom?.end) return null;

    const start = new THREE.Vector3(...geom.start);
    const end = new THREE.Vector3(...geom.end);
    const length = start.distanceTo(end);
    if (length < 0.001) return null;

    const width = Math.max(geom.width ?? 0.089, 0.01);
    const dir = end.clone().sub(start).normalize();
    const perp = new THREE.Vector3(-dir.y, dir.x, 0);
    const color = isSelected ? '#00aaff' : ink;

    // Two parallel wavy lines (sine bumps) — BIM's batting symbol.
    const wave = (offsetSign: number): THREE.Vector3[] => {
        const pts: THREE.Vector3[] = [];
        const steps = Math.max(Math.round(length / 0.05), 8);
        for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            const base = start.clone().add(dir.clone().multiplyScalar(length * t));
            const bump = Math.sin(t * Math.PI * (length / 0.15)) * width * 0.5 * offsetSign;
            pts.push(base.add(perp.clone().multiplyScalar(offsetSign * width / 2 + bump)));
        }
        return pts;
    };

    return (
        <group userData={{ id: element.id }}>
            <Line points={wave(1)} color={color} lineWidth={1.2} />
            <Line points={wave(-1)} color={color} lineWidth={1.2} />
        </group>
    );
}

/** Keynote tag: circled number with leader to its target. */
function KeynoteRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    const s = useAnnotationScaleFactor();
    const ink = useAnnotationInk();
    if (!geom?.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const value = String(element.parameters['Key Value'] ?? '?');
    const color = isSelected ? '#00aaff' : ink;
    const r = 0.15 * s;

    const tip = Array.isArray(geom.leaderTip) ? new THREE.Vector3(...geom.leaderTip) : null;

    return (
        <group userData={{ id: element.id }}>
            {tip && (
                <Line
                    points={[[pos.x, pos.y, pos.z], [tip.x, tip.y, tip.z]]}
                    color={color}
                    lineWidth={0.9}
                />
            )}
            <group position={[pos.x, pos.y, pos.z]}>
                <Line points={circlePoints(0, 0, r, 0.01)} color={color} lineWidth={1.4} />
                <Text
                    position={[0, 0, 0.015]}
                    fontSize={r * 1.15}
                    color={color}
                    anchorX="center"
                    anchorY="middle"
                    outlineWidth={0.01 * s}
                    outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}
                >
                    {value}
                </Text>
            </group>
        </group>
    );
}

/** Color Fill Legend: BIM-style chip + label table placed in the view. */
function ColorFillLegendRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    const s = useAnnotationScaleFactor();
    const ink = useAnnotationInk();
    if (!geom?.position || !Array.isArray(geom.entries)) return null;

    const pos = new THREE.Vector3(...geom.position);
    const entries = geom.entries as { label: string, color: string, count: number }[];
    const rowH = 0.3 * s;
    const chip = rowH * 0.75;
    const width = 2.6 * s;
    const height = (entries.length + 1) * rowH + 0.15 * s;
    const frame = isSelected ? '#00aaff' : ink;

    return (
        <group position={[pos.x, pos.y, pos.z]} userData={{ id: element.id }}>
            {/* Outer frame */}
            <Line
                points={[
                    [0, 0, 0.01], [width, 0, 0.01], [width, -height, 0.01],
                    [0, -height, 0.01], [0, 0, 0.01],
                ]}
                color={frame}
                lineWidth={1.2}
            />
            {/* Title row */}
            <Text
                position={[0.1 * s, -rowH * 0.6, 0.012]}
                fontSize={rowH * 0.5}
                color={frame}
                anchorX="left"
                anchorY="middle"
                outlineWidth={0.01 * s}
                outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}
            >
                {String(element.parameters['Color Fill Criteria'] ?? 'Legend')}
            </Text>
            <Line
                points={[[0, -rowH, 0.011], [width, -rowH, 0.011]]}
                color={frame}
                lineWidth={0.8}
            />
            {entries.map((entry, i) => (
                <group key={i}>
                    <mesh position={[0.15 * s + chip / 2, -rowH * (i + 1.5), 0.012]}>
                        <planeGeometry args={[chip, chip]} />
                        <meshBasicMaterial color={entry.color} />
                    </mesh>
                    <Text
                        position={[0.15 * s + chip + 0.08 * s, -rowH * (i + 1.5), 0.012]}
                        fontSize={rowH * 0.42}
                        color={frame}
                        anchorX="left"
                        anchorY="middle"
                        outlineWidth={0.008 * s}
                        outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}
                    >
                        {entry.label}
                    </Text>
                </group>
            ))}
        </group>
    );
}

/**
 * Cable tray: U-profile side rails plus (for ladder/ventilated types) rungs
 * along the routed path. Dimensions are stored in millimetres.
 */
function CableTrayRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#b0b0b0', isSelected, viewMode, viewType);
    if (!geom?.start || !geom?.end) return null;

    const start = new THREE.Vector3(...geom.start);
    const end = new THREE.Vector3(...geom.end);
    const length = start.distanceTo(end);
    if (length < 0.001) return null;

    const width = Math.max((geom.width ?? 300) / 1000, 0.05);
    const railHeight = Math.max((geom.height ?? 100) / 1000, 0.02);
    const thickness = Math.max((geom.thickness ?? 2) / 1000, 0.003);

    const mid = start.clone().add(end).multiplyScalar(0.5);
    const direction = end.clone().sub(start).normalize();
    const angle = Math.atan2(direction.y, direction.x);

    const trayType: string = geom.trayType ?? 'Solid Bottom';
    const showRungs = trayType === 'Ladder' || trayType === 'Ventilated';
    const rungCount = Math.max(Math.floor(length / 0.3), 1);

    return (
        <group position={mid} rotation={[0, 0, angle]} userData={{ id: element.id }}>
            {/* Side rails */}
            <mesh position={[0, width / 2, railHeight / 2]}>
                <boxGeometry args={[length, thickness, railHeight]} />
                {material}
                {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
            <mesh position={[0, -width / 2, railHeight / 2]}>
                <boxGeometry args={[length, thickness, railHeight]} />
                {material}
                {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
            {/* Bottom: solid plate or rungs */}
            {showRungs
                ? Array.from({ length: rungCount }, (_, i) => {
                    const step = length / rungCount;
                    const x = -length / 2 + step * (i + 0.5);
                    return (
                        <mesh key={i} position={[x, 0, thickness / 2]}>
                            <boxGeometry args={[thickness * 2, width, thickness]} />
                            {material}
                        </mesh>
                    );
                })
                : (
                    <mesh position={[0, 0, thickness / 2]}>
                        <boxGeometry args={[length, width, thickness]} />
                        {material}
                        {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                            <InkEdges isSelected={isSelected} />
                        )}
                    </mesh>
                )}
        </group>
    );
}

/** Conduit: round tube along the routed path. Diameter stored in mm. */
function ConduitRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#7a7a7a', isSelected, viewMode, viewType);
    if (!geom?.start || !geom?.end) return null;

    const start = new THREE.Vector3(...geom.start);
    const end = new THREE.Vector3(...geom.end);
    const length = start.distanceTo(end);
    if (length < 0.001) return null;

    const dM = Math.max((geom.outerDiameter ?? 21) / 1000, 0.01);
    const mid = start.clone().add(end).multiplyScalar(0.5);
    const direction = end.clone().sub(start).normalize();
    const angle = Math.atan2(direction.y, direction.x);
    const pitch = Math.asin(THREE.MathUtils.clamp(direction.z, -1, 1));

    return (
        <group position={mid} rotation={[0, 0, angle]} userData={{ id: element.id }}>
            <group rotation={[0, -pitch, 0]}>
                <mesh>
                    <cylinderGeometry args={[dM / 2, dM / 2, length, 12]} />
                    {material}
                    {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                        <InkEdges isSelected={isSelected} />
                    )}
                </mesh>
            </group>
        </group>
    );
}

/**
 * MEP equipment: mechanical (VAV/AHU) stores width/depth/height in mm and a
 * center Z; electrical panels store width/height/depth in mm with a placement
 * point at the bottom. Falls back to the generic component box dimensions.
 */
function EquipmentRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#9aa5b1', isSelected, viewMode, viewType);
    if (!geom?.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const rotation = THREE.MathUtils.degToRad(geom.rotation ?? 0);

    // Mechanical equipment: mm dims, position is center Z.
    // Electrical equipment: mm dims, position is base.
    const isMechanical = element.category === 'Mechanical Equipment';
    const width = Math.max((geom.width ?? 500) / 1000, 0.05);
    const depth = Math.max((geom.depth ?? 400) / 1000, 0.05);
    const height = Math.max((geom.height ?? 300) / 1000, 0.05);
    const baseZ = isMechanical ? pos.z - height / 2 : pos.z;

    return (
        <group position={[pos.x, pos.y, baseZ]} rotation={[0, 0, rotation]} userData={{ id: element.id }}>
            <mesh position={[0, 0, height / 2]}>
                <boxGeometry args={[width, depth, height]} />
                {material}
                {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
        </group>
    );
}

/**
 * 3D text renderer (Model Text / Text Notes).
 *
 * BIM split: `Model Text` is model geometry, so its stored size is real
 * world size and never changes with the view scale. `Text Notes` are
 * annotations, so the stored size is a paper size in meters and its world
 * size is paper * viewScale — the same note prints 2.5 mm at 1:20 and 1:100.
 */
function TextRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    const ink = useAnnotationInk();
    const viewScale = useActiveViewScale();
    if (!geom || !geom.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const value = geom.text || element.parameters['Text'] || '';
    if (!value) return null;

    const isModelText = !element.category.includes('Text Notes');
    const rawSize = typeof geom.size === 'number'
        ? geom.size
        : (isModelText ? 0.5 : 0.0025);
    // Model text may store mm (> 1); normalize to meters.
    const modelSize = rawSize > 1 ? rawSize / 1000 : rawSize;
    // Annotation: paper meters -> world meters at the active view scale.
    const paperSize = rawSize >1 ? rawSize / 1000 : rawSize; const fontSize = isModelText ? modelSize : paperSize * viewScale; const color = isSelected ? '#00aaff' : ink; 
// Contextual Text tools: alignment (left/center/right) and leader arrow.
        const alignment = (element.parameters['Text Alignment'] ?? geom.alignment) as | 'Left' | 'Center' | 'Right' | undefined; const anchorX = alignment === 'Left' ? 'left' : alignment === 'Right' ? 'right' : 'center'; const leader = (element.parameters['Leader'] ?? geom.leader) as | 'Left' | 'Right' | null | undefined; const leaderLen = fontSize * 6; const leaderOffset = fontSize * 0.4; return (
<group position={pos} userData={{ id: element.id }}>
            {leader === 'Left' && (
                <Line
                    points={[
                        [-leaderLen, -leaderOffset, 0],
                        [-leaderLen * 0.2, -leaderOffset, 0],
                        [-leaderLen, -leaderOffset - fontSize * 0.35, 0],
                    ]}
                    color={color}
                    lineWidth={1}
                />
            )}
            {leader === 'Right' && (
                <Line
                    points={[
                        [leaderLen, -leaderOffset, 0],
                        [leaderLen * 0.2, -leaderOffset, 0],
                        [leaderLen, -leaderOffset - fontSize * 0.35, 0],
                    ]}
                    color={color}
                    lineWidth={1}
                />
            )}
            <Text
                fontSize={fontSize}
                color={color}
                anchorX={anchorX as any}
                anchorY="bottom"
                outlineWidth={fontSize * 0.03}
                outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}
            >
                {value}
            </Text>
        </group>
    );
}

/**
 * Model line renderer. Draws a straight 3D line between the stored
 * start/end points; thin lines get a small screen-space width so they stay
 * visible when zoomed out.
 */
function ModelLineRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    const ink = useAnnotationInk();
    if (!geom || !geom.start || !geom.end) return null;

    return (
        <group userData={{ id: element.id }}>
            <Line
                points={[geom.start, geom.end]}
                color={isSelected ? '#00aaff' : ink}
                lineWidth={isSelected ? 3 : 1.5}
            />
        </group>
    );
}

// ... (Existing renderers)

// ==================== Annotation (Annotate tab) geometry helpers ====================

/** Closed circle polyline sampled for <Line>. */
function circlePoints(cx: number, cy: number, r: number, z: number, segments = 48): THREE.Vector3[] {
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i <= segments; i++) {
        const a = (i / segments) * Math.PI * 2;
        pts.push(new THREE.Vector3(cx + Math.cos(a) * r, cy + Math.sin(a) * r, z));
    }
    return pts;
}

/** Arc polyline between two angles (CCW from a0 by sweep radians). */
function arcPoints(cx: number, cy: number, r: number, z: number, a0: number, sweep: number, segments = 24): THREE.Vector3[] {
    const pts: THREE.Vector3[] = [];
    const n = Math.max(segments, 4);
    for (let i = 0; i <= n; i++) {
        const a = a0 + sweep * (i / n);
        pts.push(new THREE.Vector3(cx + Math.cos(a) * r, cy + Math.sin(a) * r, z));
    }
    return pts;
}

/** Open arrowhead (V) ending at `tip`, coming from `from`, in the XY plane. */
function arrowPoints(tip: THREE.Vector3, from: THREE.Vector3, size: number): THREE.Vector3[] {
    const dir = tip.clone().sub(from);
    if (dir.lengthSq() < 1e-12) dir.set(1, 0, 0);
    dir.normalize();
    const perp = new THREE.Vector3(-dir.y, dir.x, 0);
    const back = tip.clone().sub(dir.clone().multiplyScalar(size));
    const wing = size * 0.35;
    return [
        back.clone().add(perp.clone().multiplyScalar(wing)),
        tip,
        back.clone().sub(perp.clone().multiplyScalar(wing)),
    ];
}

/** Perpendicular tick line at point p across direction dir. */
function tickLine(p: THREE.Vector3, dir: THREE.Vector3, half: number): THREE.Vector3[] {
    const perp = new THREE.Vector3(-dir.y, dir.x, 0).normalize().multiplyScalar(half);
    return [p.clone().sub(perp), p.clone().add(perp)];
}

/** Chord-clipped hatch lines across a polygon (BIM-style region fill). */
function hatchSegments(
    boundary: [number, number][], angleDeg: number, spacing: number
): [THREE.Vector3, THREE.Vector3][] {
    const segs: [THREE.Vector3, THREE.Vector3][] = [];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of boundary) {
        minX = Math.min(minX, x); minY = Math.min(minY, y);
        maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
    }
    const diag = Math.hypot(maxX - minX, maxY - minY);
    const a = THREE.MathUtils.degToRad(angleDeg);
    const ux = Math.cos(a), uy = Math.sin(a);
    // Perpendicular scan axis
    const px = -uy, py = ux;
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    const half = diag / 2 + spacing;

    const inside = (x: number, y: number) => {
        let count = 0;
        for (let i = 0; i < boundary.length; i++) {
            const [x1, y1] = boundary[i];
            const [x2, y2] = boundary[(i + 1) % boundary.length];
            if ((y1 > y) !== (y2 > y)) {
                const xInt = x1 + ((y - y1) / (y2 - y1)) * (x2 - x1);
                if (xInt > x) count++;
            }
        }
        return count % 2 === 1;
    };

    for (let d = -half; d <= half; d += spacing) {
        const ox = cx + px * d, oy = cy + py * d;
        const a0 = { x: ox - ux * half, y: oy - uy * half };
        const a1 = { x: ox + ux * half, y: oy + uy * half };
        // March along the line and gather inside runs.
        const steps = 480;
        let runStart: THREE.Vector3 | null = null;
        for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            const x = a0.x + (a1.x - a0.x) * t;
            const y = a0.y + (a1.y - a0.y) * t;
            const isIn = inside(x, y);
            const pt = new THREE.Vector3(x, y, 0);
            if (isIn && !runStart) runStart = pt;
            if (!isIn && runStart) {
                segs.push([runStart, pt]);
                runStart = null;
            }
        }
        if (runStart) {
            segs.push([runStart, new THREE.Vector3(a1.x, a1.y, 0)]);
        }
    }
    return segs;
}

/**
 * Dimension renderer (annotation). Supports every Annotate > Dimension kind
 * produced by DimensionTool / SpotDimensionTool plus the legacy two-point
 * geometry. The measured endpoints are model points and stay fixed; ticks,
 * arrows and text are paper-size annotations, so they grow and shrink with
 * the view scale (BIM behavior).
 */
function DimensionRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    const s = useAnnotationScaleFactor();
    const ink = useAnnotationInk();
    if (!geom) return null;

    const color = isSelected ? '#00aaff' : ink;
    // Spot dimensions share the Dimensions category: route to the spot draw.
    if (geom.kind === 'elevation' || geom.kind === 'coordinate' || geom.kind === 'slope') {
        return <SpotAnnotationRenderer element={element} isSelected={isSelected} />;
    }

    const equality = element.parameters['Equality'] === true;
    const value = equality ? 'EQ' : String(element.parameters['Value'] ?? '');
    const z = 0.015;
    const toV = (t: number[] | undefined): THREE.Vector3 | null =>
        Array.isArray(t) ? new THREE.Vector3(t[0], t[1], (t[2] ?? 0) + z) : null;

    // ---- legacy / aligned / linear -------------------------------------
    if (geom.start && geom.end && !geom.refs) {
        const start = new THREE.Vector3(...geom.start);
        const end = new THREE.Vector3(...geom.end);
        const dir = end.clone().sub(start).normalize();
        const tickHalf = 0.06 * s;
        // Witness lines (context tab "Edit Witness Lines"): thin dashed ties
        // from the dimension endpoints to each referenced element center.
        const project = useBIMStore.getState().project;
        const witnessRefs: string[] = Array.isArray(geom.witnessRefs) ? geom.witnessRefs : [];
        const witnessAnchors = witnessRefs
            .map((id) => {
                const el = project.elements.get(id);
                if (!el) return null;
                const g = (el as any).geometryData;
                if (Array.isArray(g?.start) && Array.isArray(g?.end)) {
                    return new THREE.Vector3(
                        (g.start[0] + g.end[0]) / 2,
                        (g.start[1] + g.end[1]) / 2,
                        g.start[2] ?? 0
                    );
                }
                if (Array.isArray(g?.position)) return new THREE.Vector3(...g.position);
                return null;
            })
            .filter((v): v is THREE.Vector3 =>v !== null); return (<group userData={{ id: element.id }}>
                <Line points={[start, end]} color={color} lineWidth={1.2} />
                <Line points={tickLine(start, dir, tickHalf)} color={color} lineWidth={1} />
                <Line points={tickLine(end, dir, tickHalf)} color={color} lineWidth={1} />
                {witnessAnchors.map((anchor, i) => (
                    <Line
                        key={i}
                        points={i % 2 === 0 ? [start, anchor] : [end, anchor]}
                        color={color}
                        lineWidth={1}
                        dashed
                        dashSize={0.15 * s}
                        gapSize={0.1 * s}
                    />
                ))}
                <Text
                    position={[start.x + (end.x - start.x) / 2, start.y + (end.y - start.y) / 2, start.z + 0.05]}
                    fontSize={0.2 * s}
                    color={color}
                    anchorX="center"
                    anchorY="bottom"
                    outlineWidth={0.015 * s}
                    outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}
                >
                    {value || element.parameters['Value'] || start.distanceTo(end).toFixed(3) + ' m'}
                </Text>
            </group>
        );
    }

    if (geom.kind === 'aligned' || geom.kind === 'linear') {
        const a = toV(geom.refs?.[0]);
        const b = toV(geom.refs?.[1]);
        const ds = toV(geom.dimStart);
        const de = toV(geom.dimEnd);
        if (!a || !b || !ds || !de) return null;
        const dir = de.clone().sub(ds).normalize();
        const tickHalf = 0.06 * s;
        // Optional witness ties added by the Edit Witness Lines tool.
        const witnessRefs2: string[] = Array.isArray(geom.witnessRefs) ? geom.witnessRefs : [];
        const project2 = useBIMStore.getState().project;
        const witnessAnchors2 = witnessRefs2
            .map((id) => {
                const el = project2.elements.get(id);
                if (!el) return null;
                const g = (el as any).geometryData;
                if (Array.isArray(g?.position)) return new THREE.Vector3(...g.position);
                if (Array.isArray(g?.start) && Array.isArray(g?.end)) {
                    return new THREE.Vector3(
                        (g.start[0] + g.end[0]) / 2,
                        (g.start[1] + g.end[1]) / 2,
                        g.start[2] ?? 0,
                    );
                }
                return null;
            })
            .filter((v): v is THREE.Vector3 =>v !== null); return (<group userData={{ id: element.id }}>
                {/* Witness lines: reference points onto the dim line */}
                <Line points={[a, ds]} color={color} lineWidth={0.8} />
                <Line points={[b, de]} color={color} lineWidth={0.8} />
                {witnessAnchors2.map((anchor, i) => (
                    <Line
                        key={`w${i}`}
                        points={i % 2 === 0 ? [a, anchor] : [b, anchor]}
                        color={color}
                        lineWidth={0.8}
                        dashed
                        dashSize={0.15 * s}
                        gapSize={0.1 * s}
                    />
                ))}
                {/* Dimension line + ticks */}
                <Line points={[ds, de]} color={color} lineWidth={1.2} />
                <Line points={tickLine(ds, dir, tickHalf)} color={color} lineWidth={1} />
                <Line points={tickLine(de, dir, tickHalf)} color={color} lineWidth={1} />
                {(() => {
                    // Options Bar text placement (Above/Below/Center): the
                    // label offsets along the in-plane perpendicular of the
                    // dimension line; Center sits on the line itself.
                    const perp = new THREE.Vector3(-dir.y, dir.x, 0);
                    const offset = geom.textPosition === 'Above' ? perp
                        : geom.textPosition === 'Below' ? perp.negate()
                            : new THREE.Vector3();
                    const anchor = geom.textPosition === 'Below' ? 'top'
                        : geom.textPosition === 'Center' ? 'middle' : 'bottom';
                    const mid = new THREE.Vector3((ds.x + de.x) / 2, (ds.y + de.y) / 2, ds.z);
                    mid.addScaledVector(perp.clone().normalize(), offset.lengthSq() ? 0.12 * s : 0);
                    return (
                        <Text
                            position={[mid.x, mid.y, mid.z + 0.02]}
                            fontSize={0.2 * s}
                            color={color}
                            anchorX="center"
                            anchorY={anchor}
                            outlineWidth={0.015 * s}
                            outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}
                        >
                            {value}
                        </Text>
                    );
                })()}
            </group>
        );
    }

    if (geom.kind === 'angular') {
        const l1 = (geom.line1 ?? []).map(toV).filter(Boolean) as THREE.Vector3[];
        const l2 = (geom.line2 ?? []).map(toV).filter(Boolean) as THREE.Vector3[];
        const textPos = toV(geom.textPos);
        if (l1.length < 2 || l2.length < 2 || !textPos) return null;
        // 2D line intersection of the two referenced lines.
        const [p1, p2] = l1, [p3, p4] = l2;
        const d1x = p2.x - p1.x, d1y = p2.y - p1.y;
        const d2x = p4.x - p3.x, d2y = p4.y - p3.y;
        const den = d1x * d2y - d1y * d2x;
        let cx: number, cy: number;
        if (Math.abs(den) < 1e-9) {
            cx = (p1.x + p2.x + p3.x + p4.x) / 4;
            cy = (p1.y + p2.y + p3.y + p4.y) / 4;
        } else {
            const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / den;
            cx = p1.x + t * d1x;
            cy = p1.y + t * d1y;
        }
        const center = new THREE.Vector3(cx, cy, z);
        const a0 = Math.atan2(p2.y - cy, p2.x - cx);
        const a1 = Math.atan2(p4.y - cy, p4.x - cx);
        const aText = Math.atan2(textPos.y - cy, textPos.x - cx);
        // Choose the CCW sweep (a0→a1) that contains the text direction.
        let sweep = a1 - a0;
        while (sweep < 0) sweep += Math.PI * 2;
        const mid = a0 + sweep / 2;
        const norm = (x: number) => Math.atan2(Math.sin(x), Math.cos(x));
        if (Math.abs(norm(aText - mid)) > Math.PI / 2) {
            // The other side of the crossing: draw the complementary arc
            // (from a1 CCW to a0) so the sweep contains the text direction.
            sweep = (a0 - a1) < 0 ? (a0 - a1) + Math.PI * 2 : a0 - a1;
            return (
                <group userData={{ id: element.id }}>
                    <Line points={arcPoints(cx, cy, Math.max(center.distanceTo(textPos), 0.2 * s), z, a1, sweep)} color={color} lineWidth={1.2} />
                    <Text position={[textPos.x, textPos.y, z + 0.02]} fontSize={0.2 * s} color={color} anchorX="center" anchorY="bottom" outlineWidth={0.015 * s} outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}>{value}</Text>
                </group>
            );
        }
        const r = Math.max(center.distanceTo(textPos), 0.2 * s);
        return (
            <group userData={{ id: element.id }}>
                {/* Witness extensions from vertex through the refs */}
                <Line points={[center, p2.clone().lerp(center, -0.2)]} color={color} lineWidth={0.8} />
                <Line points={[center, p4.clone().lerp(center, -0.2)]} color={color} lineWidth={0.8} />
                <Line points={arcPoints(cx, cy, r, z, a0, sweep)} color={color} lineWidth={1.2} />
                <Text position={[textPos.x, textPos.y, z + 0.02]} fontSize={0.2 * s} color={color} anchorX="center" anchorY="bottom" outlineWidth={0.015 * s} outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}>{value}</Text>
            </group>
        );
    }

    if (geom.kind === 'radial' || geom.kind === 'diameter') {
        const c = toV(geom.center);
        const rim = toV(geom.rim);
        const textPos = toV(geom.textPos);
        if (!c || !rim || !textPos) return null;
        const r = Math.max(c.distanceTo(rim), 0.001);
        const dirToText = textPos.clone().sub(c);
        if (dirToText.lengthSq() < 1e-9) dirToText.set(1, 0, 0);
        dirToText.normalize();
        const onCircle = c.clone().add(dirToText.clone().multiplyScalar(r));
        const arrowSize = 0.12 * s;
        if (geom.kind === 'radial') {
            return (
                <group userData={{ id: element.id }}>
                    <Line points={circlePoints(c.x, c.y, r, z)} color={color} lineWidth={0.8} />
                    {/* Center crosshair */}
                    <Line points={[[c.x - 0.05 * s, c.y, z], [c.x + 0.05 * s, c.y, z]]} color={color} lineWidth={0.8} />
                    <Line points={[[c.x, c.y - 0.05 * s, z], [c.x, c.y + 0.05 * s, z]]} color={color} lineWidth={0.8} />
                    {/* Leader center → past the rim to the text */}
                    <Line points={[c, textPos]} color={color} lineWidth={1.2} />
                    <Line points={arrowPoints(onCircle, c, arrowSize)} color={color} lineWidth={1.2} />
                    <Text position={[textPos.x, textPos.y + 0.04, z + 0.02]} fontSize={0.2 * s} color={color} anchorX="center" anchorY="bottom" outlineWidth={0.015 * s} outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}>{value}</Text>
                </group>
            );
        }
        const through = c.clone().sub(dirToText.clone().multiplyScalar(r));
        return (
            <group userData={{ id: element.id }}>
                <Line points={circlePoints(c.x, c.y, r, z)} color={color} lineWidth={0.8} />
                <Line points={[through, textPos]} color={color} lineWidth={1.2} />
                <Line points={arrowPoints(onCircle, through, arrowSize)} color={color} lineWidth={1.2} />
                <Line points={arrowPoints(through, onCircle, arrowSize)} color={color} lineWidth={1.2} />
                <Text position={[textPos.x, textPos.y + 0.04, z + 0.02]} fontSize={0.2 * s} color={color} anchorX="center" anchorY="bottom" outlineWidth={0.015 * s} outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}>{value}</Text>
            </group>
        );
    }

    if (geom.kind === 'arclength') {
        const c = toV(geom.center);
        const a0p = toV(geom.arcStart);
        const a1p = toV(geom.arcEnd);
        const textPos = toV(geom.textPos);
        if (!c || !a0p || !a1p || !textPos) return null;
        const r = Math.max(c.distanceTo(a0p), 0.001);
        const startA = Math.atan2(a0p.y - c.y, a0p.x - c.x);
        let endA = Math.atan2(a1p.y - c.y, a1p.x - c.x);
        let sweep = endA - startA;
        if (sweep < 0) sweep += Math.PI * 2;
        const dimR = r + 0.15 * s;
        const dirOut = (ang: number) =>new THREE.Vector3(c.x + Math.cos(ang) * (r + 0.02 * s), c.y + Math.sin(ang) * (r + 0.02 * s), z); const t0 = dirOut(startA), t1 = dirOut(startA + sweep); const e0 = new THREE.Vector3(c.x + Math.cos(startA) * dimR, c.y + Math.sin(startA) * dimR, z); const e1 = new THREE.Vector3(c.x + Math.cos(startA + sweep) * dimR, c.y + Math.sin(startA + sweep) * dimR, z); const tickDir = e1.clone().sub(e0).normalize(); return (<group userData={{ id: element.id }}>
                {/* Referenced arc (faint) + dim arc */}
                <Line points={arcPoints(c.x, c.y, r, z, startA, sweep)} color={color} lineWidth={0.8} />
                <Line points={arcPoints(c.x, c.y, dimR, z, startA, sweep)} color={color} lineWidth={1.2} />
                {/* Radial witness ticks */}
                <Line points={[t0, e0]} color={color} lineWidth={0.8} />
                <Line points={[t1, e1]} color={color} lineWidth={0.8} />
                <Line points={tickLine(e0, tickDir, 0.05 * s)} color={color} lineWidth={1} />
                <Line points={tickLine(e1, tickDir, 0.05 * s)} color={color} lineWidth={1} />
                <Text position={[textPos.x, textPos.y + 0.04, z + 0.02]} fontSize={0.2 * s} color={color} anchorX="center" anchorY="bottom" outlineWidth={0.015 * s} outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}>{value}</Text>
            </group>
        );
    }

    return null;
}

/**
 * Spot annotation renderer (Annotate > Dimension panel spot tools):
 * elevation triangle + horizontal text flag, coordinate crosshair + N/E
 * readout, slope triangle + % flag. Paper-sized: scales with the view.
 */
function SpotAnnotationRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    const s = useAnnotationScaleFactor();
    const ink = useAnnotationInk();
    if (!geom?.position) return null;

    const color = isSelected ? '#00aaff' : ink;
    const z = 0.02;
    const pos = new THREE.Vector3(geom.position[0], geom.position[1], (geom.position[2] ?? 0) + z);
    // Legacy spots stored only an anchor point: keep the flag above the target.
    const flagSrc = Array.isArray(geom.flagPos) ? geom.flagPos : [pos.x, pos.y + 0.3 * s, pos.z];
    const flag = new THREE.Vector3(flagSrc[0], flagSrc[1], (flagSrc[2] ?? 0) + z);
    const value = String(element.parameters['Value'] ?? '');
    const tri = 0.12 * s;

    // Horizontal flag line under the text (BIM spot head style).
    const flagLen = Math.max(value.length * 0.12 * s, 0.8 * s);

    return (
        <group userData={{ id: element.id }}>
            {/* Leader from target to flag */}
            <Line points={[pos, flag]} color={color} lineWidth={1} />
            {geom.kind === 'elevation' && (
                <Line
                    points={[
                        [pos.x - tri, pos.y, pos.z],
                        [pos.x + tri, pos.y, pos.z],
                        [pos.x, pos.y + tri, pos.z],
                        [pos.x - tri, pos.y, pos.z],
                    ]}
                    color={color}
                    lineWidth={1.2}
                />
            )}
            {geom.kind === 'coordinate' && (
                <>
                    <Line points={circlePoints(pos.x, pos.y, tri * 0.8, pos.z)} color={color} lineWidth={1} />
                    <Line points={[[pos.x - tri, pos.y, pos.z], [pos.x + tri, pos.y, pos.z]]} color={color} lineWidth={1} />
                    <Line points={[[pos.x, pos.y - tri, pos.z], [pos.x, pos.y + tri, pos.z]]} color={color} lineWidth={1} />
                </>
            )}
            {geom.kind === 'slope' && (
                <Line
                    points={[
                        [pos.x, pos.y, pos.z],
                        [pos.x + tri * 1.6, pos.y, pos.z],
                        [pos.x + tri * 1.6, pos.y + tri, pos.z],
                        [pos.x, pos.y, pos.z],
                    ]}
                    color={color}
                    lineWidth={1.2}
                />
            )}
            {/* Text flag */}
            <Line points={[[flag.x, flag.y, flag.z], [flag.x + flagLen, flag.y, flag.z]]} color={color} lineWidth={1.2} />
            <Text
                position={[flag.x + flagLen / 2, flag.y + 0.04 * s, flag.z + 0.01]}
                fontSize={0.2 * s}
                color={color}
                anchorX="center"
                anchorY="bottom"
                outlineWidth={0.015 * s}
                outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}
            >
                {value}
            </Text>
        </group>
    );
}

/**
 * Tag renderer (Annotate > Tag panel). Draws the tag value in a BIM-style
 * card; multi-line values (room number + name) stack. When the target
 * element is resolvable, a thin leader ties the card to the target point.
 * Tags are annotations: constant paper size, world size follows view scale.
 */
function TagRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    const s = useAnnotationScaleFactor();
    const ink = useAnnotationInk();
    const target = useBIMStore((state) =>
        geom?.targetId ? state.project.elements.get(geom.targetId) : undefined);
    if (!geom || !geom.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const rawValue = String(element.parameters['Value'] ?? '?');
    const lines = rawValue.split('\n');

    const color = isSelected ? '#00aaff' : ink;
    const leaderColor = isSelected ? '#00aaff' : (ink === '#ffffff' ? '#cccccc' : '#444444');
    const lineH = 0.25 * s;
    const boxW = Math.max(...lines.map((l) => l.length)) * 0.14 * s + 0.2 * s;
    const boxH = lines.length * lineH + 0.1 * s;

    // Leader tip: the target element center when resolvable.
    let tip: THREE.Vector3 | null = null;
    const tg = (target as any)?.geometryData;
    if (tg) {
        if (Array.isArray(tg.position)) tip = new THREE.Vector3(...tg.position);
        else if (Array.isArray(tg.start) && Array.isArray(tg.end)) {
            tip = new THREE.Vector3(
                (tg.start[0] + tg.end[0]) / 2,
                (tg.start[1] + tg.end[1]) / 2,
                (tg.start[2] ?? 0) + 0.2,
            );
        } else if (Array.isArray(tg.path) && tg.path.length) {
            tip = new THREE.Vector3(...tg.path[Math.floor(tg.path.length / 2)]);
        }
    }

    return (
        <group position={pos} userData={{ id: element.id }}>
            {tip && (
                <Line
                    points={[
                        [0, 0, 0],
                        [tip.x - pos.x, tip.y - pos.y, (tip.z ?? 0) - pos.z],
                    ]}
                    color={leaderColor}
                    lineWidth={0.8}
                    dashed
                    dashSize={0.08 * s}
                    gapSize={0.06 * s}
                />
            )}
            {tip && (
                <mesh position={[tip.x - pos.x, tip.y - pos.y, (tip.z ?? 0) - pos.z]}>
                    <sphereGeometry args={[0.03 * s, 8, 8]} />
                    <meshBasicMaterial color={leaderColor} />
                </mesh>
            )}
            {/* Tag card border */}
            <Line
                points={[
                    [-boxW / 2, -boxH / 2, 0.001],
                    [boxW / 2, -boxH / 2, 0.001],
                    [boxW / 2, boxH / 2, 0.001],
                    [-boxW / 2, boxH / 2, 0.001],
                    [-boxW / 2, -boxH / 2, 0.001],
                ]}
                color={color}
                lineWidth={1.2}
            />
            {lines.map((line, i) => (
                <Text
                    key={i}
                    position={[0, boxH / 2 - lineH * (i + 0.65), 0.002]}
                    fontSize={lineH * 0.85}
                    color={color}
                    anchorX="center"
                    anchorY="middle"
                    outlineWidth={0.015 * s}
                    outlineColor={ink === '#ffffff' ? '#000000' : '#ffffff'}
                >
                    {line}
                </Text>
            ))}
        </group>
    );
}

import { Edges } from '@react-three/drei';
import { CeilingGridMaterial } from '../rendering/Materials';

function CeilingRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geometryData = (element as any).geometryData;
    // Default material fallback
    const material = useViewMaterial('#ffffff', isSelected, viewMode, viewType);

    if (!geometryData || !geometryData.boundary) return null;

    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';
    const isShaded = viewMode === 'shaded';

    // Shape
    const shape = new THREE.Shape();
    const p = geometryData.boundary;
    shape.moveTo(p[0][0], p[0][1]);
    for (let i = 1; i < p.length; i++) {
        shape.lineTo(p[i][0], p[i][1]);
    }
    shape.closePath();

    const minZ = geometryData.heightOffsetFromLevel || 2.6;
    const thickness = geometryData.thickness || 0.05;

    // Extrude DOWN? Ceilings usually have a set height (bottom face).
    // So if minZ is the bottom, we extrude UP? 
    // BIM "Height Offset from Level" is usually the height of the bottom face.
    // So the volume is [minZ, minZ + thickness].

    const geo = new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false });

    // Check if Grid Type
    const isGrid = element.type.includes('Grid');

    return (
        <group userData={{ id: element.id }}>
            <mesh geometry={geo} position={[0, 0, minZ]}>
                {/* Top/Side faces use standard material */}
                {material}

                {/* Bottom Face (Index 1 usually, or we can use material arrays) */}
                {/* For ExtrudeGeometry, material 0 is sides, material 1 is top/bottom caps? No. */}
                {/* We can just render a separate plane for the grid at the bottom if needed, or use a sophisticated shader. */}
                {/* For simplicity, let's just create a bottom plane for the grid visualization to avoid UV mapping issues on ExtrudeGeometry cap. */}
            </mesh>

            {/* Grid Visualization on Bottom Face */}
            {isGrid && !isWireframe && (
                <mesh position={[0, 0, minZ - 0.001]}>
                    <shapeGeometry args={[shape]} />
                    <CeilingGridMaterial color={isSelected ? '#00aaff' : 'gray'} backgroundColor="white" spacing={0.6} />
                </mesh>
            )}

            {(isPlan || isHiddenLine || isWireframe || isConsistentColors || isShaded) && (
                <InkEdges geometry={geo} position={[0, 0, minZ]} isSelected={isSelected} />
            )}
        </group>
    );
}

function GenericElementRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const material = useViewMaterial('orange', isSelected, viewMode, viewType);
    return (
        <group position={[0, 0, 0.5]} userData={{ id: element.id }}>
            <mesh>
                <boxGeometry args={[1, 1, 1]} />
                {material}
            </mesh>
        </group>
    );
}

/**
 * Structural Opening void visualization: a dashed wireframe box (BIM shows
 * hosted openings as their outline once the host is cut). The material
 * removal itself happens in each host renderer via CSG cutters.
 */
function StructuralOpeningWireframe({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = element.geometryData as any;

    // Sketched 'Profile' void: draw the actual sketched outline mapped onto
    // the host wall face (the box bounds below would misrepresent it). The
    // hook runs unconditionally — never after the early return below.
    const hostWall = useBIMStore(useShallow((state: any) => (
        geom.kind === 'Profile' && geom.hostId
            ? state.project.elements.get(geom.hostId) as WallElement | undefined
            : undefined
    )));

    if (!geom || !Array.isArray(geom.center)) return null;

    if (geom.kind === 'Profile' && Array.isArray(geom.profile) && geom.profile.length >= 3 && hostWall) {
        const ctx = {
            start: new THREE.Vector3(...hostWall.geometryData.start),
            end: new THREE.Vector3(...hostWall.geometryData.end),
            baseZ: hostWall.geometryData.baseOffset || 0,
        };
        const loop = geom.profile.map(([u, v]: [number, number]) =>wallLocalToWorld(ctx, u, v)); return (<group userData={{ id: element.id }}>
                <Line points={[...loop, loop[0]]} color={isSelected ? '#00aaff' : '#ff6b6b'} lineWidth={2} />
            </group>
        );
    }

    const w = Math.max(geom.width ?? 0.6, 0.05);
    const h = Math.max(geom.height ?? 0.6, 0.05);
    const d = Math.max(geom.depth ?? 0.6, 0.05);
    // Local box axes: kind Wall stores width along-run, height vertical, depth
    // across; every other kind stores width/height in XY and depth along Z.
    const dims: [number, number, number] = geom.kind === 'Wall' ? [w, d, h] : [w, h, d];
    const rotationRad = THREE.MathUtils.degToRad(geom.rotation ?? 0);
    return (
        <group position={geom.center} rotation={[0, 0, rotationRad]} userData={{ id: element.id }}>
            <lineSegments>
                <edgesGeometry args={[new THREE.BoxGeometry(dims[0], dims[1], dims[2])]} />
                <lineBasicMaterial color={isSelected ? '#00aaff' : '#ff6b6b'} transparent opacity={0.7} />
            </lineSegments>
        </group>
    );
}

/** Rebar Coupler: short cylinder along the coupler axis (Structure > Rebar Coupler). */
function RebarCouplerRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = element.geometryData as any;
    if (!geom?.start || !geom?.end) return null;
    const start = new THREE.Vector3(...geom.start);
    const end = new THREE.Vector3(...geom.end);
    const length = start.distanceTo(end);
    if (length < 0.01) return null;
    const material = useViewMaterial('#444444', isSelected, viewMode, viewType);

    const radius = Math.max((geom.barDiameter ?? 16) / 1000 / 2 * 1.6, 0.015);
    const mid = start.clone().add(end).multiplyScalar(0.5);
    const dir = end.clone().sub(start).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir);

    return (
        <group position={mid} quaternion={quat} userData={{ id: element.id }}>
            <mesh>
                <cylinderGeometry args={[radius, radius, length, 12]} />
                {material}
            </mesh>
        </group>
    );
}

/** Reference Plane: translucent green plane with border (Datum panel). */
function ReferencePlaneRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = element.geometryData as any;
    if (!geom?.origin || !geom?.normal || !geom?.direction) return null;
    const origin = new THREE.Vector3(...geom.origin);
    const normal = new THREE.Vector3(...geom.normal).normalize();
    const uDir = new THREE.Vector3(...geom.direction).normalize();
    const ext = Array.isArray(geom.extension) ? geom.extension : [5, 5];
    const halfU = Math.min(Math.max(ext[0] ?? 5, 0.5), 100);
    const halfV = Math.min(Math.max(ext[1] ?? 5, 0.5), 100);

    const vDir = new THREE.Vector3().crossVectors(normal, uDir).normalize();
    // Local frame: X = u (trace direction), Y = v (in-plane perpendicular),
    // Z = plane normal. Build the orientation matrix directly.
    const basis = new THREE.Matrix4().makeBasis(uDir, vDir, normal);
    const quat = new THREE.Quaternion().setFromRotationMatrix(basis);

    const planeGeo = new THREE.PlaneGeometry(halfU * 2, halfV * 2);
    const border = [
        new THREE.Vector3(-halfU, -halfV, 0),
        new THREE.Vector3(halfU, -halfV, 0),
        new THREE.Vector3(halfU, halfV, 0),
        new THREE.Vector3(-halfU, halfV, 0),
        new THREE.Vector3(-halfU, -halfV, 0),
    ];

    return (
        <group position={origin} quaternion={quat} userData={{ id: element.id }}>
            <mesh geometry={planeGeo}>
                <meshBasicMaterial color={isSelected ? '#00aaff' : '#33cc33'} transparent opacity={0.08} side={THREE.DoubleSide} depthWrite={false} />
            </mesh>
            <Line points={border} color={isSelected ? '#00aaff' : '#33cc33'} lineWidth={1} dashed dashScale={8} />
        </group>
    );
}

/**
 * Wall Sweep / Wall Reveal band: a thin box running along the host wall at
 * the band elevation. Sweeps protrude from the face, reveals sit flush and
 * read as a darker groove.
 */
function WallBandRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = element.geometryData as any;
    const material = useViewMaterial(element.category === 'Wall Sweeps' ? '#d9c9a3' : '#9aa3ad', isSelected, viewMode, viewType);
    if (!geom?.start || !geom?.end) return null;

    const start = new THREE.Vector3(...geom.start);
    const end = new THREE.Vector3(...geom.end);
    const length = start.distanceTo(end);
    if (length < 0.05) return null;

    const protrude = Math.abs(geom.protrude ?? 0.05);
    const bandDepth = Math.max(protrude, 0.02);
    const bandHeight = geom.bandHeight ?? 0.15;
    // Lateral position: on the wall face line, nudged outward for sweeps and
    // inward for reveals so the band reads correctly from outside.
    const sign = (geom.protrude ?? 0.05) >= 0 ? 1 : -1; const dir = end.clone().sub(start).normalize(); const lateral = new THREE.Vector3(-dir.y, dir.x, 0).multiplyScalar(sign * (geom.width ?? 0.2) * 0.5); const mid = start.clone().add(end).multiplyScalar(0.5).add(lateral); const angle = Math.atan2(dir.y, dir.x); return (<group position={mid} rotation={[0, 0, angle]} userData={{ id: element.id }}>
            <mesh>
                <boxGeometry args={[length, bandDepth, bandHeight]} />
                {material}
                {(viewType === 'plan' || viewMode === 'hiddenLine') && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
        </group>
    );
}

/**
 * Soffit / Fascia / Gutter / Slab Edge: a closed path band extruded along
 * its polyline. Rendered as segment boxes with kind-tuned cross-sections.
 */
function EdgeBandRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = element.geometryData as any;
    const kind = geom?.kind ?? element.category.replace(/s$/, '');
    const color = kind === 'Gutter' ? '#7c8792' : kind === 'Fascia' ? '#e8c97a' : kind === 'SlabEdge' ? '#d9832e' : '#dfe6ec';
    const material = useViewMaterial(color, isSelected, viewMode, viewType);
    if (!geom?.path || geom.path.length < 2) return null;

    const points = geom.path.map((p: number[]) =>new THREE.Vector3(...p)); const width = geom.width ?? 0.2; const height = geom.height ?? 0.15; const thickness = geom.thickness ?? 0.02; const segments = []; for (let i = 1; i< points.length; i++) {
        const a = points[i - 1];
        const b = points[i];
        const len = a.distanceTo(b);
        if (len < 0.02) continue;
        const dir = b.clone().sub(a).normalize();
        const angle = Math.atan2(dir.y, dir.x);
        const mid = a.clone().add(b).multiplyScalar(0.5);
        // Soffits sit under the eave; everything else centers on the path.
        const zBias = kind === 'Soffit' ? -height / 2 : 0;
        segments.push(
            <group key={i} position={[mid.x, mid.y, mid.z + zBias]} rotation={[0, 0, angle]}>
                <mesh>
                    <boxGeometry args={[len, width, height]} />
                    {material}
                    {(viewType === 'plan' || viewMode === 'hiddenLine') && (
                        <InkEdges isSelected={isSelected} />
                    )}
                </mesh>
            </group>
        );
    }
    return <group userData={{ id: element.id }}>{segments}</group>;
}

/** Room Separator: dashed blue line with endpoint nodes, plus its volume hint. */
function RoomSeparatorRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = element.geometryData as any;
    if (!geom?.start || !geom?.end) return null;
    const a = new THREE.Vector3(...geom.start);
    const b = new THREE.Vector3(...geom.end);
    const color = isSelected ? '#00aaff' : '#2f7bd6';
    return (
        <group userData={{ id: element.id }}>
            <Line points={[a, b]} color={color} lineWidth={2} dashed dashScale={6} />
            {[a, b].map((p, i) => (
                <mesh key={i} position={p}>
                    <sphereGeometry args={[0.08, 12, 12]} />
                    <meshBasicMaterial color={color} />
                </mesh>
            ))}
        </group>
    );
}

/** Area Boundary open line stroke (orange). */
function AreaBoundaryLineRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = element.geometryData as any;
    if (!geom?.path || geom.path.length < 2) return null;
    const points = geom.path.map((p: number[]) =>new THREE.Vector3(p[0], p[1], (p[2] ?? 0) + 0.03)); return (<group userData={{ id: element.id }}>
            <Line points={points} color={isSelected ? '#00aaff' : '#d9832e'} lineWidth={2.5} />
        </group>
    );
}

/** Model Group marker: dashed bounding box around the member centroid. */
function ModelGroupMarkerRenderer({ element, isSelected, viewType }: { element: BIMElement, isSelected: boolean, viewType: string }) {
    const geom = element.geometryData as any;
    if (!geom?.origin) return null;
    const origin = new THREE.Vector3(...geom.origin);
    const memberCount = (geom.memberIds ?? []).length;
    const size = 1 + memberCount * 0.25;
    const color = isSelected ? '#00aaff' : '#2f7bd6';
    const corners = [
        [-1, -1, 0], [1, -1, 0], [1, 1, 0], [-1, 1, 0], [-1, -1, 0],
    ].map(([x, y]) =>origin.clone().add(new THREE.Vector3(x * size / 2, y * size / 2, 0.02))); return (<group userData={{ id: element.id }}>
            <Line points={corners} color={color} lineWidth={1.5} dashed dashScale={5} />
            {viewType === 'plan' && (
                <Text position={origin.clone().add(new THREE.Vector3(0, 0, 0.3))} fontSize={0.3} color={color} anchorX="center" anchorY="bottom" outlineWidth={0.02} outlineColor="#ffffff">
                    {element.name ?? 'Group'}
                </Text>
            )}
        </group>
    );
}

// Helper to create materials based on view mode.
// `bim` optionally carries the document-level material + appearance asset so
// shading colors, hatch patterns and PBR properties flow from the material
// registry (BIM-like) instead of only hardcoded per-function colors.
function useViewMaterial(
    color: string,
    isSelected: boolean,
    viewMode: string,
    viewType: string,
    isPlanHatch: boolean = false,
    bim?: { material?: BIMMaterial; asset?: AppearanceAsset; imageUrls?: Map<string, string> },
) {
    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';
    const isRealistic = viewMode === 'realistic';

    const material = bim?.material;
    const asset = bim?.asset;
    // Material registry wins over the legacy per-function color argument.
    const resolvedColor = material
        ? (material.graphics.useRenderAppearance && asset ? asset.color : material.graphics.shadingColor)
        : color;

    // Wireframe: Transparent with visible edges (edges handled by renderer)
    if (isWireframe) {
        // Use transparent material so we can see through, and rely on <Edges>component. return<meshBasicMaterial color={resolvedColor} transparent opacity={0} depthWrite={false} />;
    }

    // In Plan View with Hatch enabled (e.g. Walls)
    if (isPlan && isPlanHatch) {
        // For Hidden Line, use the specific PlanViewMaterial (material cut pattern / white)
        if (isHiddenLine) {
            const hatch = material ? hatchParams(material) : undefined;
            return (
                <PlanViewMaterial
                    showHatch={true}
                    spacing={5.0}
                    backgroundColor={hatch ? hatch.background : 'white'}
                    color={hatch ? hatch.foreground : 'black'}
                    hatch={hatch}
                />
            );
        }
        // For Shaded/Consistent/Realistic, we fall through to the standard colored materials below
        // to ensure the user sees the mode change.
    }

    // Hidden Line (Non-plan or Non-hatch elements)
    if (isHiddenLine) {
        return <meshStandardMaterial color="white" roughness={1} metalness={0} polygonOffset polygonOffsetFactor={1} />;
    }

    // Consistent Colors: Flat shading (no lighting effect on color), just the texture/color
    if (isConsistentColors) {
        return <meshBasicMaterial color={isSelected ? '#0078d4' : resolvedColor} />;
    }

    // Shaded: Standard lighting, smooth shading
    if (viewMode === 'shaded') {
        return <meshStandardMaterial color={isSelected ? '#0078d4' : resolvedColor} roughness={0.7} metalness={0.1} />;
    }

    // Realistic: PBR materials, shadows, lighting — driven by the appearance asset.
    if (isRealistic) {
        const mapUrl = asset?.imageId ? bim?.imageUrls?.get(asset.imageId) : undefined;
        const map = mapUrl ? appearanceTexture(mapUrl, asset?.tileSize ?? 1) : undefined;
        return <meshStandardMaterial
            color={isSelected ? '#0078d4' : (asset ? asset.color : resolvedColor)}
            map={map}
            roughness={asset ? asset.roughness : 0.5}
            metalness={asset ? asset.metalness : 0.2}
            transparent={!!asset && asset.transparency > 0}
            opacity={asset ? 1 - asset.transparency : 1}
            envMapIntensity={1.0}
        />;
    }

    // Default fallback
    return <meshStandardMaterial color={isSelected ? '#0078d4' : resolvedColor} />;
}

function WallRenderer({ element, isSelected, viewType, viewMode, activeView, hostedElements, structuralOpenings }: { element: WallElement, isSelected: boolean, viewType: string, viewMode: string, activeView?: View, hostedElements?: BIMElement[], structuralOpenings?: BIMElement[] }) {
    const committedGeometry = (element as any).geometryData;
    // Live shape-handle drag preview: while a handle drag runs on THIS wall,
    // the tiny preview store carries the in-progress geometry so the body
    // follows the cursor frame-by-frame. Only this wall's renderer matches
    // the id — no project-map churn, no kernel/undo traffic per drag frame.
    const dragGeometry = useWallEditPreviewStore((s) => (s.wallId === element.id ? s.geometryData : null));
    const geometryData = dragGeometry ?? committedGeometry;
    const project = useBIMStore((state) => state.project);
    const imageUrls = useImageUrlMap();
    // Hosted openings are provided by the memoized element wrapper (scoped
    // subscription); fall back to an empty list defensively.
    const hosted = hostedElements ?? EMPTY_HOSTED_ELEMENTS;
    const parametricOpenings = structuralOpenings ?? EMPTY_HOSTED_ELEMENTS;

    // Find hosted elements (kept for direct renderers without a wrapper)
    const hostedFallback = useBIMStore(useShallow((state) => {
        if (hostedElements) return EMPTY_HOSTED_ELEMENTS;
        const out: BIMElement[] = [];
        state.project.elements.forEach(e => {
            if ((e.category === 'Doors' || e.category === 'Windows') &&
                (e as any).geometryData?.hostWallId === element.id) {
                out.push(e);
            }
        });
        return out;
    }));
    const openings = hostedElements ? hosted : hostedFallback;

    // Cut plane height of the active plan view (world Z, meters). Walls that
    // span it are truncated there — the BIM plan cut behavior. Returns null
    // outside plan views or when the wall doesn't cross the plane.
    const cutPlaneZ = useMemo(() => {
        if (viewType !== 'plan' || !activeView || !activeView.viewRange) return null;
        const level = activeView.associatedLevelId
            ? project.levels.get(activeView.associatedLevelId)
            : undefined;
        if (!level) return null;

        const baseZ = geometryData.baseOffset || 0;
        const cutZ = level.elevation / 1000 + activeView.viewRange.cutPlane;
        // A sketched elevation profile can rise above the nominal height
        // (gable tops): the effective top drives the cut-span test.
        const profileTop = Array.isArray(geometryData.elevationProfile) && geometryData.elevationProfile.length >= 3
            ? polygonBounds2D(geometryData.elevationProfile).maxV
            : 0;
        const effectiveHeight = Math.max(geometryData.height ?? 3, profileTop);
        // Only cut walls that actually span the cut plane
        if (baseZ >= cutZ || baseZ + effectiveHeight<= cutZ) return null;
        return cutZ;
    }, [viewType, activeView, project.levels, geometryData]);

    // Body geometries come from the persistent wall layer cache: view-mode
    // switches unmount/remount this renderer, and rebuilding every wall's CSG
    // body in one synchronous pass was the visible wait between modes. Cache
    // hits re-attach the existing geometry; the cache owns their lifetime.
    // Live drag previews bypass the cache (per-frame churn, uncached build).
    const layersMeshes = useMemo(() => {
        if (!geometryData) return null;
        if (dragGeometry) {
            return buildWallLayersUncached(geometryData, openings, parametricOpenings, cutPlaneZ, project);
        }
        return getWallLayerMeshes(geometryData, openings, parametricOpenings, cutPlaneZ, project);
    }, [geometryData, openings, parametricOpenings, cutPlaneZ, dragGeometry, project]);

    // Release the previous committed geometry's cache entry when the wall is
    // edited: the old key can never be looked up again, so its GPU buffers
    // are freed immediately. Runs on the committed identity only — drag
    // previews never touch it, so warm-up entries survive handle drags.
    const previousCommittedGeometryRef = useRef(committedGeometry);
    useEffect(() => {
        const previous = previousCommittedGeometryRef.current;
        if (previous !== committedGeometry) {
            previousCommittedGeometryRef.current = committedGeometry;
            releaseWallGeometry(previous);
        }
    }, [committedGeometry]);

    // BVH + dispose lifecycle: heavy per-element CSG geometry gets a bounds
    // tree so the per-pointermove hover raycast drops to O(log n) per wall.
    // Cache-owned layers are pooled across view-mode switches — the cache
    // disposes them on eviction/release/overwrite — so only uncached builds
    // (live drag previews) are freed with this component.
    useEffect(() => {
        layersMeshes?.layers.forEach((layer) => {
            const geo = layer.geometry as THREE.BufferGeometry | undefined;
            if (!geo) return;
            const triCount = (geo.index?.count ?? geo.attributes?.position?.count ?? 0) / 3;
            if (triCount >= BVH_MIN_TRIANGLES) ensureGeometryBVH(geo);
        });
        if (layersMeshes?.cacheOwned) return;
        return () => {
            layersMeshes?.layers.forEach((layer) => {
                const geo = layer.geometry as (THREE.BufferGeometry & { disposeBoundsTree?: () => void }) | undefined;
                geo?.disposeBoundsTree?.();
                geo?.dispose();
            });
        };
    }, [layersMeshes]);

    if (!geometryData || !layersMeshes) return null;

    // We can't call hooks inside the map, so we use a helper component or just standard material logic here
    // But useViewMaterial is a hook (uses useMemo potentially? no, mostly logic).
    // Actually useViewMaterial returns a JSX Element <meshStandardMaterial...>. 
// calling it inside render is fine if it doesn't violate rules of hooks.
    // Wait, the function defined above `useViewMaterial` is NOT a hook (doesn't start with use... wait it does).
    // Copied code: `function useViewMaterial...`
    // It returns JSX. It is a "CUSTOM HOOK" conceptually if it uses other hooks?
    // It doesn't use other hooks except it was defined inside the file.
    // Ah, line 282: `function useViewMaterial...`
    // It renders <meshStandardMaterial>. It's a helper function returning JSX, not a hook.
    // It doesn't use state/effect.
        return (
<group userData={{ id: element.id }}>
            {layersMeshes.layers.map((layer, i) => {
                const layerDef = element.geometryData.layers?.[layer.index];
                const layerMat = resolveMaterial(project, layerDef?.materialId);
                return (
                    <mesh key={i} geometry={layer.geometry} userData={{ id: element.id }}>
                        {/* Re-use logic for material but override color */}
                        {/* We can construct the material manually or use the helper with override */}
                        <WallLayerMaterial
                            color={layer.color}
                            isSelected={isSelected}
                            viewMode={viewMode}
                            viewType={viewType}
                            isCore={layerDef?.function === 'Structure'}
                            material={layerMat}
                            asset={resolveAppearance(project, layerMat)}
                            imageUrls={imageUrls}
                        />
                        {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe') && (
                            <InkEdges isSelected={isSelected} />
                        )}
                    </mesh>
                );
            })}
        </group>
    );
}

// Helper component for materials to avoid Hook rules issues if any
function WallLayerMaterial({ color, isSelected, viewMode, viewType, material, asset, imageUrls }: any) {
    // The material registry drives shading, plan cut hatch and realistic PBR;
    // color is the pre-resolved fallback (legacy per-function palette).
    const mat = useViewMaterial(color, isSelected, viewMode, viewType, true, { material, asset, imageUrls });
    return mat;
}

/** id ->url map of the shared image library, for appearance texture loading. */ function useImageUrlMap(): Map<string, string> {
    const images = useImageLibraryStore((state) => state.images);
    return useMemo(() => {
        const map = new Map<string, string>();
        images.forEach((img) => map.set(img.id, img.url));
        return map;
    }, [images]);
}

/**
 * Object Styles default material for a category (Manage > Object Styles
 * material column). Returns the registry material + asset so non-layered
 * renderers (doors, windows, roofs...) can honor category-wide assignment.
 */
function useCategoryMaterial(category: string) {
    const project = useBIMStore((state) => state.project);
    const materialName = useManageSettingsStore((state) => state.objectStyles[category]?.materialName);
    const imageUrls = useImageUrlMap();
    return useMemo(() => {
        if (!materialName) return undefined;
        const material = resolveMaterial(project, materialName);
        if (!material) return undefined;
        return { material, asset: resolveAppearance(project, material), imageUrls };
    }, [project, materialName, imageUrls]);
}

function DoorRenderer({ element, isSelected, viewType, viewMode, hostWall, activeView }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string, hostWall?: WallElement, activeView?: View }) {
    const geometryData = (element as any).geometryData;
    const categoryBim = useCategoryMaterial(element.category);
    const material = useViewMaterial('#8b4513', isSelected, viewMode, viewType, false, categoryBim);
    if (!geometryData) return null;

    let position = new THREE.Vector3(0, 0, 0);
    let rotation = 0;
    let wallThickness = 0.2;

    if (hostWall) {
        const start = new THREE.Vector3(...hostWall.geometryData.start);
        const end = new THREE.Vector3(...hostWall.geometryData.end);
        position = start.clone().lerp(end, geometryData.position);
        position.z += (hostWall.geometryData.baseOffset || 0);
        wallThickness = hostWall.geometryData.thickness || 0.2;

        const dir = end.clone().sub(start).normalize();
        rotation = Math.atan2(dir.y, dir.x);
    }

    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';
    const width = geometryData.width || 0.915;
    const height = geometryData.height || 2.134;
    const frameWidth = 0.05;

    // 3D Geometry
    const content3D = (
        <group>
            {/* Frame */}
            <mesh position={[0, 0, height / 2]}>
                <boxGeometry args={[width + frameWidth * 2, wallThickness + 0.02, height]} />
                <meshStandardMaterial color="#4a4a4a" />
            </mesh>
            {/* Panel (Closed) */}
            <mesh position={[0, 0, height / 2]}>
                <boxGeometry args={[width, 0.04, height - 0.02]} />
                {material}
            </mesh>
        </group>); 
// 2D Plan Symbol
        const content2D = (
<group>
            {/* Wall Mask (Hides the wall below the door) */}
            <mesh position={[0, 0, 0.01]}>
                <planeGeometry args={[width, wallThickness + 0.02]} />
                <meshBasicMaterial color="white" polygonOffset polygonOffsetFactor={-1} />
            </mesh>

            {/* Door Panel (Open 90 degrees) */}
            <group position={[-width / 2, wallThickness / 2, 0]}>
                {/* Pivot point at hinge */}
                <mesh position={[0, width / 2, 0]} rotation={[0, 0, Math.PI / 2]}>
                    <planeGeometry args={[width, 0.05]} />
                    <meshBasicMaterial color="black" />
                </mesh>
            </group>

            {/* Swing Arc */}
            <DoorSwingArc width={width} />
        </group>
    );

    const flipHand = element.parameters['Flip Hand'] === true;
    const flipFacing = element.parameters['Flip Facing'] === true;

    // Apply scaling for flipping
    // Flip Hand (Left/Right) -> Scale X = -1
    // Flip Facing (In/Out) ->Scale Y = -1
        const scaleX = flipHand ? -1 : 1; const scaleY = flipFacing ? -1 : 1;
        
// Adjust position if flipped?
        // The pivot point is center of width usually in BIM families.
        // Our renderer centers the door at [0,0].
        // If we flip scale, the pivot remains at [0,0].
        // Our DoorRenderer centers the Geometry.
        // Ideally, for a door, the "Origin" is the center of the width and center of thickness.
        // So simple scaling works.
        return (
<group position={[position.x, position.y, position.z]} rotation={[0, 0, rotation]} userData={{ id: element.id }}>
            <group scale={[scaleX, scaleY, 1]}>
                {isPlan ? (
                    // 2D symbol sits on the host wall's base plane (position.z
                    // is the wall base; the plan flattens everything to it)
                    <group position={[0, 0, -geometryData.sillHeight]}>
                        {content2D}
                    </group>
                ) : content3D}
                {(isPlan || isHiddenLine || isWireframe || isConsistentColors || viewMode === 'shaded') && !isPlan && (
                    <InkEdges isSelected={isSelected} />
                )}
            </group>
        </group>
    );
}

/**
 * Shared door-swing arcs, cached by (rounded) width. The old code built a new
 * THREE.BufferGeometry in the render body of EVERY door on every re-render and
 * never disposed it (GPU leak + broken `{...arcGeo}` prop spread). The arc is
 * fully determined by the leaf width, so a small module-level cache removes the
 * per-render allocation entirely.
 */
const doorSwingArcCache = new Map<string, THREE.BufferGeometry>();
const DOOR_SWING_MATERIAL = new THREE.LineBasicMaterial({ color: 'black' });
function getDoorSwingArcGeometry(width: number): THREE.BufferGeometry {
    const key = width.toFixed(3);
    let geo = doorSwingArcCache.get(key);
    if (!geo) {
        // Points live in wall-local space: arc centered at [-width/2, 0.1]
        // (hinge offset from the wall face), 0..90 degrees, radius = width.
        geo = new THREE.BufferGeometry().setFromPoints(
            new THREE.Path().absarc(-width / 2, 0.1, width, 0, Math.PI / 2, false).getPoints(20)
        );
        doorSwingArcCache.set(key, geo);
    }
    return geo;
}

function DoorSwingArc({ width }: { width: number }) {
    // The Line wrapper is per instance (one 3D parent per object), but the
    // geometry and material are the shared cached resources above. R3F never
    // disposes objects mounted through <primitive>, so the cache survives 
// door unmounts. The old code built a new BufferGeometry per render and
    // spread it onto <bufferGeometry>, leaking GPU memory.
    const line = useMemo(
        () => new THREE.Line(getDoorSwingArcGeometry(width), DOOR_SWING_MATERIAL),
        [width]
    );
    return <primitive object={line} />;
}

function WindowRenderer({ element, isSelected, viewType, viewMode, hostWall, activeView }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string, hostWall?: WallElement, activeView?: View }) {
    const geometryData = (element as any).geometryData;
    const categoryBim = useCategoryMaterial(element.category);
    const material = useViewMaterial('#87ceeb', isSelected, viewMode, viewType, false, categoryBim);
    if (!geometryData) return null;

    let position = new THREE.Vector3(0, 0, 0);
    let rotation = 0;
    let wallThickness = 0.2;

    if (hostWall) {
        const start = new THREE.Vector3(...hostWall.geometryData.start);
        const end = new THREE.Vector3(...hostWall.geometryData.end);
        position = start.clone().lerp(end, geometryData.position);
        position.z += (hostWall.geometryData.baseOffset || 0) + (geometryData.sillHeight || 0.9);
        wallThickness = hostWall.geometryData.thickness || 0.2;

        const dir = end.clone().sub(start).normalize();
        rotation = Math.atan2(dir.y, dir.x);
    }

    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';
    const width = geometryData.width || 1.2;
    const height = geometryData.height || 1.5;
    const frameWidth = 0.05;

    // 3D Geometry
    const content3D = (
        <group>
            {/* Frame */}
            <mesh>
                <boxGeometry args={[width, wallThickness + 0.02, height]} />
                <meshStandardMaterial color="#4a4a4a" />
            </mesh>
            {/* Glass */}
            <mesh>
                <boxGeometry args={[width - frameWidth * 2, 0.02, height - frameWidth * 2]} />
                {material}
            </mesh>
        </group>); 
// 2D Plan Symbol
        const content2D = (
<group>
            {/* Wall Mask */}
            <mesh position={[0, 0, 0.01]}>
                <planeGeometry args={[width, wallThickness + 0.02]} />
                <meshBasicMaterial color="white" polygonOffset polygonOffsetFactor={-1} />
            </mesh>

            {/* Frame Lines */}
            <mesh position={[0, wallThickness / 2, 0]}>
                <planeGeometry args={[width, 0.05]} />
                <meshBasicMaterial color="gray" />
            </mesh>
            <mesh position={[0, -wallThickness / 2, 0]}>
                <planeGeometry args={[width, 0.05]} />
                <meshBasicMaterial color="gray" />
            </mesh>

            {/* Glass Line */}
            <mesh position={[0, 0, 0]}>
                <planeGeometry args={[width - 0.1, 0.02]} />
                <meshBasicMaterial color="#87ceeb" />
            </mesh>
        </group>); const flipFacing = element.parameters['Flip Facing'] === true; const scaleY = flipFacing ? -1 : 1; return (<group position={[position.x, position.y, isPlan ? 0 : position.z + height / 2]} rotation={[0, 0, rotation]} userData={{ id: element.id }}>
            <group scale={[1, scaleY, 1]}>
                {isPlan ? (
                    // 2D symbol lies flat on the plan plane; position.z already
                    // contains the wall base + sill offset, which is dropped in
                    // plan (the projection shows the cut-plane view regardless
                    // of the window's actual elevation).
                    content2D
                ) : content3D}

                {(isPlan || isHiddenLine || isWireframe || isConsistentColors || viewMode === 'shaded') && !isPlan && (
                    <InkEdges isSelected={isSelected} />
                )}
            </group>
        </group>
    );
}

function FloorRenderer({ element, isSelected, viewType, viewMode, structuralOpenings }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string, structuralOpenings?: BIMElement[] }) {
    const geometryData = (element as any).geometryData;
    // Fallback material
    // const material = useViewMaterial('#808080', isSelected, viewMode, viewType);

    if (!geometryData || !geometryData.boundary || geometryData.boundary.length < 3) return null;

    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';
    const isShaded = viewMode === 'shaded';
    const project = useBIMStore((state) => state.project);
    const imageUrls = useImageUrlMap();

    // Shape-edited floor (BIM "Edit Sub-Elements" slopes / split lines):
    // route through the slab solver so the modified top surface renders.
    const shapeEditor = geometryData.shapeEditor;
    const hasShapeData = !!(shapeEditor?.isEnabled &&
        ((shapeEditor.points?.length ?? 0) > 0 || (shapeEditor.splitLines?.length ?? 0) > 0));
    const shapedGeometry = useMemo(() => {
        if (!hasShapeData) return null;
        try {
            return SlabGeometrySolver.createFloorMesh(element as any).geometry;
        } catch {
            return null;
        }
    }, [element, hasShapeData]);
    useEffect(() => () => { shapedGeometry?.dispose(); }, [shapedGeometry]);
    if (shapedGeometry) {
        return (
            <group userData={{ id: element.id }}>
                <mesh geometry={shapedGeometry} userData={{ id: element.id }}>
                    <meshStandardMaterial color={isSelected ? '#0078d4' : '#a8a8a8'} roughness={0.8} />
                </mesh>
                {/* Shape-point handles (BIM sub-element blue dots), clickable
                    feedback while the Edit Sub-Elements tool is active. */}
                {(shapeEditor.points ?? []).map((pt: any) => (
                    <mesh key={pt.id} position={[pt.position[0], pt.position[1], (geometryData.heightOffsetFromLevel || 0) + pt.offset]} userData={{ id: element.id }}>
                        <sphereGeometry args={[0.08, 12, 12]} />
                        <meshBasicMaterial color="#00b4ff" />
                    </mesh>
                ))}
            </group>
        );
    }

    // Create Shape from boundary
    const shape = new THREE.Shape();
    const p = geometryData.boundary;
    shape.moveTo(p[0][0], p[0][1]);
    for (let i = 1; i < p.length; i++) {
        shape.lineTo(p[i][0], p[i][1]);
    }
    shape.closePath();

    // Calculate Z positions
    const levelOffset = geometryData.heightOffsetFromLevel || 0;

    // Layers
    const layers = geometryData.layers || [{ function: 'Structure', materialId: 'Generic', thickness: 0.2, wraps: false }];

    const renderedLayers = [];
    let currentZ = levelOffset;

    for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];
        const thickness = layer.thickness;

        // Floor layers usually stack Top to Bottom.
        // So we start at levelOffset, and go down.
        // ExtrudeGeometry creates volume from 0 to depth. 
        // If we want it to go down, we can position at z-thickness and extrude up (depth=thickness).

        const geo = new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false });

        // Parametric Structural Opening voids (vertical/shaft/by-face cuts)
        // pierce the slab: bake the layer transform, then CSG-subtract the
        // world-space cutters.
        geo.translate(0, 0, currentZ - thickness);
        let finalGeo: THREE.BufferGeometry = geo;
        if (structuralOpenings && structuralOpenings.length > 0) {
            const cutters: THREE.Mesh[] = [];
            for (const opening of structuralOpenings) {
                const cutter = buildOpeningCutter(opening.geometryData as any);
                if (cutter) cutters.push(cutter);
            }
            if (cutters.length > 0) {
                const target = new THREE.Mesh(geo);
                target.updateMatrix();
                const result = CSGHelper.subtract(target, cutters);
                result.geometry.clearGroups();
                finalGeo = result.geometry;
            }
        }

        // Determine color: material registry first, per-function palette fallback.
        const registryColor = layerMaterialColor(project, layer);
        let color = registryColor ?? '#808080';
        if (!registryColor) {
            switch (layer.function) {
                case 'Structure': color = '#808080'; break;
                case 'Substrate': color = '#a0a0a0'; break;
                case 'Finish 1': color = '#e0e0e0'; break; // Light tiles?
                case 'Finish 2': color = '#d0d0d0'; break;
                case 'Thermal/Air Layer': color = '#ffff00'; break;
                case 'Membrane Layer': color = '#000000'; break;
            }
        }

        renderedLayers.push({
            geometry: finalGeo,
            position: [0, 0, 0], // layer transform is baked into finalGeo for CSG
            color: color,
            index: i,
            material: resolveMaterial(project, layer.materialId),
            isCore: layer.function === 'Structure'
        });

        currentZ -= thickness;
    }

    return (
        <group userData={{ id: element.id }}>
            {renderedLayers.map((layer, i) => (
                <mesh key={i} geometry={layer.geometry} position={new THREE.Vector3(...layer.position)}>
                    <WallLayerMaterial
                        color={layer.color}
                        isSelected={isSelected}
                        viewMode={viewMode}
                        viewType={viewType}
                        isCore={layer.isCore}
                        material={layer.material}
                        asset={resolveAppearance(project, layer.material)}
                        imageUrls={imageUrls}
                    />
                    {(isPlan || isHiddenLine || isWireframe || isConsistentColors || isShaded) && (
                        <InkEdges isSelected={isSelected} />
                    )}
                </mesh>
            ))}
        </group>
    );
}

import { GeometryHelper } from '../core/GeometryHelper';

function RoofRenderer({ element, isSelected, viewType, viewMode, structuralOpenings }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string, structuralOpenings?: BIMElement[] }) {
    const geometryData = (element as any).geometryData;
    const categoryBim = useCategoryMaterial(element.category);
    const material = useViewMaterial('#8b4513', isSelected, viewMode, viewType, false, categoryBim); // Wood/Shingle color

    if (!geometryData || !geometryData.footprint) return null;

    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';
    const isShaded = viewMode === 'shaded';

    const roofType = geometryData.roofType || 'Flat';
    const slope = geometryData.slope || 0;
    const offset = geometryData.baseOffsetFromLevel || 0;

    let geometry: THREE.BufferGeometry;

    if (roofType === 'Flat' || slope <= 0) {
        // Extrusion same as floor
        const shape = new THREE.Shape();
        const p = geometryData.footprint;
        shape.moveTo(p[0][0], p[0][1]);
        for (let i = 1; i < p.length; i++) {
            shape.lineTo(p[i][0], p[i][1]);
        }
        shape.closePath();

        geometry = new THREE.ExtrudeGeometry(shape, { depth: 0.3, bevelEnabled: false }); // Default thickness 300mm
        // Adjust to originate at level (bottom) or offset?
        // Usually Roof Offset is to the bottom face.
    } else {
        // Sloped Roof
        geometry = GeometryHelper.generateHipRoofGeometry(geometryData.footprint, slope);
    }

    // Parametric Structural Opening voids (vertical/shaft) pierce the roof:
    // bake the offset transform and CSG-subtract world-space cutters.
    geometry.translate(0, 0, offset);
    let finalGeometry = geometry;
    if (structuralOpenings && structuralOpenings.length > 0) {
        const cutters = structuralOpenings
            .map(o => buildOpeningCutter(o.geometryData as any))
            .filter((m): m is THREE.Mesh => !!m);
        if (cutters.length > 0) {
            const target = new THREE.Mesh(geometry);
            target.updateMatrix();
            const result = CSGHelper.subtract(target, cutters);
            result.geometry.clearGroups();
            finalGeometry = result.geometry;
        }
    }

    return (
        <group userData={{ id: element.id }}>
            <mesh geometry={finalGeometry}>
                {material}
                {(isPlan || isHiddenLine || isWireframe || isConsistentColors || isShaded) && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
        </group>
    );
}

function ColumnRenderer({ element, isSelected, viewType, viewMode, activeView, structuralOpenings }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string, activeView?: View, structuralOpenings?: BIMElement[] }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#cccccc', isSelected, viewMode, viewType);
    if (!geom) return null;

    const pos = new THREE.Vector3(...geom.position);
    let height = geom.height;
    let zPos = geom.height / 2;

    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';

    if (isPlan) {
        let cutPlaneHeight = 1.2;
        if (activeView && activeView.viewRange) {
            cutPlaneHeight = activeView.viewRange.cutPlane;
        }

        // Columns base is usually at level elevation (0 relative to level)
        // So visual height is just cutPlaneHeight
        if (cutPlaneHeight >0 && cutPlaneHeight< height) {
            height = cutPlaneHeight;
            zPos = height / 2;
        }
    }

    pos.z += zPos;

    // Parametric Structural Opening voids cut the column: bake the column
    // transform into a world-space box, then CSG-subtract the cutters.
    if (structuralOpenings && structuralOpenings.length > 0) {
        const cutters = structuralOpenings
            .map(o => buildOpeningCutter(o.geometryData as any))
            .filter((m): m is THREE.Mesh => !!m);
        if (cutters.length > 0) {
            const box = new THREE.BoxGeometry(geom.width, geom.depth, height);
            const matrix = new THREE.Matrix4().makeRotationZ(THREE.MathUtils.degToRad(geom.rotation || 0));
            matrix.setPosition(pos.x, pos.y, pos.z);
            box.applyMatrix4(matrix);
            const target = new THREE.Mesh(box);
            target.updateMatrix();
            const result = CSGHelper.subtract(target, cutters);
            result.geometry.clearGroups();
            return (
                <group userData={{ id: element.id }}>
                    <mesh geometry={result.geometry}>
                        {material}
                        {(isPlan || isHiddenLine || isWireframe || isConsistentColors || viewMode === 'shaded') && (
                            <InkEdges isSelected={isSelected} />
                        )}
                    </mesh>
                </group>
            );
        }
    }

    const content = (
        <mesh>
            <boxGeometry args={[geom.width, geom.depth, height]} />
            {material}
            {(isPlan || isHiddenLine || isWireframe || isConsistentColors || viewMode === 'shaded') && (
                <InkEdges isSelected={isSelected} />
            )}
        </mesh>
    );
    return (
    <group position={pos} rotation={[0, 0, THREE.MathUtils.degToRad(geom.rotation || 0)]} userData={{ id: element.id }}>
            {content}
        </group>
    );
}

/**
 * Structural member renderer shared by beams and braces (both live in
 * 'Structural Framing').
 *
 * The BeamTool writes an EnhancedBeamGeometryData with the cross-section
 * nested under `profile` (mm), while legacy/brace data carries flat
 * `width`/`depth` in meters — resolve both into a world-space box aligned
 * to the member axis. Cross-section is centered on the location line,
 * honoring the justification (Top/Center/Bottom) so the reference line
 * means the same thing it does in BIM.
 */
function resolveFramingMemberGeometry(geom: any): {
    start: THREE.Vector3;
    end: THREE.Vector3;
    width: number;   // m, cross-section width (b)
    depth: number;   // m, cross-section depth (h)
} | null {
    if (!geom?.start || !geom?.end) return null;

    // Enhanced beam profile lives in mm under `profile`; legacy data has flat meter fields.
    const profile = geom.profile;
    const width = profile ? (profile.width ?? profile.diameter ?? 0) / 1000 : (geom.width ?? 0.2);
    const depth = profile ? (profile.depth ?? profile.diameter ?? 0) / 1000 : (geom.depth ?? 0.2);

    if (!(width > 0) || !(depth > 0)) return null;

    return {
        start: new THREE.Vector3(...geom.start),
        end: new THREE.Vector3(...geom.end),
        width,
        depth,
    };
}

function FramingMemberRenderer({ element, isSelected, viewType, viewMode, kind, structuralOpenings }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string, kind: 'beam' | 'brace', structuralOpenings?: BIMElement[] }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial(kind === 'brace' ? '#555555' : '#333333', isSelected, viewMode, viewType);

    // CutThrough holes and parametric Structural Opening voids need a real
    // CSG subtraction, so the whole member (segments included) is rebuilt
    // once into a single world-space geometry whenever contour cuts or hosted
    // openings exist. Computed before the early returns below to keep the
    // hook order stable.
    const hasHostOpenings = !!structuralOpenings && structuralOpenings.length > 0;
    const steelHoleGeo = useMemo(() => {
        const hasContours = element.steelCuts?.some(c => c.kind === 'CutThrough' && (c.contour?.length ?? 0) >= 3);
        if (!hasContours && !hasHostOpenings) return null;
        const frame = resolveFramingMemberGeometry(geom);
        if (!frame) return null;
        const { start: fs, end: fe, width: fw, depth: fd } = frame;
        const flen = fs.distanceTo(fe);
        if (flen < 0.001) return null;

        const segments = computeMemberSegments(element);
        if (!segments || segments.segments.length === 0) return null;
        const dir = fe.clone().sub(fs).normalize();
        const yaw = Math.atan2(dir.y, dir.x);
        const just = geom?.justification as string | undefined;
        let jz = 0;
        if (just === 'Top') jz = -fd / 2;
        else if (just === 'Bottom') jz = fd / 2;

        const geos: THREE.BufferGeometry[] = [];
        for (const seg of segments.segments) {
            const segLen = (seg.t1 - seg.t0) * flen;
            if (segLen < 0.001) continue;
            const geo = new THREE.BoxGeometry(segLen, fw * seg.widthScale, fd * seg.depthScale);
            const center = fs.clone().addScaledVector(dir, ((seg.t0 + seg.t1) / 2) * flen);
            const m = new THREE.Matrix4().makeRotationZ(yaw);
            m.setPosition(center.x, center.y, center.z + jz + seg.depthShift * (fd - fd * seg.depthScale) / 2);
            geo.applyMatrix4(m);
            geos.push(geo);
        }
        if (geos.length === 0) return null;

        const merged = mergeGeometries(geos);
        if (!merged) return null;
        const baseMesh = new THREE.Mesh(merged);
        baseMesh.updateMatrix();
        const cutters = buildSteelHoleCutters(element, { start: fs, end: fe, width: fw, depth: fd });
        const cutterMeshes = cutters.map(c => {
            const mesh = new THREE.Mesh(c.geometry);
            mesh.updateMatrix();
            return mesh;
        });
        // Parametric Structural Opening voids cut the member too.
        if (hasHostOpenings) {
            for (const opening of structuralOpenings!) {
                const voidMesh = buildOpeningCutter(opening.geometryData as any);
                if (voidMesh) cutterMeshes.push(voidMesh);
            }
        }
        const result = CSGHelper.subtract(baseMesh, cutterMeshes);
        // Rendered with a single view material; drop CSG material groups so
        // three.js never resolves out-of-range material indices.
        result.geometry.clearGroups();
        return result.geometry;
    }, [element, structuralOpenings, hasHostOpenings]);

    const member = resolveFramingMemberGeometry(geom);
    if (!member) return null;

    const { start, end, width, depth } = member;

    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';

    const length = start.distanceTo(end);
    if (length < 0.001) return null;

    // Parametric steel cuts (Steel tab: Modifier/Cope/Miter) split the
    // member into axis segments with per-zone section overrides; CutThrough
    // contours are handled by the CSG-baked steelHoleGeo above.
    const hasSteelCuts = !!element.steelCuts?.length;
    const cutSegments = hasSteelCuts ? computeMemberSegments(element) : null;

    const mid = start.clone().add(end).multiplyScalar(0.5);
    const direction = end.clone().sub(start).normalize();
    const angle = Math.atan2(direction.y, direction.x);

    // z Justification: where the reference line sits within the section.
    const justification = geom.justification as string | undefined;
    let depthOffset = 0;
    if (justification === 'Top') depthOffset = -depth / 2;
    else if (justification === 'Bottom') depthOffset = depth / 2;

    const showEdges = isPlan || isHiddenLine || isWireframe || isConsistentColors || viewMode === 'shaded';

    // Contour cuts: one world-space CSG mesh replaces the box(es) entirely.
    if (steelHoleGeo) {
        return (
            <group userData={{ id: element.id }}>
                <mesh geometry={steelHoleGeo}>
                    {material}
                    {showEdges && <InkEdges isSelected={isSelected} />}
                </mesh>
            </group>
        );
    }

    if (cutSegments && (cutSegments.segments.length > 1 || cutSegments.segments[0]?.t0 !== 0 || cutSegments.segments[0]?.t1 !== 1)) {
        return (
            <group userData={{ id: element.id }}>
                {cutSegments.segments.map((seg, i) => {
                    const segLen = (seg.t1 - seg.t0) * length;
                    if (segLen < 0.001) return null;
                    const segW = width * seg.widthScale;
                    const segD = depth * seg.depthScale;
                    const center = start.clone().addScaledVector(direction, ((seg.t0 + seg.t1) / 2) * length);
                    const shiftZ = seg.depthShift * (depth - segD) / 2;
                    return (
                        <group key={i} position={[center.x, center.y, center.z + depthOffset + shiftZ]} rotation={[0, 0, angle]}>
                            <mesh>
                                <boxGeometry args={[segLen, segW, segD]} />
                                {material}
                                {showEdges && <InkEdges isSelected={isSelected} />}
                            </mesh>
                        </group>
                    );
                })}
            </group>
        );
    }

    const content = (
        <mesh>
            <boxGeometry args={[length, width, depth]} />
            {material}
            {(isPlan || isHiddenLine || isWireframe || isConsistentColors || viewMode === 'shaded') && (
                <InkEdges isSelected={isSelected} />
            )}
        </mesh>
    );
    return (
    <group position={[mid.x, mid.y, mid.z + depthOffset]} rotation={[0, 0, angle]} userData={{ id: element.id }}>
            {content}
        </group>
    );
}

function BeamRenderer(props: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    return <FramingMemberRenderer {...props} kind="beam" />;
}

function BraceRenderer(props: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    return <FramingMemberRenderer {...props} kind="brace" />;
}

/**
 * Truss renderer: draws each member of the generated truss (top/bottom chords,
 * verticals, diagonals) as a prismatic box along its axis.
 */
function TrussRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#666666', isSelected, viewMode, viewType);

    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';
    const showEdges = viewType === 'plan' || isHiddenLine || isWireframe || isConsistentColors || viewMode === 'shaded';

    const members = geom?.members as any[] | undefined;
    if (!Array.isArray(members) || members.length === 0) return null;

    return (
        <group userData={{ id: element.id }}>
            {members.map((member: any) => {
                if (!member?.start || !member?.end) return null;
                const start = new THREE.Vector3(...member.start);
                const end = new THREE.Vector3(...member.end);
                const length = start.distanceTo(end);
                if (length < 0.001) return null;

                const profile = member.profile;
                const sizeM = Math.max((profile?.width ?? 100) / 1000, 0.03);
                const depthM = Math.max((profile?.height ?? profile?.width ?? 100) / 1000, 0.03);

                const mid = start.clone().add(end).multiplyScalar(0.5);
                const direction = end.clone().sub(start).normalize();
                const angle = Math.atan2(direction.y, direction.x);
                const pitch = Math.asin(THREE.MathUtils.clamp(direction.z, -1, 1));

                return (
                    <group key={member.id ?? `${member.start.join(',')}-${member.end.join(',')}`}
                        position={mid} rotation={[0, 0, angle]}>
                        {/* Tilt the box's local X axis onto the member axis:
                            yaw around Z (outer), pitch around local Y (inner,
                            negated because +Y rotation sends +X toward -Z). */}
                        <group rotation={[0, -pitch, 0]}>
                            <mesh>
                                <boxGeometry args={[length, sizeM, depthM]} />
                                {material}
                                {showEdges && <InkEdges isSelected={isSelected} />}
                            </mesh>
                        </group>
                    </group>
                );
            })}
        </group>
    );
}

/**
 * Beam system renderer: boundary outline plus the generated beam lines drawn
 * as framing boxes along the system's beam direction.
 */
function BeamSystemRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#333333', isSelected, viewMode, viewType);

    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';
    const showEdges = viewType === 'plan' || isHiddenLine || isWireframe || isConsistentColors || viewMode === 'shaded';

    const beams = geom?.beams as any[] | undefined;
    if (!Array.isArray(beams) || beams.length === 0) return null;

    const boundary = geom.boundary as number[][] | undefined;
    const defaultWidth = 0.2;
    const defaultDepth = 0.5;

    return (
        <group userData={{ id: element.id }}>
            {boundary && boundary.length >= 3 && (
                <Line
                    points={[...boundary.map(p => new THREE.Vector3(...(p as [number, number, number]))), new THREE.Vector3(...(boundary[0] as [number, number, number]))]}
                    color={isSelected ? '#00aaff' : '#888888'}
                    lineWidth={1}
                    dashed
                    dashScale={5}
                />
            )}
            {beams.map((beam: any, i: number) => {
                if (!beam?.start || !beam?.end) return null;
                const start = new THREE.Vector3(...beam.start);
                const end = new THREE.Vector3(...beam.end);
                const length = start.distanceTo(end);
                if (length < 0.001) return null;

                const mid = start.clone().add(end).multiplyScalar(0.5);
                const direction = end.clone().sub(start).normalize();
                const angle = Math.atan2(direction.y, direction.x);

                return (
                    <group key={i} position={mid} rotation={[0, 0, angle]}>
                        <mesh>
                            <boxGeometry args={[length, defaultWidth, defaultDepth]} />
                            {material}
                            {showEdges && <InkEdges isSelected={isSelected} />}
                        </mesh>
                    </group>
                );
            })}
        </group>
    );
}

function DuctRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    // Color Fill scheme (systems legend): use the resolved palette color.
    const fillColor = element.parameters?.['Color Fill Color'] as string | undefined;
    const material = useViewMaterial(fillColor ?? '#silver', isSelected, viewMode, viewType);
    if (!geom || !geom.path) return null;

    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';

    // Render segments
    return (
        <group>
            {geom.path.map((p: any, i: number) => {
                if (i === 0) return null;
                const start = new THREE.Vector3(...geom.path[i - 1]);
                const end = new THREE.Vector3(...p);
                const length = start.distanceTo(end);
                const pos = start.clone().add(end).multiplyScalar(0.5);
                const direction = end.clone().sub(start).normalize();
                const angle = Math.atan2(direction.y, direction.x);

                const content = (
                    <mesh>
                        <boxGeometry args={[length, geom.width, geom.height]} />
                        {material}
                        {(isPlan || isHiddenLine || isWireframe || isConsistentColors || viewMode === 'shaded') && (
                            <InkEdges isSelected={isSelected} />
                        )}
                    </mesh>
    );
    return (
    <group key={i} position={pos} rotation={[0, 0, angle]} userData={{ id: element.id }}>
                        {content}
                    </group>
                );
            })}
        </group>
    );
}

function PipeRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    // Color Fill scheme (systems legend): use the resolved palette color.
    const fillColor = element.parameters?.['Color Fill Color'] as string | undefined;
    const material = useViewMaterial(fillColor ?? '#b87333', isSelected, viewMode, viewType);
    if (!geom || !geom.path) return null;

    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';

    return (
        <group>
            {geom.path.map((p: any, i: number) => {
                if (i === 0) return null;
                const start = new THREE.Vector3(...geom.path[i - 1]);
                const end = new THREE.Vector3(...p);
                const length = start.distanceTo(end);
                const pos = start.clone().add(end).multiplyScalar(0.5);
                const direction = end.clone().sub(start).normalize();
                const angle = Math.atan2(direction.y, direction.x);

                const content = (
                    <mesh>
                        <cylinderGeometry args={[geom.diameter / 2, geom.diameter / 2, length, 8]} />
                        {material}
                        {(isPlan || isHiddenLine || isWireframe || isConsistentColors || viewMode === 'shaded') && (
                            <InkEdges isSelected={isSelected} />
                        )}
                    </mesh>
    );
    return (
    <group key={i} position={pos} rotation={[0, 0, angle + Math.PI / 2]} userData={{ id: element.id }}>
                        {content}
                    </group>
                );
            })}
        </group>
    );
}

// function ToposolidRenderer moved below

function RailingRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geometryData = (element as any).geometryData;
    const material = useViewMaterial('#a0a0a0', isSelected, viewMode, viewType); // Metal

    if (!geometryData || !geometryData.path) return null;

    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';

    // Construct Path
    const points = geometryData.path.map((p: any) => new THREE.Vector3(...p));
    const curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.1);
    // Or just lines for now? Railing usually straight segments.
    // Let's use CurvePath or just segments.

    // Simple Tube Geometry for Top Rail
    const tubeGeo = new THREE.TubeGeometry(curve, points.length * 10, 0.025, 8, false);

    // Balusters (simplistic)
    const balusters = [];
    if (points.length > 1) {
        for (let i = 0; i < points.length; i++) {
            // For each segment, place balusters?
            // Just place at points for now
            balusters.push(
                <mesh key={i} position={points[i]}>
                    <cylinderGeometry args={[0.01, 0.01, 1.1, 8]} />
                    <meshStandardMaterial color="#a0a0a0" />
                </mesh>
            );
            // Shift cylinder up by height/2 (0.55) so base is on path
            // But path is usually at floor level.
            // So position.z += 0.55
        }
    }

    return (
        <group userData={{ id: element.id }}>
            {/* Top Rail */}
            <mesh geometry={tubeGeo} position={[0, 0, 1.1]}>
                {material}
            </mesh>

            {/* Balusters */}
            {points.map((p: THREE.Vector3, i: number) => (
                <mesh key={i} position={[p.x, p.y, p.z + 0.55]}>
                    <cylinderGeometry args={[0.01, 0.01, 1.1, 8]} />
                    {material}
                </mesh>
            ))}

            {(isPlan || isHiddenLine || isWireframe || isConsistentColors) && (
                <InkEdges geometry={tubeGeo} position={[0, 0, 1.1]} isSelected={isSelected} />
            )}
        </group>
    );
}

function RampRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geometryData = (element as any).geometryData;
    const material = useViewMaterial('#CCCCCC', isSelected, viewMode, viewType); // Concrete

    if (!geometryData || !geometryData.start || !geometryData.end) return null;

    const start = new THREE.Vector3(...geometryData.start);
    const end = new THREE.Vector3(...geometryData.end);
    const width = geometryData.width || 1.0;

    const length = new THREE.Vector2(start.x - end.x, start.y - end.y).length();
    const heightDiff = end.z - start.z;

    // Create Ramp Geometry (Wedge)
    const shape = new THREE.Shape();
    shape.moveTo(0, 0); // Start Bottom
    shape.lineTo(length, heightDiff); // End Top
    shape.lineTo(length, heightDiff - 0.2); // End Bottom (Thickness 0.2)
    shape.lineTo(0, -0.2); // Start Bottom (Thickness)
    shape.closePath();

    const extrudeSettings = {
        depth: width,
        bevelEnabled: false
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    // Extrusion happens along Z axis by default in Shape -> Extrude?
    // Wait, shape is 2D (XY). Extrusion is Z.
    // We want the Profile to be vertical (Length/Height), and Extrude along Width.

    // We need to rotate the mesh to align with Start->End direction.
        const dir = end.clone().sub(start); const angle = Math.atan2(dir.y, dir.x);
        // Profile is defined in X=Length, Y=Height (Z in world).
        // So we need to rotate X to align with path, Y to align with Z (Up).
        // And Extrude (Z in local) to align with Width (Cross product of path and up).
        // Actually, simple Box with slope is easier for now, but Wedge is better.
        return (
<group position={[start.x, start.y, start.z]} rotation={[Math.PI / 2, 0, angle]} userData={{ id: element.id }}>
            <mesh geometry={geometry} rotation={[Math.PI / 2, 0, 0]} position={[0, width / 2, 0]}>
                {material}
                <InkEdges isSelected={isSelected} />
            </mesh>
        </group>
    );
}

function ComponentRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geometryData = (element as any).geometryData;
    const material = useViewMaterial('#purple', isSelected, viewMode, viewType); // Generic Purple

    // Imported CAD: parsed line work (points pairs) drawn with per-vertex
    // colors; legacy start/end payloads are converted on the fly.
    if (geometryData?.lines && Array.isArray(geometryData.lines) && element.type === 'Imported CAD') {
        const normalized = geometryData.lines.map((ln: any) =>
            ln.points ? ln : { ...ln, points: [ln.start, ln.end] }
        );
        // New imports store lines relative to the object origin (bbox-centered);
        // the legacy unit-square payload is absolute. Position by origin only
        // when the parsed marker exists.
        const isParsed = !!geometryData.cad;
        const origin = (geometryData.origin ?? [0, 0, 0]) as [number, number, number];
        // Contextual Foreground draw order disables depth testing.
        const depthTest = element.parameters?.['Draw Order'] !== 'Foreground';
        return (
            <group position={isParsed ? origin : [0, 0, 0]} userData={{ id: element.id }}>
                <CadGeometryRenderer geom={{ ...geometryData, lines: normalized }} isSelected={isSelected} depthTest={depthTest} />
            </group>
        );
    }

    if (!geometryData || !geometryData.position) return null;

    const [x, y, z] = geometryData.position;
    const rotation = geometryData.rotation ? THREE.MathUtils.degToRad(geometryData.rotation) : 0;

    const width = geometryData.width || 0.5;
    const depth = geometryData.depth || 0.5;
    const height = geometryData.height || 0.5;

    // Simple Box for now. Future: Load GLTF based on element.type

    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';
    const isShaded = viewMode === 'shaded';

    return (
        <group position={[x, y, z]} rotation={[0, 0, rotation]} userData={{ id: element.id }}>
            <mesh position={[0, 0, height / 2]}>
                <boxGeometry args={[width, depth, height]} />
                {material}
                {(isPlan || isHiddenLine || isWireframe || isConsistentColors || isShaded) && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
            {/* Draw 'Front' arrow or similar? */}
        </group>
    );
}

function MassRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geometryData = (element as any).geometryData;
    // BIM "Show Mass Form and Floors": when off, the mass form and its mass
    // floors disappear from every view (Conceptual Mass panel toggle).
    const showMass = useAppStore((s) =>s.view.showMassFormAndFloors); 
// Mass is usually semi-transparent in BIM unless "Show Mass" is toggled. // Let's make it translucent purple //
        const material = useViewMaterial('#a020f0', isSelected, viewMode, viewType); if (!showMass) return null; if (!geometryData || !geometryData.footprint) return null; const isPlan = viewType === 'plan'; const isHiddenLine = viewMode === 'hiddenLine'; const isWireframe = viewMode === 'wireframe'; const isConsistentColors = viewMode === 'consistentColors'; const isShaded = viewMode === 'shaded'; const shape = new THREE.Shape(); const p = geometryData.footprint; shape.moveTo(p[0][0], p[0][1]); for (let i = 1; i
< p.length; i++) {
        shape.lineTo(p[i][0], p[i][1]);
    }
    shape.closePath();

    const height = geometryData.height || 5.0;
    const elevation = geometryData.elevation || 0;

    const geo = new THREE.ExtrudeGeometry(shape, { depth: height, bevelEnabled: false });

    // Mass floor plates (parameters.floors): thin translucent slabs stacked at
    // their elevations — the "Mass Floors" half of the toggle.
    const floors: { elevation: number }[] = geometryData.floors ?? [];

    return (
        <group position={[0, 0, elevation]} userData={{ id: element.id }}>
            <mesh geometry={geo}>
                {/* Override standard material for transparency if needed */}
                <meshStandardMaterial
                    color="#a020f0"
                    transparent
                    opacity={0.5}
                    side={THREE.DoubleSide}
                />
                {(isPlan || isHiddenLine || isWireframe || isConsistentColors || isShaded) && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
            {floors.map((f, i) => (
                <mesh key={i} geometry={geo} position={[0, 0, f.elevation - elevation - 0.05]} scale={[1, 1, 0.02 / Math.max(height, 0.1)]}>
                    <meshBasicMaterial color="#7ba7cc" transparent opacity={0.55} side={THREE.DoubleSide} depthWrite={false} />
                </mesh>
            ))}
        </group>
    );
}

function RoomRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geometryData = (element as any).geometryData;
    // Rooms are crosshairs or color fills
    // Semi-transparent blue for fill
    // const material = useViewMaterial('#aaccff', isSelected, viewMode, viewType);

    if (!geometryData || !geometryData.boundary) return null;

    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';

    // In BIM, Room Color Fill is a specific mode. Standard hidden line doesn't show fill usually, just the tag and maybe specific 'Reference' crosshair.
    // If selected, we show fill.

    const showFill = isSelected || viewMode === 'shaded' || viewMode === 'consistentColors';

    const shape = new THREE.Shape();
    const p = geometryData.boundary;
    shape.moveTo(p[0][0], p[0][1]);
    for (let i = 1; i < p.length; i++) {
        shape.lineTo(p[i][0], p[i][1]);
    }
    shape.closePath();

    const shapeGeo = new THREE.ShapeGeometry(shape);

    // Color Fill scheme (Annotate >Color Fill): the legend tool writes the 
// resolved palette color into 'Color Fill Color'; paint it when present.
        const fillColor = element.parameters?.['Color Fill Color'] as string | undefined; return (
<group userData={{ id: element.id }} position={[0, 0, 0.02]}>
            {/* 0.02 to be above floor */}

            {/* Fill */}
            {(showFill || (fillColor && isPlan)) && (
                <mesh geometry={shapeGeo}>
                    <meshBasicMaterial color={fillColor && !isSelected ? fillColor : '#aaccff'} transparent opacity={0.3} side={THREE.DoubleSide} />
                </mesh>
            )}

            {/* Crosshair (Reference Lines) - Always visible in plan if reference is on, but let's just show border if selected */}
            {(isPlan || isHiddenLine) && (
                <Edges geometry={shapeGeo} color={isSelected ? '#00aaff' : 'transparent'} threshold={15} />
            )}

            {/* Crosshair Logic could go here */}
            {isSelected && (
                <group position={geometryData.position}>
                    <line>
                        <bufferGeometry args={undefined} ref={(geom) => {
                            if (geom) geom.setFromPoints([new THREE.Vector3(-1, -1, 0), new THREE.Vector3(1, 1, 0)]);
                        }} />
                        <lineBasicMaterial color="#00aaff" />
                    </line>
                    <line>
                        <bufferGeometry args={undefined} ref={(geom) => {
                            if (geom) geom.setFromPoints([new THREE.Vector3(-1, 1, 0), new THREE.Vector3(1, -1, 0)]);
                        }} />
                        <lineBasicMaterial color="#00aaff" />
                    </line>
                </group>
            )}
        </group>
    );
}

function AreaRenderer({ element, isSelected, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geometryData = (element as any).geometryData;
    // Areas are usually yellow/green boundaries

    if (!geometryData || !geometryData.boundary) return null;

    const shape = new THREE.Shape();
    const p = geometryData.boundary;
    shape.moveTo(p[0][0], p[0][1]);
    for (let i = 1; i < p.length; i++) {
        shape.lineTo(p[i][0], p[i][1]);
    }
    shape.closePath();

    const shapeGeo = new THREE.ShapeGeometry(shape);

    const showFill = isSelected || viewMode === 'shaded';

    return (
        <group userData={{ id: element.id }} position={[0, 0, 0.03]}>
            {showFill && (
                <mesh geometry={shapeGeo}>
                    <meshBasicMaterial color="#ffdd00" transparent opacity={0.3} side={THREE.DoubleSide} />
                </mesh>
            )}

            {/* Boundary Lines - Area Boundary Lines are purple/magenta usually */}
            <Edges geometry={shapeGeo} color={isSelected ? '#00aaff' : '#ff00ff'} threshold={15} />
        </group>
    );
}

function ToposolidRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geometryData = (element as any).geometryData;
    const material = useViewMaterial('#4caf50', isSelected, viewMode, viewType); // Green for grass

    // Find intersecting cutting elements (e.g. Masses) and building pads
    // (terrain excavation). Scoped subscription (identity-stable via
    // useShallow) replaces the whole-project prop so toposolids only refresh
    // when a cutter actually changes.
    const cuttingElements = useBIMStore(useShallow((state) => {
        const out: BIMElement[] = [];
        state.project.elements.forEach((e) => {
            if (e.category === 'Mass' || e.category === 'Building Pads' || e.category === 'Graded Regions') out.push(e);
        });
        return out;
    }));

    const solidMesh = useMemo(() => {
        if (!geometryData || !geometryData.boundary) return null;

        const shape = new THREE.Shape();
        const p = geometryData.boundary;
        shape.moveTo(p[0][0], p[0][1]);
        for (let i = 1; i < p.length; i++) {
            shape.lineTo(p[i][0], p[i][1]);
        }
        shape.closePath();

        const height = geometryData.height || 3.0; // Depth downwards
        const elevation = geometryData.baseElevation || 0;

        // Extrude geometry
        const geo = new THREE.ExtrudeGeometry(shape, { depth: height, bevelEnabled: false });

        // Base Mesh
        const baseMesh = new THREE.Mesh(geo);
        // Position: In BIM Toposolid, sketch is Top Face. Extrude Down.
        // ExtrudeGeometry extrudes +Z. So we rotate/flip or just position at elem - height?
        // Let's assume height is positive thickness.
        // We want Top Face at Elevation. Bottom Face at Elevation - Height.
        // If we Extrude +Z, we put mesh at Elevation - Height.
        // Baked into geometry (not a mesh transform): the JSX below consumes
        // solidMesh.geometry only, and the no-cutter early return of
        // CSGHelper.subtract() would discard any mesh-level transform.
        geo.translate(0, 0, elevation - height);
        baseMesh.updateMatrix();

        // Create Cutting Meshes
        // This is expensive, so limit to selection or small numbers usually.
        const cutMeshes = cuttingElements.map(cutter => {
            const cGeom = (cutter as any).geometryData;
            const poly: [number, number][] | undefined = cGeom?.footprint ?? cGeom?.boundary;
            if (!poly || poly.length < 3) return null;

            // Create cutter geometry (simple extrusion)
            const cShape = new THREE.Shape();
            cShape.moveTo(poly[0][0], poly[0][1]);
            for (let i = 1; i < poly.length; i++) cShape.lineTo(poly[i][0], poly[i][1]);
            cShape.closePath();

            // 'Cut' building pads and graded regions trim the terrain down to
            // their surface elevation: the void extrudes UP from that level.
            // 'Fill' pads build ground instead of removing it, so they never
            // act as cutters. Masses keep the classic mass-cuts-ground behavior.
            let cHeight = 0;
            let cElev = 0;
            if (cutter.category === 'Building Pads') {
                if ((cGeom.kind ?? 'Cut') !== 'Cut') return null;
                cElev = cGeom.elevation ?? 0;
                cHeight = 30; // generous void above the pad plane
            } else if (cutter.category === 'Graded Regions') {
                const off = cGeom.elevationOffset ?? 0;
                if (off >= 0) return null; // embankment: not a cut
                cElev = (geometryData.baseElevation ?? 0) + off;
                cHeight = 30;
            } else {
                cHeight = cGeom.height || 5;
                cElev = cGeom.elevation || 0;
            }
            const cGeo = new THREE.ExtrudeGeometry(cShape, { depth: cHeight, bevelEnabled: false });
            const cMesh = new THREE.Mesh(cGeo);
            cMesh.position.set(0, 0, cElev);
            cMesh.updateMatrix();
            return cMesh;
        }).filter(m => m !== null) as THREE.Mesh[];

        if (cutMeshes.length > 0) {
            return CSGHelper.subtract(baseMesh, cutMeshes);
        }

        return baseMesh;

    }, [geometryData, cuttingElements]);

    if (!solidMesh) return null;

    const isPlan = viewType === 'plan';
    const isHiddenLine = viewMode === 'hiddenLine';
    const isWireframe = viewMode === 'wireframe';
    const isConsistentColors = viewMode === 'consistentColors';
    const isShaded = viewMode === 'shaded';

    return (
        <group userData={{ id: element.id }}>
            <mesh geometry={solidMesh.geometry}>
                {material}
                {(isPlan || isHiddenLine || isWireframe || isConsistentColors || isShaded) && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
            {/* Contour Lines? Future enhancement */}
        </group>
    );
}

// ==================== Massing & Site element renderers ====================

/** Shared closed-loop outline from a boundary polygon at an elevation. */
function boundaryLoop(boundary: [number, number][], z: number): THREE.Vector3[] {
    return [...boundary.map((p) => new THREE.Vector3(p[0], p[1], z)), new THREE.Vector3(boundary[0][0], boundary[0][1], z)];
}

/** Site Component: RPC-style tree/shrub/bench/lamp/car primitive. */
function SiteComponentRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    if (!geom?.position) return null;
    const [x, y, z] = geom.position;
    const kind: string = geom.kind ?? 'Tree';
    const trunk = isSelected ? '#00aaff' : '#5e452a';
    const foliage = isSelected ? '#66ccff' : kind === 'Car' ? '#3f7d9e' : '#2e8b2e';

    return (
        <group position={[x, y, z]} userData={{ id: element.id }}>
            {kind === 'Tree' && (
                <>
                    <mesh position={[0, 0, 1.2]}>
                        <cylinderGeometry args={[0.12, 0.16, 2.4, 8]} />
                        <meshStandardMaterial color={trunk} />
                    </mesh>
                    <mesh position={[0, 0, 3.6]}>
                        <coneGeometry args={[1.2, 3, 12]} />
                        <meshStandardMaterial color={foliage} />
                    </mesh>
                </>
            )}
            {kind === 'Shrub' && (
                <mesh position={[0, 0, 0.5]}>
                    <sphereGeometry args={[0.6, 12, 10]} />
                    <meshStandardMaterial color={foliage} />
                </mesh>
            )}
            {kind === 'Bench' && (
                <mesh position={[0, 0, 0.4]}>
                    <boxGeometry args={[1.6, 0.5, 0.15]} />
                    <meshStandardMaterial color={trunk} />
                </mesh>
            )}
            {kind === 'Lamp' && (
                <>
                    <mesh position={[0, 0, 2.2]}>
                        <cylinderGeometry args={[0.05, 0.07, 4.4, 8]} />
                        <meshStandardMaterial color="#444c55" />
                    </mesh>
                    <mesh position={[0, 0, 4.5]}>
                        <sphereGeometry args={[0.18, 10, 8]} />
                        <meshStandardMaterial color="#ffe58a" emissive="#ffd95e" emissiveIntensity={0.7} />
                    </mesh>
                </>
            )}
            {kind === 'Car' && (
                <mesh position={[0, 0, 0.75]}>
                    <boxGeometry args={[2, 4.5, 1.5]} />
                    <meshStandardMaterial color={foliage} />
                </mesh>
            )}
        </group>
    );
}

/** Parking Area: asphalt sheet, white bay stripes and a yellow access edge. */
function ParkingAreaRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    if (!geom) return null;
    const { x0, y0, width, depth, bays, bayWidth } = geom as {
        x0: number; y0: number; width: number; depth: number; bays: number; bayWidth: number; elevation: number;
    };
    const z = (geom.elevation ?? 0) + 0.02;

    const stripes: JSX.Element[] = [];
    for (let i = 1; i < bays; i++) {
        const sx = x0 + i * bayWidth;
        stripes.push(
            <Line key={i} points={[[sx, y0, z], [sx, y0 + depth, z]]} color={isSelected ? '#00aaff' : '#f2f2f2'} lineWidth={1.6} />
        );
    }

    return (
        <group userData={{ id: element.id }}>
            <mesh position={[x0 + width / 2, y0 + depth / 2, z - 0.02]}>
                <boxGeometry args={[width, depth, 0.04]} />
                <meshStandardMaterial color={isSelected ? '#1f6fae' : '#3a4047'} />
            </mesh>
            <Line points={[[x0, y0, z + 0.01], [x0 + width, y0, z + 0.01]]} color="#ffd95e" lineWidth={2.4} />
            {stripes}
        </group>
    );
}

/** Property Line: red dashed parcel boundary with corner dots. */
function PropertyLineRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    const boundary: [number, number][] | undefined = geom?.boundary;
    if (!boundary || boundary.length < 2) return null;
    const pts = boundaryLoop(boundary, 0.02);
    return (
        <group userData={{ id: element.id }}>
            <Line points={pts} color={isSelected ? '#00aaff' : '#c0392b'} lineWidth={2} dashed dashSize={0.8} gapSize={0.35} />
            {boundary.map((p, i) => (
                <mesh key={i} position={[p[0], p[1], 0.05]}>
                    <sphereGeometry args={[0.12, 10, 8]} />
                    <meshBasicMaterial color={isSelected ? '#00aaff' : '#22303c'} />
                </mesh>
            ))}
        </group>
    );
}

/** Building Pad: leveled surface plane + boundary outline at the pad elevation. */
function BuildingPadOutlineRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    const boundary: [number, number][] | undefined = geom?.boundary;
    if (!boundary || boundary.length < 3) return null;
    const z = geom.elevation ?? 0;
    const shape = new THREE.Shape();
    shape.moveTo(boundary[0][0], boundary[0][1]);
    for (let i = 1; i < boundary.length; i++) shape.lineTo(boundary[i][0], boundary[i][1]);
    shape.closePath();
    const fillColor = (geom.kind ?? 'Cut') === 'Cut' ? '#79828c' : '#a5834f';
    return (
        <group userData={{ id: element.id }}>
            <mesh position={[0, 0, z]}>
                <shapeGeometry args={[shape]} />
                <meshBasicMaterial color={fillColor} transparent opacity={0.85} side={THREE.DoubleSide} />
            </mesh>
            <Line points={boundaryLoop(boundary, z + 0.01)} color={isSelected ? '#00aaff' : '#22303c'} lineWidth={1.6} />
        </group>
    );
}

/** Graded Region: hatched proposed surface outline (orange dashed). */
function GradedRegionRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    const boundary: [number, number][] | undefined = geom?.boundary;
    if (!boundary || boundary.length < 3) return null;
    const offset = geom.elevationOffset ?? 0;
    const z = offset >= 0 ? 0.05 : 0.03; return (<group userData={{ id: element.id }}>
            <Line points={boundaryLoop(boundary, z)} color={isSelected ? '#00aaff' : '#e6a23c'} lineWidth={2} dashed dashSize={0.6} gapSize={0.3} />
        </group>
    );
}

/** Subregion: tinted material overlay on the toposolid surface. */
function SubregionRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    const boundary: [number, number][] | undefined = geom?.boundary;
    if (!boundary || boundary.length < 3) return null;
    const shape = new THREE.Shape();
    shape.moveTo(boundary[0][0], boundary[0][1]);
    for (let i = 1; i < boundary.length; i++) shape.lineTo(boundary[i][0], boundary[i][1]);
    shape.closePath();
    return (
        <group userData={{ id: element.id }}>
            <mesh position={[0, 0, 0.04]}>
                <shapeGeometry args={[shape]} />
                <meshBasicMaterial color={isSelected ? '#00aaff' : '#b8bfc7'} transparent opacity={0.45} side={THREE.DoubleSide} />
            </mesh>
            <Line points={boundaryLoop(boundary, 0.05)} color={isSelected ? '#66ccff' : '#5b6570'} lineWidth={1.2} />
        </group>
    );
}

/** Air terminal (diffuser): flat face plate with a neck cylinder. Dims in mm. */
function AirTerminalRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#e8e8e8', isSelected, viewMode, viewType);
    if (!geom?.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const faceNormal = new THREE.Vector3(...(geom.faceNormal ?? [0, 0, 1]));
    const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), faceNormal.clone().normalize());

    const width = Math.max((geom.width ?? 600) / 1000, 0.05);
    const height = Math.max((geom.height ?? 600) / 1000, 0.05);
    const neck = 0.25; // fixed visual neck length (m)

    return (
        <group position={pos} quaternion={quaternion} userData={{ id: element.id }}>
            {/* Face plate */}
            <mesh position={[0, 0, 0.01]}>
                <boxGeometry args={[width, height, 0.02]} />
                {material}
                {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
            {/* Neck behind the face */}
            <mesh position={[0, 0, -neck / 2]}>
                <cylinderGeometry args={[Math.min(width, height) * 0.3, Math.min(width, height) * 0.3, neck, 16]} />
                {material}
            </mesh>
        </group>
    );
}

/** Duct placeholder: hollow rectangular/round run drawn as a translucent box. */
function DuctPlaceholderRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial(PREVIEW_COLORS.mep, isSelected, viewMode, viewType);
    if (!geom?.start || !geom?.end) return null;

    const start = new THREE.Vector3(...geom.start);
    const end = new THREE.Vector3(...geom.end);
    const length = start.distanceTo(end);
    if (length < 0.001) return null;

    const mid = start.clone().add(end).multiplyScalar(0.5);
    const direction = end.clone().sub(start).normalize();
    const angle = Math.atan2(direction.y, direction.x);

    const isRound = geom.shape === 'Round';
    const width = Math.max((geom.width ?? 300) / 1000, 0.05);
    const height = Math.max((geom.height ?? 300) / 1000, 0.05);
    const d = Math.max((geom.diameter ?? width) / 1000, 0.05);

    return (
        <group position={mid} rotation={[0, 0, angle]} userData={{ id: element.id }}>
            <mesh>
                {isRound
                    ? <cylinderGeometry args={[d / 2, d / 2, length, 12]} />
                    : <boxGeometry args={[length, width, height]} />}
                {material}
                {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
        </group>
    );
}

/** Pipe placeholder: single-pipe centerline run as a thin tube. */
function PipePlaceholderRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial(PREVIEW_COLORS.mep, isSelected, viewMode, viewType);
    if (!geom?.start || !geom?.end) return null;

    const start = new THREE.Vector3(...geom.start);
    const end = new THREE.Vector3(...geom.end);
    const length = start.distanceTo(end);
    if (length < 0.001) return null;

    const dM = Math.max((geom.estimatedDiameter ?? 100) / 1000, 0.01);
    const mid = start.clone().add(end).multiplyScalar(0.5);
    const direction = end.clone().sub(start).normalize();
    const angle = Math.atan2(direction.y, direction.x);
    const pitch = Math.asin(THREE.MathUtils.clamp(direction.z, -1, 1));

    return (
        <group position={mid} rotation={[0, 0, angle]} userData={{ id: element.id }}>
            <group rotation={[0, -pitch, 0]}>
                <mesh>
                    <cylinderGeometry args={[dM / 2, dM / 2, length, 8]} />
                    {material}
                    {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                        <InkEdges isSelected={isSelected} />
                    )}
                </mesh>
            </group>
        </group>
    );
}

/** Flex duct/pipe: spline curve through control points, rendered as a tube. */
function FlexDuctRenderer({ element, isSelected, viewMode }: { element: BIMElement, isSelected: boolean, viewMode: string }) {
    const geom = (element as any).geometryData;
    if (!Array.isArray(geom?.controlPoints) || geom.controlPoints.length < 2) return null;

    const points = geom.controlPoints.map((p: [number, number, number]) =>new THREE.Vector3(...p)); const curve = new THREE.CatmullRomCurve3(points); const dM = Math.max((geom.diameter ?? 200) / 1000, 0.01); const tube = new THREE.TubeGeometry(curve, points.length * 8, dM / 2, 10, false); return (<group userData={{ id: element.id }}>
            <mesh geometry={tube}>
                <meshStandardMaterial color={isSelected ? '#0078d4' : '#556677'} roughness={0.7} />
                {viewMode !== 'rendered' && <InkEdges isSelected={isSelected} threshold={20} />}
            </mesh>
        </group>
    );
}

function FlexPipeRenderer(props: { element: BIMElement, isSelected: boolean, viewMode: string }) {
    return <FlexDuctRenderer {...props} />;
}

/** Duct accessory (damper etc.): small box marker on the duct run. */
function DuctAccessoryRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#cc8844', isSelected, viewMode, viewType);
    if (!geom?.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const rotation = THREE.MathUtils.degToRad(geom.rotation ?? 0);
    const size = 0.25;

    return (
        <group position={pos} rotation={[0, 0, rotation]} userData={{ id: element.id }}>
            <mesh>
                <boxGeometry args={[size, size, size]} />
                {material}
                {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
        </group>
    );
}

/** Pipe accessory (valve): body wedge + red hand-wheel disc above the run. */
function PipeAccessoryRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#9aa3ad', isSelected, viewMode, viewType);
    if (!geom?.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const rotation = THREE.MathUtils.degToRad(geom.rotation ?? 0);
    const size = 0.16;

    return (
        <group position={pos} rotation={[0, 0, rotation]} userData={{ id: element.id }}>
            {/* Valve body: two cones meeting at the seat */}
            <mesh rotation={[Math.PI / 2, 0, 0]} position={[-size * 0.55, 0, 0]}>
                <coneGeometry args={[size * 0.55, size * 1.1, 12]} />
                {material}
            </mesh>
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[size * 0.55, 0, 0]}>
                <coneGeometry args={[size * 0.55, size * 1.1, 12]} />
                {material}
            </mesh>
            {/* Stem */}
            <mesh position={[0, 0, size * 0.8]}>
                <cylinderGeometry args={[size * 0.09, size * 0.09, size * 0.9, 8]} />
                <meshStandardMaterial color={isSelected ? '#00aaff' : '#5b6570'} />
            </mesh>
            {/* Hand wheel (BIM valves read red) */}
            <mesh position={[0, 0, size * 1.25]} rotation={[Math.PI / 2, 0, 0]}>
                <torusGeometry args={[size * 0.55, size * 0.08, 8, 20]} />
                <meshStandardMaterial color={isSelected ? '#00aaff' : '#cc3333'} />
            </mesh>
        </group>
    );
}

/** Cable tray / conduit fitting: short U-shaped trough elbow marker. */
function TrayFittingRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#8d97a2', isSelected, viewMode, viewType);
    if (!geom?.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const rotation = THREE.MathUtils.degToRad(geom.rotation ?? 0);
    const size = 0.3;
    const wall = size * 0.12;

    return (
        <group position={pos} rotation={[0, 0, rotation]} userData={{ id: element.id }}>
            {/* Trough: base plate + two side rails forming an elbow blank */}
            <mesh position={[0, 0, -size / 2]}>
                <boxGeometry args={[size, size, wall]} />
                {material}
            </mesh>
            <mesh position={[0, size / 2 - wall / 2, 0]}>
                <boxGeometry args={[size, wall, size]} />
                {material}
            </mesh>
            <mesh position={[0, -size / 2 + wall / 2, 0]}>
                <boxGeometry args={[size, wall, size]} />
                {material}
            </mesh>
            {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                <mesh position={[0, 0, 0]}>
                    <boxGeometry args={[size, size, size]} />
                    <meshBasicMaterial visible={false} />
                    <InkEdges isSelected={isSelected} />
                </mesh>
            )}
        </group>
    );
}

/** Sprinkler: small pendant head — cylinder body plus deflector disc. */
function SprinklerRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#cc3333', isSelected, viewMode, viewType);
    if (!geom?.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const radius = Math.max(geom.radius ?? 0.05, 0.02);

    return (
        <group position={pos} userData={{ id: element.id }}>
            <mesh>
                <cylinderGeometry args={[radius * 0.35, radius * 0.35, radius * 2, 8]} />
                {material}
                {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe') && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
            <mesh position={[0, 0, -radius]}>
                <cylinderGeometry args={[radius, radius, radius * 0.15, 12]} />
                <meshStandardMaterial color={isSelected ? '#0078d4' : '#ee5555'} />
            </mesh>
        </group>
    );
}

/** Plumbing fixture: simple porcelain box body (WC/Lav footprint). Dims in mm. */
function PlumbingFixtureRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#f5f5f5', isSelected, viewMode, viewType);
    if (!geom?.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const rotation = THREE.MathUtils.degToRad(geom.rotation ?? 0);
    const width = Math.max((geom.width ?? 400) / 1000, 0.05);
    const depth = Math.max((geom.depth ?? 600) / 1000, 0.05);
    const height = Math.max((geom.height ?? 400) / 1000, 0.05);

    // Position stores the center Z (tool adds height/2 at creation)
    return (
        <group position={[pos.x, pos.y, pos.z]} rotation={[0, 0, rotation]} userData={{ id: element.id }}>
            <mesh>
                <boxGeometry args={[width, depth, height]} />
                {material}
                {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
        </group>
    );
}

/**
 * Lighting fixture: recessed cans render as discs, linear/troffer types as
 * boxes with an emissive lens face. Dims in mm.
 */
function LightingFixtureRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#dddddd', isSelected, viewMode, viewType);
    if (!geom?.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const rotation = Array.isArray(geom.rotation) ? geom.rotation[2] ?? 0 : 0;
    const rotationRad = THREE.MathUtils.degToRad(rotation);

    const category: string = geom.lightingCategory ?? 'Recessed';
    const radiusM = geom.radius ? geom.radius / 1000 : 0.075;
    const lengthM = geom.length ? geom.length / 1000 : 1.2;
    const widthM = geom.width ? geom.width / 1000 : 0.3;

    const isLinear = category === 'Linear' || category === 'Track Lighting';
    const lens = (
        <meshStandardMaterial
            color={isSelected ? '#00aaff' : '#fffbe6'}
            emissive="#fff3c4"
            emissiveIntensity={0.6}
            roughness={0.4}
        />
    );
    return (
    <group position={pos} rotation={[0, 0, rotationRad]} userData={{ id: element.id }}>
            {isLinear ? (
                <mesh position={[0, 0, -0.03]}>
                    <boxGeometry args={[lengthM, widthM, 0.06]} />
                    {material}
                    {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                        <InkEdges isSelected={isSelected} />
                    )}
                </mesh>
            ) : (
                <mesh rotation={[Math.PI / 2, 0, 0]}>
                    <cylinderGeometry args={[radiusM, radiusM, 0.06, 20]} />
                    {material}
                    {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                        <InkEdges isSelected={isSelected} />
                    )}
                </mesh>
            )}
            {/* Emissive lens / aperture */}
            <mesh position={[0, 0, isLinear ? -0.062 : -0.033]} rotation={isLinear ? [0, 0, 0] : [Math.PI / 2, 0, 0]}>
                {isLinear
                    ? <planeGeometry args={[lengthM * 0.9, widthM * 0.7]} />
                    : <circleGeometry args={[radiusM * 0.75, 20]} />}
                {lens}
            </mesh>
        </group>
    );
}

/**
 * Electrical device (outlet/switch/data): wall plate box sized by gang count
 * (one gang ≈ 70 x 115 mm plate, 40 mm deep).
 */
function ElectricalDeviceRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#f0f0f0', isSelected, viewMode, viewType);
    if (!geom?.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const rotation = THREE.MathUtils.degToRad(geom.rotation ?? 0);
    const gangs = Math.max(geom.boxType ?? 1, 1);
    const plateW = 0.07 * gangs;
    const plateH = 0.115;
    const plateD = 0.04;

    return (
        <group position={pos} rotation={[0, 0, rotation]} userData={{ id: element.id }}>
            <mesh>
                <boxGeometry args={[plateW, plateD, plateH]} />
                {material}
                {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
        </group>
    );
}

/** Wire: arc/spline polyline between connected devices with tick marks. */
function WireRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    if (!Array.isArray(geom?.points) || geom.points.length < 2) return null;

    const points = geom.points.map((p: [number, number, number]) =>new THREE.Vector3(...p)); const ticks = geom.tickMarks; const tickCount = ticks ? (ticks.hot ?? 0) + (ticks.neutral ?? 0) + (ticks.ground ?? 0) : 0; return (<group userData={{ id: element.id }}>
            <Line
                points={points}
                color={isSelected ? '#00aaff' : '#222222'}
                lineWidth={isSelected ? 3 : 1.5}
            />
            {/* Home-run ticks near the start point */}
            {tickCount > 0 && Array.from({ length: Math.min(tickCount, 4) }, (_, i) => (
                <Line
                    key={i}
                    points={[
                        [points[0].x - 0.08 + i * 0.06, points[0].y + 0.06, points[0].z + 0.005],
                        [points[0].x - 0.04 + i * 0.06, points[0].y - 0.06, points[0].z + 0.005],
                    ]}
                    color={isSelected ? '#00aaff' : '#222222'}
                    lineWidth={1.5}
                />
            ))}
        </group>
    );
}

/** Fabrication part: straight runs as boxes, fittings as corner blocks. */
function FabricationPartRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = (element as any).geometryData;
    const material = useViewMaterial('#00aaaa', isSelected, viewMode, viewType);
    if (!geom?.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const rotation = Array.isArray(geom.rotation) ? geom.rotation[2] ?? 0 : 0;
    const rotationRad = THREE.MathUtils.degToRad(rotation);

    const def = (element as any).type as string;
    const isFitting = def.includes('Elbow') || def.includes('Tee');
    const size = 0.3;

    return (
        <group position={pos} rotation={[0, 0, rotationRad]} userData={{ id: element.id }}>
            <mesh>
                {isFitting
                    ? <boxGeometry args={[size, size, size]} />
                    : <boxGeometry args={[1.5, size, size]} />}
                {material}
                {(viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe' || viewMode === 'consistentColors' || viewMode === 'shaded') && (
                    <InkEdges isSelected={isSelected} />
                )}
            </mesh>
        </group>
    );
}

/** Revision cloud: closed scalloped boundary drawn above the view plane. */
function RevisionCloudRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    const s = useAnnotationScaleFactor();
    const ink = useAnnotationInk();
    if (!Array.isArray(geom?.boundary) || geom.boundary.length < 3) return null;

    const boundary = geom.boundary as [number, number][];
    const color = isSelected ? '#00aaff' : ink;

    // Scalloped arcs: each boundary segment is replaced by a row of outward
    // semicircular arcs (the BIM cloud look). Arc diameter adapts to the
    // segment length so short edges still get clean bumps.
    const bumps: THREE.Vector3[][] = [];
    for (let i = 0; i < boundary.length; i++) {
        const a = new THREE.Vector3(boundary[i][0], boundary[i][1], 0.02);
        const b = new THREE.Vector3(boundary[(i + 1) % boundary.length][0], boundary[(i + 1) % boundary.length][1], 0.02);
        const segLen = a.distanceTo(b);
        if (segLen < 0.001) continue;
        // Arc radius: ~1/4 segment, clamped to paper-ish world size.
        const r = THREE.MathUtils.clamp(segLen / 4, 0.1 * s, 0.4 * s);
        const n = Math.max(Math.round(segLen / (r * 2)), 1);
        const dir = b.clone().sub(a).normalize();
        const perp = new THREE.Vector3(-dir.y, dir.x, 0); // outward for CCW loops
        const stepLen = segLen / n;
        for (let j = 0; j < n; j++) {
            const c0 = a.clone().add(dir.clone().multiplyScalar(stepLen * j));
            const c1 = a.clone().add(dir.clone().multiplyScalar(stepLen * (j + 1)));
            const center = c0.clone().lerp(c1, 0.5);
            const arcR = Math.max(c0.distanceTo(center), 0.001);
            const startA = Math.atan2(c0.y - center.y, c0.x - center.x);
            // Half-circle from c0 to c1 bulging toward +perp: pick the sign
            // of the ±180° sweep whose mid-angle points along perp.
            const perpA = Math.atan2(perp.y, perp.x);
            let diff = perpA - startA;
            while (diff >Math.PI) diff -= Math.PI * 2; while (diff< -Math.PI) diff += Math.PI * 2;
            const sweep = diff > 0 ? Math.PI : -Math.PI;
            bumps.push(arcPoints(center.x, center.y, arcR, 0.02, startA, sweep, 10));
        }
    }

    return (
        <group userData={{ id: element.id }}>
            {bumps.map((arcPts, i) => (
                <Line
                    key={i}
                    points={arcPts.length > 1 ? arcPts : [arcPts[0], arcPts[0]]}
                    color={color}
                    lineWidth={1.5}
                />
            ))}
        </group>
    );
}

/**
 * Generic annotation symbol (Annotate > Symbol panel). Renders the actual
 * symbol per type — North Arrow (N + filled arrowhead), Break Line (zigzag),
 * Centerline (long-short dashes) — instead of a bare crosshair. Paper-sized:
 * scales with the view.
 */
function SymbolAnnotationRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    // Annotation symbol: fixed paper footprint that follows the view scale.
    const s = useAnnotationScaleFactor();
    const ink = useAnnotationInk();
    if (!geom?.position) return null;

    const pos = new THREE.Vector3(...geom.position);
    const color = isSelected ? '#00aaff' : ink;
    const outline = ink === '#ffffff' ? '#000000' : '#ffffff';

    if (element.type === 'North Arrow') {
        const h = 0.9 * s;
        const w = 0.35 * s;
        return (
            <group position={pos} userData={{ id: element.id }}>
                {/* Solid black half, open half — classic north arrow */}
                <mesh position={[0, h * 0.1, 0.011]}>
                    <shapeGeometry args={[(() => {
                        const sh = new THREE.Shape();
                        sh.moveTo(0, h / 2);
                        sh.lineTo(-w / 2, -h / 2);
                        sh.lineTo(0, -h / 3);
                        sh.closePath();
                        return sh;
                    })()]} />
                    <meshBasicMaterial color={color} side={THREE.DoubleSide} />
                </mesh>
                <Line
                    points={[[0, h / 2, 0.012], [w / 2, -h / 2, 0.012], [0, -h / 3, 0.012], [0, h / 2, 0.012]]}
                    color={color}
                    lineWidth={1.2}
                />
                <Text
                    position={[0, h * 0.62, 0.012]}
                    fontSize={0.35 * s}
                    color={color}
                    anchorX="center"
                    anchorY="bottom"
                    outlineWidth={0.02 * s}
                    outlineColor={outline}
                >
                    N
                </Text>
            </group>
        );
    }

    if (element.type === 'Break Line') {
        const L = 0.8 * s;
        const zig = 0.08 * s;
        return (
            <group position={pos} userData={{ id: element.id }}>
                <Line
                    points={[
                        [-L, 0, 0.011],
                        [-L * 0.4, 0, 0.011],
                        [-L * 0.3, zig, 0.011],
                        [L * 0.3, -zig, 0.011],
                        [L * 0.4, 0, 0.011],
                        [L, 0, 0.011],
                    ]}
                    color={color}
                    lineWidth={1.4}
                />
            </group>
        );
    }

    if (element.type === 'Centerline') {
        const L = 0.8 * s;
        return (
            <group position={pos} userData={{ id: element.id }}>
                {/* Long dash / short dash / long dash */}
                <Line points={[[-L, 0, 0.011], [-L * 0.45, 0, 0.011]]} color={color} lineWidth={1.2} />
                <Line points={[[-L * 0.3, 0, 0.011], [-L * 0.15, 0, 0.011]]} color={color} lineWidth={1.2} />
                <Line points={[[0, 0, 0.011], [L * 0.55, 0, 0.011]]} color={color} lineWidth={1.2} />
                <Line points={[[L * 0.7, 0, 0.011], [L, 0, 0.011]]} color={color} lineWidth={1.2} />
            </group>
        );
    }

    // Fallback for unknown symbol families: crosshair + label.
    const cross = 0.1 * s;
    return (
        <group position={pos} userData={{ id: element.id }}>
            <Line points={[[-cross, 0, 0.01], [cross, 0, 0.01]]} color={color} lineWidth={1.5} />
            <Line points={[[0, -cross, 0.01], [0, cross, 0.01]]} color={color} lineWidth={1.5} />
            <Text
                position={[0, 0.15 * s, 0.01]}
                fontSize={0.12 * s}
                color={color}
                anchorX="center"
                anchorY="bottom"
                outlineWidth={0.01 * s}
                outlineColor={outline}
            >
                {element.type}
            </Text>
        </group>
    );
}

/** Area reinforcement: hatched slab zone drawn as a translucent polygon.
 *  Also draws single Fabric Sheets (center + sheet size geometry payload). */
function AreaReinforcementRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    if (!geom) return null;

    // Fabric Sheet flavor: one standard welded-wire rectangle at a center point.
    if (Array.isArray(geom.center) && typeof geom.sheetLength === 'number' && typeof geom.boundary === 'undefined') {
        const center = geom.center as [number, number, number];
        const len = geom.sheetLength as number;
        const wid = (geom.sheetWidth as number) ?? len;
        const rot = THREE.MathUtils.degToRad(geom.rotation ?? 0);
        const shape = new THREE.Shape();
        shape.moveTo(-len / 2, -wid / 2);
        shape.lineTo(len / 2, -wid / 2);
        shape.lineTo(len / 2, wid / 2);
        shape.lineTo(-len / 2, wid / 2);
        shape.closePath();
        const shapeGeo = new THREE.ShapeGeometry(shape);
        const corners = [
            [-len / 2, -wid / 2], [len / 2, -wid / 2], [len / 2, wid / 2], [-len / 2, wid / 2], [-len / 2, -wid / 2],
        ].map(([cx, cy]) =>new THREE.Vector3(cx, cy, 0)); return (<group userData={{ id: element.id }} position={[center[0], center[1], center[2]]} rotation={[0, 0, rot]}>
                <mesh geometry={shapeGeo}>
                    <meshBasicMaterial color={isSelected ? '#00aaff' : '#00bcd4'} transparent opacity={0.3} side={THREE.DoubleSide} />
                </mesh>
                <Line points={corners} color={isSelected ? '#00aaff' : '#0097a7'} lineWidth={1.5} />
            </group>
        );
    }

    if (!Array.isArray(geom?.boundary) || geom.boundary.length < 3) return null;

    const boundary = geom.boundary as [number, number][];
    const elevation = typeof geom.elevation === 'number' ? geom.elevation : 0;

    const shape = new THREE.Shape();
    shape.moveTo(boundary[0][0], boundary[0][1]);
    for (let i = 1; i < boundary.length; i++) shape.lineTo(boundary[i][0], boundary[i][1]);
    shape.closePath();

    const shapeGeo = new THREE.ShapeGeometry(shape);
    const outline = [...boundary.map(p =>new THREE.Vector3(p[0], p[1], 0)), new THREE.Vector3(boundary[0][0], boundary[0][1], 0)]; return (<group userData={{ id: element.id }} position={[0, 0, elevation + 0.015]}>
            <mesh geometry={shapeGeo}>
                <meshBasicMaterial color={isSelected ? '#00aaff' : '#888888'} transparent opacity={0.3} side={THREE.DoubleSide} />
            </mesh>
            <Line points={outline} color={isSelected ? '#00aaff' : '#555555'} lineWidth={1.5} />
        </group>
    );
}

/** Path reinforcement: bar lines distributed along the sketch path. */
function PathReinforcementRenderer({ element, isSelected }: { element: BIMElement, isSelected: boolean }) {
    const geom = (element as any).geometryData;
    if (!Array.isArray(geom?.path) || geom.path.length < 2) return null;

    // Path may store Vector3-like objects; normalize to tuples defensively.
    const rawPts = geom.path.map((p: any) => Array.isArray(p) ? p : [p.x, p.y, p.z]) as [number, number, number][];
    const points = rawPts.map(p =>new THREE.Vector3(...p)); const spacing = Math.max((geom.primaryBars?.spacing ?? 200) / 1000, 0.05); 
// Distribute bar lines perpendicular to the path
        const bars: THREE.Vector3[][] = []; let carry = 0; for (let i = 1; i
< points.length; i++) {
        const a = points[i - 1];
        const b = points[i];
        const seg = b.clone().sub(a);
        const len = seg.length();
        if (len < 0.001) continue;
        const dir = seg.clone().normalize();
        const perp = new THREE.Vector3(-dir.y, dir.x, 0).multiplyScalar(0.15);

        let t = carry;
        carry = 0;
        while (t <= len) {
            if (t >0.01 && t< len - 0.01) {
                const p = a.clone().add(dir.clone().multiplyScalar(t));
                bars.push([
                    p.clone().sub(perp),
                    p.clone().add(perp),
                ]);
            }
            t += spacing;
        }
        carry = t - len;
    }

    return (
        <group userData={{ id: element.id }}>
            <Line points={points} color={isSelected ? '#00aaff' : '#444444'} lineWidth={2} />
            {bars.map((bar, i) => (
                <Line key={i} points={bar} color={isSelected ? '#00aaff' : '#666666'} lineWidth={1} />
            ))}
        </group>
    );
}

// ==================== Precast tab renderers ====================

/** Concrete appearance shared by every solid precast member renderer. */
const PRECAST_CONCRETE_COLOR = '#c7c2b8';

/**
 * Precast wall panel / column / beam: a linear concrete solid from start to
 * end with its cross-section, plus the joint gap reserved at the ends. Wall
 * panels extrude a length x height section; columns and beams a width x depth
 * box along their axis. Plan views flatten wall panels into their footprint.
 */
function PrecastLinearPanelRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = element.geometryData as any;
    const categoryBim = useCategoryMaterial(element.category);
    const material = useViewMaterial(PRECAST_CONCRETE_COLOR, isSelected, viewMode, viewType, false, categoryBim);
    if (!geom?.start || !geom?.end) return null;

    const isWall = element.category === 'Precast Walls';
    const start = new THREE.Vector3(...geom.start);
    const end = new THREE.Vector3(...geom.end);
    const baseZ = isWall ? (geom.baseOffset ?? 0) : start.z;
    const width = isWall ? Math.max(geom.thickness ?? 0.2, 0.05) : Math.max(geom.width ?? 0.3, 0.05);
    const depth = isWall ? Math.max(geom.height ?? 3, 0.2) : Math.max(geom.depth ?? 0.5, 0.05);
    const length = start.distanceTo(end);
    if (length < 0.02) return null;

    const rotation = Math.atan2(end.y - start.y, end.x - start.x);
    const center = start.clone().add(end).multiplyScalar(0.5);
    // Panel geometry already sits INSIDE its joints (split reserves the gap
    // between strips), so the solid spans the full start→end run.
    const runLength = length;
    const isPlan = viewType === 'plan';
    // Vertical members (columns): the section axis is world Z, so the box is
    // built width x depth x length without the XY run rotation.
    const isVertical = !isWall && Math.hypot(end.x - start.x, end.y - start.y) < 1e-6;

    return (
        <group userData={{ id: element.id }}>
            {isVertical ? (
                <mesh position={[center.x, center.y, center.z]}>
                    <boxGeometry args={[width, depth, runLength]} />
                    {material}
                    <InkEdges isSelected={isSelected} />
                </mesh>
            ) : (
                <mesh position={[center.x, center.y, isWall ? baseZ + depth / 2 : start.z + depth / 2]} rotation={[0, 0, rotation]}>
                    <boxGeometry args={[runLength, width, depth]} />
                    {material}
                    <InkEdges isSelected={isSelected} />
                </mesh>
            )}
            {/* Panel mark label (BIM shows member marks on the panel). */}
            {!isPlan && geom.mark && (
                <Text
                    position={[center.x, center.y, (isWall ? baseZ + depth : isVertical ? center.z + runLength / 2 : start.z + depth) + 0.12]}
                    fontSize={0.22}
                    color={isSelected ? '#00aaff' : '#4d4d4d'}
                    anchorX="center"
                    anchorY="bottom"
                >
                    {geom.mark}
                </Text>
            )}
        </group>
    );
}

/**
 * Precast floor panel / hollow-core plank: extrude the footprint polygon by
 * the slab thickness. Hollow-core panels get their void channels drawn as
 * dashed translucent cylinders so the product type reads in the model.
 */
function PrecastFloorPanelRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const geom = element.geometryData as any;
    const categoryBim = useCategoryMaterial(element.category);
    const material = useViewMaterial(PRECAST_CONCRETE_COLOR, isSelected, viewMode, viewType, false, categoryBim);

    const thickness = Math.max(geom?.thickness ?? 0.2, 0.02);
    const baseZ = geom?.baseZ ?? 0;
    // Hook-order stability: the boundary validity gate happens INSIDE the
    // memo, so the extrude useMemo/useEffect always run for this component.
    const slabGeo = useMemo(() => {
        const boundary = geom?.boundary as [number, number][] | undefined;
        if (!Array.isArray(boundary) || boundary.length < 3) return null;
        const shape = new THREE.Shape();
        shape.moveTo(boundary[0][0], boundary[0][1]);
        for (let i = 1; i < boundary.length; i++) shape.lineTo(boundary[i][0], boundary[i][1]);
        shape.closePath();
        const g = new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false });
        g.translate(0, 0, baseZ);
        return g;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [element, thickness, baseZ]);
    useEffect(() => () => { slabGeo?.dispose(); }, [slabGeo]);

    if (!slabGeo) return null;
    const b = geom.boundary as [number, number][];

    const isPlan = viewType === 'plan';
    const cores: THREE.Vector3[][] = [];
    // Centroid of the footprint (panel mark anchor + void band centering).
    let cX = 0, cY = 0;
    for (const [x, y] of b) { cX += x; cY += y; }
    cX /= b.length; cY /= b.length;
    if (!isPlan && geom.hollowCore && geom.core) {
        // Void channels run along the span axis, evenly spaced across the
        // plank width (bounding band in the rotated frame).
        const spanAngle = geom.spanAngle ?? 0;
        const cu = Math.cos(spanAngle), su = Math.sin(spanAngle);
        let uMin = Infinity, uMax = -Infinity, vMin = Infinity;
        for (const [x, y] of b) {
            const u = x * cu + y * su;
            const v = x * -su + y * cu;
            if (u < uMin) uMin = u;
            if (u >uMax) uMax = u; if (v< vMin) vMin = v;
        }
        for (let i = 0; i < (geom.core.count as number); i++) {
            const v = vMin + (geom.core.spacing as number) * (i + 0.5);
            cores.push([
                new THREE.Vector3(uMin * cu - v * su, uMin * su + v * cu, baseZ + thickness / 2),
                new THREE.Vector3(uMax * cu - v * su, uMax * su + v * cu, baseZ + thickness / 2),
            ]);
        }
    }

    return (
        <group userData={{ id: element.id }}>
            <mesh geometry={slabGeo}>
                {material}
                <InkEdges isSelected={isSelected} />
            </mesh>
            {cores.map((pair, i) => (
                <Line key={i} points={pair} color="#8a8a8a" lineWidth={1} dashed dashScale={18} />
            ))}
            {geom.mark && (
                <Text
                    position={[cX, cY, baseZ + thickness + 0.12]}
                    fontSize={0.22}
                    color={isSelected ? '#00aaff' : '#4d4d4d'}
                    anchorX="center"
                    anchorY="bottom"
                >
                    {geom.mark}
                </Text>
            )}
        </group>
    );
}

/** Embedded mounting hardware: shaped solid oriented to its host face. */
function PrecastMountingPartRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const part = element as any;
    const geom = part.geometryData;
    const built = useMemo(() =>buildMountingPartMesh(part), [element]); const material = useViewMaterial(built.color, isSelected, viewMode, viewType); if (!geom?.position || !geom?.normal) return null; return (<group position={geom.position} quaternion={built.orient} userData={{ id: element.id }}>
                <mesh geometry={built.geometry}>
                    {material}
                    <InkEdges isSelected={isSelected} threshold={20} />
                </mesh>
        </group>
    );
}

/**
 * Precast reinforcement mesh: every generated bar as a merged tube solid,
 * drawn in the rebar rust tone. In plan/hidden-line views the mesh falls back
 * to flat lines so it never obscures the host panel outline.
 */
function PrecastReinforcementRenderer({ element, isSelected, viewType, viewMode }: { element: BIMElement, isSelected: boolean, viewType: string, viewMode: string }) {
    const reinf = element as any;
    const flatView = viewType === 'plan' || viewMode === 'hiddenLine' || viewMode === 'wireframe';
    const mesh = useMemo(() => (flatView ? null : buildReinforcementMesh(reinf)), [reinf, flatView]);
    const material = useViewMaterial('#b5541f', isSelected, viewMode, viewType);

    const lineBars = useMemo(() => {
        if (!flatView) return [];
        return (reinf.geometryData?.bars ?? []).map((bar: any) => [
            new THREE.Vector3(...bar.start),
            new THREE.Vector3(...bar.end),
        ]);
    }, [reinf, flatView]);

    if (flatView) {
        if (lineBars.length === 0) return null;
        return (
            <group userData={{ id: element.id }}>
                {lineBars.map((pair: THREE.Vector3[], i: number) => (
                    <Line key={i} points={pair} color={isSelected ? '#00aaff' : '#b5541f'} lineWidth={1} />
                ))}
            </group>
        );
    }
    if (!mesh) return null;
    return (
        <group userData={{ id: element.id }}>
            <mesh geometry={mesh}>{material}</mesh>
        </group>
    );
}
